import {
  type InfiniteData,
  infiniteQueryOptions,
  keepPreviousData,
  mutationOptions,
  type QueryClient,
  type QueryKey,
  queryOptions,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  useIsMutating,
  useMutation,
  UseMutationOptions,
  useQueries,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
  useSuspenseQuery,
} from '@tanstack/react-query'
import _ from 'lodash'
import { useCallback, useEffect, useEffectEvent, useMemo, useRef } from 'react'
import invariant from 'tiny-invariant'
import { match } from 'ts-pattern'
import { z, ZodError } from 'zod'
import { buildPath } from '../build-path.ts'
import { isoToMs, nowMs } from '../date-utils.ts'
import { inlineAnswerQueryPolicy, isForbiddenError, isRejectedCredentialsError } from '../error-utils.ts'
import { isLabsEnabled } from '../runtime-env.ts'
import { invariantType } from '../type-utils.ts'
import { ApiError } from './api-error'
import {
  AddProjectMembershipsRequestSchema,
  type AddRepositoryRequest,
  AddRepositoryRequestSchema,
  type AiScanRunState,
  AllowedMethodsSchema,
  type AnalysisCancellationResponse,
  AnalysisCancellationResponseSchema,
  AnalysisPaginatedResponseSchema,
  type AnalysisResponse,
  AnalysisResponseSchema,
  AnalysisStateResponse,
  type ApiRootResponse,
  ApiRootResponseSchema,
  type AzureManagedIdentitiesResponse,
  AzureManagedIdentitiesResponseSchema,
  AzureOrganizationPaginatedResponseSchema,
  type AzureProjectPaginatedResponse,
  AzureProjectPaginatedResponseSchema,
  type AzureRepositoryPaginatedResponse,
  AzureRepositoryPaginatedResponseSchema,
  type BeaconEffort,
  type BeaconRun,
  BeaconRunListSchema,
  BeaconRunSchema,
  type BeaconScanRequest,
  type BeaconSummary,
  BeaconSummarySchema,
  type BitbucketRepositoryPaginatedResponse,
  BitbucketRepositoryPaginatedResponseSchema,
  type BitbucketWorkspacePaginatedResponse,
  BitbucketWorkspacePaginatedResponseSchema,
  type BranchEmbedParameter,
  type BranchPaginatedResponse,
  BranchPaginatedResponseSchema,
  type ChangesetPaginatedResponse,
  ChangesetPaginatedResponseSchema,
  type CodingGuidelinesResponse,
  CodingGuidelinesResponseSchema,
  CreateFeedbackSubmissionRequestSchema,
  type CreateIntegrationRequest,
  CreateIntegrationRequestSchema,
  type CreateProjectRequest,
  CreateProjectRequestSchema,
  type CreatePushRequest,
  type CreatePushResponse,
  CreatePushResponseSchema,
  type CreateRepositoryIntegrationRequest,
  CreateRepositoryIntegrationRequestSchema,
  type CreateWorkflowRequest,
  CreateWorkflowRequestSchema,
  CurrentUserResponseSchema,
  ExploitChainsPhaseSchema,
  type ExploitChainsResponse,
  ExploitChainsResponseSchema,
  ExploitChainsStatusSchema,
  type FeedbackRating,
  type FeedbackScope,
  type FeedbackSubmissionPaginatedResponse,
  FeedbackSubmissionPaginatedResponseSchema,
  type FeedbackSubmissionResponse,
  FeedbackSubmissionResponseSchema,
  type FindingAggregateResponse,
  FindingAggregateResponseSchema,
  type FindingEmbedParameter,
  FindingOverlayPhaseSchema,
  type FindingOverlayResponse,
  FindingOverlayResponseSchema,
  type FindingOverlaysResponse,
  FindingOverlaysResponseSchema,
  FindingOverlayStatusSchema,
  type FindingResponseWithoutEmbed,
  FindingResponseWithoutEmbedSchema,
  type FindingResultPaginatedResponse,
  FindingResultPaginatedResponseSchema,
  type FreeformUploadResponse,
  FreeformUploadResponseSchema,
  type FreeformUploadSessionPaginatedResponse,
  FreeformUploadSessionPaginatedResponseSchema,
  type FreeformUploadSessionResponse,
  FreeformUploadSessionResponseSchema,
  getIsIntegrationTypeSupportedByApi,
  type GetPushesResponse,
  GetPushesResponseSchema,
  type GitLabProjectPaginatedResponse,
  GitLabProjectPaginatedResponseSchema,
  type HALLink,
  type Integration,
  type IntegrationPaginatedResponse,
  IntegrationPaginatedResponseSchema,
  type IntegrationSourceResponse,
  type IntegrationType,
  IntegrationTypeCountsResponseSchema,
  isInFlightGenerationStatus,
  ModelledIntegrationSchema,
  type OpenGrepRule,
  type OpenGrepRuleSet,
  OpenGrepRuleSetSchema,
  OpenGrepRulesPhaseSchema,
  type OpenGrepRulesResponse,
  OpenGrepRulesResponseSchema,
  OpenGrepRulesStatusSchema,
  type OrganizationPreferencesResponse,
  OrganizationPreferencesResponseSchema,
  type PaginatedResponse,
  type PatchesCreatePatchRequest,
  PatchesCreatePatchRequestSchema,
  type PatchPaginatedResponse,
  PatchPaginatedResponseSchema,
  type PolarisStatusLookupResponse,
  PolarisStatusLookupResponseSchema,
  type Project,
  type ProjectMembershipPaginatedResponse,
  ProjectMembershipPaginatedResponseSchema,
  ProjectPaginatedResponseSchema,
  ProjectSchema,
  RefreshRequestResponseSchema,
  RefreshRequestsResponseSchema,
  type RefreshRequestStateResponse,
  type RepositoryEmbedParameter,
  type RepositoryIntegration,
  type RepositoryIntegrationPaginatedResponse,
  RepositoryIntegrationPaginatedResponseSchema,
  RepositoryPaginatedResponseSchema,
  RepositorySchema,
  type ResultsVerificationResponse,
  ResultsVerificationResponseSchema,
  type SbomResponse,
  SbomResponseSchema,
  type ScalePaginatedResponse,
  ScalePaginatedResponseSchema,
  ScaleSchema,
  type ScanAggregateResponse,
  ScanAggregateResponseSchema,
  type ScanAnalysisState,
  type ScanEmbedParameter,
  type ScanResponse,
  ScanResponseSchema,
  type ScaStatusResponse,
  type ScorecardsResponse,
  ScorecardsResponseSchema,
  type SonarProjectPaginatedResponse,
  SonarProjectPaginatedResponseSchema,
  type SupportBundleResponse,
  SupportBundleResponseSchema,
  type SystemStatusesResponse,
  SystemStatusesResponseSchema,
  type ThreatModelContent,
  ThreatModelContentSchema,
  ThreatModelPhaseSchema,
  type ThreatModelResponse,
  ThreatModelResponseSchema,
  ThreatModelStatusSchema,
  type Tool,
  type UpdateCodingGuidelinesRequest,
  type UpdateIntegrationRequest,
  UpdateIntegrationRequestSchema,
  type UpdateProjectRequest,
  UpdateProjectRequestSchema,
  type UpdateRepositoryIntegrationRequest,
  UpdateRepositoryIntegrationRequestSchema,
  type UpdateWorkflowRequest,
  UpdateWorkflowRequestSchema,
  WorkflowPaginatedResponseSchema,
  type WritableIntegrationType,
} from './api-schemas.ts'

import type { DefaultError } from '@tanstack/react-query'
import { useReconnectingSSEStream } from '../../hooks/use-reconnecting-sse-stream.ts'
import { type SSEStream, useSSEStream } from '../../hooks/use-sse-stream.ts'
import type { paths } from './openapi-spec'
import { SCAN_FINDINGS_PARAMS } from './query-params'
import type { CreateSbomRequestFormData, CreateScanRequestFormData } from './request-form-data.ts'
import {
  type AnalysisStreamEvent,
  AnalysisStreamEventSchema,
  BEACON_EVENT_NAMES,
  type BeaconStreamFrame,
  BeaconStreamFrameSchema,
  ExploitChainsProgressEventNameSchema,
  type ExploitChainsStreamEvent,
  ExploitChainsStreamEventSchema,
  type FindingOverlaysStreamEvent,
  FindingOverlaysStreamEventSchema,
  isTerminalBeaconFrame,
  type OpenGrepRulesStreamEvent,
  OpenGrepRulesStreamEventSchema,
  type SupportBundlePhaseEvent,
  SupportBundlePhaseEventSchema,
  type ThreatModelStreamEvent,
  ThreatModelStreamEventSchema,
} from './sse-schemas.ts'
import { type AvailableTemplate, getTemplate } from './templates.ts'

// #region: utils
export class ZodParseError extends Error {
  public readonly zodError: ZodError
  public readonly path: string
  public readonly schemaName?: string

  constructor(path: string, zodError: ZodError, schemaName?: string) {
    super(`Zod parse error at ${path}: ${zodError.message}`)
    this.name = 'ZodParseError'
    this.path = path
    this.zodError = zodError
    this.schemaName = schemaName
  }

  get issues(): z.core.$ZodIssue[] {
    return this.zodError.issues
  }
}

type LogZodErrorLogger = ({
  path,
  name,
  message,
  stack,
  issues,
}: {
  path: string
  name: string
  message: string
  stack?: string
  issues: z.core.$ZodIssue[]
}) => unknown

export async function logZodError<T>(
  path: string,
  schema: z.ZodType,
  action: () => Promise<T>,
  logger: LogZodErrorLogger = console.error
): Promise<T> {
  try {
    return await action()
  } catch (error: unknown) {
    if (error instanceof ZodError) {
      const { name, message, stack, issues } = error
      logger({
        path,
        name,
        message,
        stack,
        issues,
      })

      throw new ZodParseError(path, error, schema.description)
    }

    throw error
  }
}

const REQUESTED_WITH_HEADER = 'X-Requested-With'

/**
 * Headers for a wrapper request: the caller's replace the defaults wholesale, except that
 * `X-Requested-With` is always present.
 *
 * The backend answers 403 to a state-changing browser request without it (CSRF defense), so a caller
 * must not be able to drop it by passing its own `headers`. The rest of the defaults stay replaceable
 * because a multipart caller must omit `Content-Type: application/json` for the browser to set the boundary.
 */
const withRequestedWith = (callerHeaders: HeadersInit | undefined, defaultHeaders: HeadersInit): Headers => {
  const headers = new Headers(callerHeaders ?? defaultHeaders)
  if (!headers.has(REQUESTED_WITH_HEADER)) {
    headers.set(REQUESTED_WITH_HEADER, 'JavaScript')
  }
  return headers
}

type FetchWrapperParams<T extends z.ZodType> = {
  path: string
  options?: RequestInit
  schema: T
  successStatusCodes?: number[]
  deserializeFn?: (response: Response) => Promise<unknown>
}

/**
 * Wrapper function for API GET requests
 *
 * Makes a fetch request to the specified path, validates the response against the provided schema,
 * and handles errors by throwing an ApiError with appropriate status and body.
 *
 * Exported for tests only. Do not call it directly; go through a `use…` hook, which owns the query options.
 *
 * @param {Object} params - Parameters for the fetch request
 * @param {string} params.path - The API endpoint path
 * @param {RequestInit} [params.options] - Optional fetch request options
 * @param {z.ZodType} params.schema - Zod schema for validating the response
 * @param {number[]} [params.successStatusCodes] - HTTP status codes considered successful (defaults to [200, 201, 204])
 * @param {Function} [params.deserializeFn] - Custom function to deserialize the response (defaults to response.json())
 * @returns {Promise<z.infer<T>>} - Parsed and validated response data
 * @throws {ApiError} - If the response status is not in the successStatusCodes list
 */
export const fetchWrapperQuery = async <T extends z.ZodType>({
  path,
  options = {},
  schema,
  successStatusCodes = [200, 201, 204],
  deserializeFn = (response: Response) => response.json() as Promise<unknown>,
}: FetchWrapperParams<T>): Promise<z.infer<T>> => {
  const response = await fetch(path, {
    ...options,
    headers: withRequestedWith(options.headers, { Accept: 'application/hal+json, application/json' }),
  })

  if (!successStatusCodes.includes(response.status)) {
    throw new ApiError(response.status, await response.text(), path)
  }

  return await logZodError<z.infer<T>>(path, schema, async () => schema.parse(await deserializeFn(response)))
}

type FetchWrapperMutationParams<T extends z.ZodType = z.ZodType> = {
  path: string
  options?: RequestInit
  schema: T
  successStatusCodes?: number[]
  deserializeFn?: (response: Response) => Promise<unknown>
}

/**
 * Wrapper function for API mutation requests (POST, PUT, DELETE, etc.)
 *
 * Makes a fetch request to the specified path with JSON content type headers,
 * validates the response against the provided schema, and handles errors.
 *
 * Exported for tests only. Do not call it directly; go through a `use…` hook, which owns the mutation options.
 *
 * @param {Object} params - Parameters for the mutation request
 * @param {string} params.path - The API endpoint path
 * @param {RequestInit} [params.options] - Optional fetch request options
 * @param {z.ZodType} [params.schema] - Zod schema for validating the response
 * @param {number[]} [params.successStatusCodes] - HTTP status codes considered successful (defaults to [200, 201, 204])
 * @param {Function} [params.deserializeFn] - Custom function to deserialize the response (defaults to returning undefined)
 * @returns {Promise<z.infer<T>>} - Parsed and validated response data
 * @throws {ApiError} - If the response status is not in the successStatusCodes list
 */
export const fetchWrapperMutation = async <T extends z.ZodType = z.ZodType>({
  path,
  options,
  schema,
  successStatusCodes = [200, 201, 204],
  deserializeFn = () => Promise.resolve(undefined),
}: FetchWrapperMutationParams<T>): Promise<z.infer<T>> => {
  const response = await fetch(path, {
    ...options,
    headers: withRequestedWith(options?.headers, {
      Accept: 'application/hal+json, application/json',
      'Content-Type': 'application/json',
    }),
  })

  if (!successStatusCodes.includes(response.status)) {
    throw new ApiError(response.status, await response.text(), path)
  }

  return await logZodError<z.infer<T>>(path, schema, async () => schema.parse(await deserializeFn(response)))
}

type OnSuccess<T, TVariables = void, TContext = unknown> = UseMutationOptions<
  T,
  DefaultError,
  TVariables,
  TContext
>['onSuccess']
type OnError<T, TVariables = void, TContext = unknown> = UseMutationOptions<
  T,
  DefaultError,
  TVariables,
  TContext
>['onError']
type OnMutate<TVariables = void, TContext = unknown> = UseMutationOptions<
  unknown,
  DefaultError,
  TVariables,
  TContext
>['onMutate']
type OnSettled<T, TVariables = void, TContext = unknown> = UseMutationOptions<
  T,
  DefaultError,
  TVariables,
  TContext
>['onSettled']

type PageQueryParams = {
  'page-number': string
  'page-size': string
}

const createPageParams = (pageIndex?: number, pageSize?: number): URLSearchParams => {
  if (pageIndex === undefined || pageSize === undefined) {
    return new URLSearchParams()
  }
  return new URLSearchParams({
    'page-number': pageIndex.toString(),
    'page-size': pageSize.toString(),
  } satisfies PageQueryParams)
}

// #endregion utils

// #region: queries

export const useGetSystemStatuses = (
  options: Omit<UseQueryOptions<SystemStatusesResponse>, 'queryKey' | 'queryFn'> = {}
) =>
  useQuery({
    queryKey: ['system-statuses'],
    queryFn: () =>
      fetchWrapperQuery({
        path: '/api/v1/system-statuses',
        schema: SystemStatusesResponseSchema,
      }),
    ...options,
  })

export const getCurrentUserQueryKey = () => ['current-user'] as const

const ROOT_FETCHED_CURRENT_USER_GRACE_MS = 5_000

export const getCurrentUserOptions = () =>
  queryOptions({
    queryKey: getCurrentUserQueryKey(),
    queryFn: () =>
      fetchWrapperQuery({
        path: '/api/v1/users/me',
        schema: CurrentUserResponseSchema,
      }),
    // `root.tsx` fetches the current user during the persisted cache's restore, before any query may
    // fetch, so mounts in the following few seconds reuse that response instead of requesting it again.
    refetchOnMount: query => Date.now() - query.state.dataUpdatedAt > ROOT_FETCHED_CURRENT_USER_GRACE_MS,
  })

export const useCurrentUser = () => useQuery(getCurrentUserOptions())

/**
 * Whether the signed-in user holds the admin role, or `undefined` when the answer is unknown.
 *
 * `undefined` covers three cases that a caller cannot tell apart and should not try to: the
 * request is in flight, it failed, or it came back without a `roles` array. Callers that hide
 * privileged controls must treat `undefined` as "we don't know" rather than "no" — collapsing it
 * to `false` flashes a permission message at an admin on every page load, and names the wrong
 * cause when the platform simply never sent the field.
 *
 * A failed request will not resolve on its own, so a caller that hides a control on `undefined`
 * should pair this with `useCurrentUser().isError` and explain itself, rather than leave an admin
 * with neither the control nor a reason.
 *
 * The `roles` array from `/api/v1/users/me` is the only authorization input the front end gets.
 * Enforcement is off by default, and with it off every signed-in user has full access — so on a
 * default install this is `true` for everyone. The fixtures and stories that model that default
 * point here rather than restating it. The platform side owns that behavior and documents it
 * under "Enforcing the admin role" in the enterprise-server docs.
 */
export const useIsAdmin = () => {
  const { data } = useCurrentUser()
  return data?.roles ? data.roles.includes('admin') : undefined
}

/**
 * The admin role plus the one decision every admin-gated write control shares: whether to offer it.
 *
 * `isAdmin === undefined` covers two situations that want opposite answers, which is why callers
 * must not re-derive this for themselves. In flight, withhold: it resolves in milliseconds, and a
 * control that appears and then vanishes is worse than a beat of nothing. Failed, offer: that
 * answer is never coming, so withholding is permanent, and a real admin would be left with neither
 * the control nor any way back to it. The server is the real authority either way — a non-admin
 * who acts on an offered control is refused with a 403 that names the permission — so deferring to
 * it costs a wasted round trip and guessing wrong costs a stranded admin.
 *
 * `isAdmin` and `isRoleUnavailable` are returned too, for surfaces that explain the withholding
 * rather than only perform it: only the failed case gets a message, since the in-flight case
 * resolves on its own. `isRolePending` is the request itself still in flight, for a surface that
 * waits rather than render a layout the role could change; `isAdmin === undefined` is not a
 * substitute, since a response without `roles` leaves it undefined for good.
 */
export const useAdminRole = () => {
  const isAdmin = useIsAdmin()
  const { isError: isRoleUnavailable, isPending: isRolePending } = useCurrentUser()
  return {
    isAdmin,
    isRoleUnavailable,
    isRolePending,
    hasWriteAccess: isAdmin === true || (isAdmin === undefined && isRoleUnavailable),
  }
}

/**
 * Runs `onDemote` when the role turns out not to be admin while a write form is open, so the caller
 * can close the form instead of leaving a non-admin in one whose submit can only 403.
 *
 * A demotion can arrive from a background refetch rather than a refused write: `useCurrentUser` runs
 * under the global `staleTime: 0`, so tabbing away and back re-reads the role. `undefined` is the
 * answer not having arrived yet, not a demotion, so it leaves an admin's form alone. When a refused
 * write is what revealed the demotion, a sticky 403 toast precedes this, so any toast `onDemote`
 * raises should read as a follow-on to it.
 */
export const useOnDemotion = ({ isWriteFormOpen, onDemote }: { isWriteFormOpen: boolean; onDemote: () => void }) => {
  const isAdmin = useIsAdmin()
  const handleDemotion = useEffectEvent(onDemote)
  useEffect(() => {
    if (isAdmin === false && isWriteFormOpen) {
      handleDemotion()
    }
  }, [isAdmin, isWriteFormOpen])
}

/** The narrowings `GET /api/v1/integrations` applies server-side, all of them optional. */
type IntegrationsQueryParams = {
  page?: PageOptions
  /**
   * Narrows to one tool. A caller that needs one tool's instances asks for them rather than reading
   * every page and filtering the result.
   */
  type?: IntegrationType
  /**
   * Case-insensitive term, matched by a rule that differs between the two halves of the collection:
   * a managed instance matches on name or base URI as substrings and on its whole id (it is a UUID,
   * where a substring match would be noise), a system instance on its id or name as substrings, and
   * never on its base URI. `ManagedIntegrationQueries.filter` and
   * `RegistryIntegrationsResource.matchesTerm` are the two implementations; `matchesIntegrationQuery`
   * in `playwright/handlers.ts` reproduces both.
   */
  search?: string
  /**
   * Narrows to one half of the collection: `managed` for the instances a user created in-product,
   * `system` for the ones an operator configured in the admin console. A caller that wants only one
   * kind asks for it rather than reading both and calling `getIsSystemInstance` on every item.
   */
  source?: IntegrationSourceResponse
}

type GetIntegrationsOptionsInput = IntegrationsQueryParams &
  // No explicit error type param: `react-query.d.ts` registers `ApiError | Error`, and naming
  // `ApiError` here would type a caller's `retry`/`throwOnError` callback more narrowly than what
  // actually reaches it.
  Omit<UseQueryOptions<IntegrationPaginatedResponse>, 'queryKey' | 'queryFn'>

const getIntegrationsOptions = ({
  page = { number: 0, size: 10 },
  type,
  search,
  source,
  ...rest
}: GetIntegrationsOptionsInput = {}) => {
  // Normalized before the key is built rather than only before the request, so `'foo '`, `'foo'`,
  // `''` and `undefined` do not resolve to two cache entries per request they actually issue. A
  // blank term is omitted rather than sent: the platform reads it as no search either way.
  const searchTerm = search?.trim() || undefined

  return queryOptions({
    // `type`, `search` and `source` are all part of the key, so two narrowings of the same
    // collection cache separately rather than one overwriting the other.
    queryKey: ['integrations', { page, type, search: searchTerm, source }],
    queryFn: () => {
      const queryParams = createPageParams(page.number, page.size)
      if (type) {
        queryParams.set('type', type)
      }
      if (source) {
        queryParams.set('source', source)
      }
      if (searchTerm) {
        queryParams.set('search', searchTerm)
      }

      return fetchWrapperQuery({
        path: `/api/v1/integrations?${queryParams}`,
        schema: IntegrationPaginatedResponseSchema,
      })
    },
    ...rest,
  })
}

/**
 * How many instances each tool has, across the whole collection.
 *
 * Answers "which tools have instances" for a tool chooser without reading the collection. The
 * counts ignore `search` and `type`, so this is one cache entry shared by every surface that needs
 * the question answered.
 *
 * The key sits under `['integrations']` even though the endpoint is its own resource rather than a
 * path beneath the collection: creating or deleting an instance changes these counts, and that
 * prefix is what the write hooks already invalidate. The key describes what invalidates the entry,
 * not what the URL looks like.
 */
const getIntegrationTypeCountsOptions = () =>
  queryOptions({
    queryKey: ['integrations', 'type-counts'],
    queryFn: () =>
      fetchWrapperQuery({
        path: '/api/v1/integration-types',
        schema: IntegrationTypeCountsResponseSchema,
      }),
  })

export const useGetIntegrationTypeCounts = () => useQuery(getIntegrationTypeCountsOptions())

/**
 * The tools that have at least one instance.
 *
 * The endpoint omits a tool with no instances, so the reported types *are* the answer and there are
 * no zeroes to filter.
 *
 * `isError` is returned alongside because `types` cannot distinguish "no tool has an instance" from
 * "the request failed" — both are `[]`, and once the query settles into an error `isLoading` is
 * `false` too. This one endpoint now decides which tools a chooser offers at all, so a caller that
 * renders an empty chooser on `[]` must read `isError` and say the request failed instead.
 * `isPending` also covers a request paused while the browser is offline, which `isLoading` does not.
 */
export const useIntegrationTypesWithInstances = () => {
  const { data, isLoading, isPending, isError } = useGetIntegrationTypeCounts()
  const types = useMemo(() => (data?.type_counts ?? []).map(({ type }) => type), [data])
  return { types, isLoading, isPending, isError }
}

/**
 * How many instances one tool has, across the whole collection.
 *
 * The endpoint's contract is that an absent tool has no instances, and this is where that rule is
 * read: the picker's "is this tool's instance field a choice at all" and the add-findings footer's
 * "does this tool need an instance picked" must be the same answer, which two independently written
 * lookups over the same payload would eventually stop being.
 *
 * `undefined` until the counts arrive, matching `useIntegrationCount` — the absent-means-zero
 * rule applies to a settled response, and a caller that read an unsettled one as 0 would tell the
 * user a tool has no instances while the request is still in flight. `undefined` for the type is 0,
 * though: no tool selected has no instances to count, and that needs no request. `isError` is what
 * separates an `undefined` count whose request failed from one still in flight.
 */
export const useIntegrationInstanceCount = (type: IntegrationType | undefined) => {
  const { data, isLoading, isError } = useGetIntegrationTypeCounts()
  if (type === undefined) {
    return { count: 0, isLoading, isError }
  }
  if (data === undefined) {
    return { count: undefined, isLoading, isError }
  }
  return { count: data.type_counts.find(entry => entry.type === type)?.count ?? 0, isLoading, isError }
}

export const useGetIntegrations = (input?: GetIntegrationsOptionsInput) => useQuery(getIntegrationsOptions(input))

// Query params only, unlike `useGetIntegrations` above: `useSuspenseQuery` rejects `enabled`,
// `placeholderData` and `throwOnError`, so `UseQueryOptions` cannot be passed through to it. The
// narrower input is what the type system allows here, not a policy about what callers may set.
export const useSuspenseGetIntegrations = (input?: IntegrationsQueryParams) =>
  useSuspenseQuery(getIntegrationsOptions(input))

/**
 * Warms what the repositories page's own surfaces read, so neither shows a loading state on open.
 *
 * Two keys because two components ask different questions: `AddRepositoryModal` suspends on the
 * first page at `ALL_INTEGRATIONS_PAGE_SIZE`, which is also the page its connection pickers read,
 * and the repository settings and add-findings drawers read the tool counts. Warming any other
 * narrowing of the collection warms nothing anyone on this page asks for — which is why this moved
 * off the unnarrowed default when the modal did.
 */
export const usePrefetchRepositoriesPageIntegrations = () => {
  const queryClient = useQueryClient()
  useEffect(() => {
    // Warming is best-effort: if either fetch fails, the surface that reads it runs its own query
    // and shows the error.
    queryClient.query(getIntegrationsOptions({ page: { number: 0, size: ALL_INTEGRATIONS_PAGE_SIZE } })).catch(() => {})
    queryClient.query(getIntegrationTypeCountsOptions()).catch(() => {})
  }, [queryClient])
}

export const ALL_INTEGRATIONS_PAGE_SIZE = 100

/**
 * The window a searchable picker requests. A collection with more than this many items has the tail
 * reachable by typing a search term, which the picker forwards as `search` — it does not page.
 */
export const PICKER_PAGE_SIZE = 25

/**
 * One page of a single tool's instances, narrowed by a search term.
 *
 * What the instance picker reads. The window, the placeholder policy and the "no tool, no request"
 * rule are all consequences of asking the collection for one tool's page, so they are stated here
 * rather than assembled at the call site out of an exported page size.
 *
 * `placeholderData: keepPreviousData` because `search` is part of the query key: without it
 * react-query resets `data` to `undefined` on every new term and the list blanks between keystrokes.
 * The caller reads `isPlaceholderData` to tell the user the list is behind their typing.
 *
 * The request is skipped until a tool is chosen: an un-narrowed page would be fetched only for the
 * caller to discard every item of a tool it did not ask for.
 */
export const useIntegrationInstances = ({ type, search }: { type?: IntegrationType; search?: string }) =>
  useGetIntegrations({
    type,
    search,
    page: { number: 0, size: PICKER_PAGE_SIZE },
    enabled: type !== undefined,
    placeholderData: keepPreviousData,
  })

// Re-runs only what failed, so a Retry does not also reload pages that are already on screen. Active
// only, so it does not re-run unmounted queries such as an old picker search.
const refetchFailedActiveQueries = (queryClient: QueryClient, queryKey: QueryKey) =>
  queryClient.refetchQueries({ queryKey, type: 'active', predicate: query => query.state.status === 'error' })

/**
 * Every instance across pages, optionally narrowed to one source.
 *
 * For the surfaces that need the whole collection rather than a page of it: the integrations tab,
 * which draws a card per instance, and the Add repository modal, which asks which connections of a
 * given type this installation has. Both pass no source, so `getAllInstances` and the connection
 * pickers do any tool/SCM split client-side — that is a question about the `type`, which the
 * endpoint does not filter on. Anything that needs less than the whole list has a narrower hook
 * alongside this one.
 *
 * `source` is a parameter rather than a second copy of this hook because the two differed only in
 * that one argument, and a copy is where the page size and the first-load rule below drift apart.
 */
export const useAllIntegrations = ({ source }: { source?: IntegrationSourceResponse } = {}) => {
  const queryClient = useQueryClient()
  // `useGetAllPages` derives its page list *from* page 0, so its result array is empty both before
  // that request settles and when the collection is genuinely empty — it cannot report the first
  // load. Subscribing to page 0 here reads the same query key, so it costs no extra request.
  const firstPage = useGetIntegrations({ source, page: { number: 0, size: ALL_INTEGRATIONS_PAGE_SIZE } })
  const results = useGetAllPages({
    getPageQueryOptions: pageOptions => getIntegrationsOptions({ source, page: pageOptions }),
    pageSize: ALL_INTEGRATIONS_PAGE_SIZE,
  })
  // A background refetch that fails keeps the page's data, and that list is still whole — unless the
  // data it kept is an empty list, which may be a stale or persisted-cache answer and would render
  // "No integrations" over a request that just failed.
  const isFirstPageFailed =
    firstPage.isLoadingError || (firstPage.isRefetchError && (firstPage.data?._embedded?.items ?? []).length === 0)
  return {
    integrations: results.flatMap(result => result.data?._embedded?.items ?? []),
    isLoading: firstPage.isLoading || results.some(result => result.isLoading),
    firstPageError: isFirstPageFailed ? firstPage.error : undefined,
    hasFailedPage: isFirstPageFailed || results.some(result => result.isLoadingError),
    // Every failed integrations query on screen rather than only this hook's pages, so the tab
    // badge's count request recovers alongside the list it labels.
    refetch: () => refetchFailedActiveQueries(queryClient, ['integrations']),
  }
}

/**
 * The same collection, suspending until its first page is in hand.
 *
 * For a caller that has to read the list on its very first render rather than react to it
 * arriving: `AddRepositoryFormContainer` decides which connection type the form opens on from
 * what the installation has, and that is initial form state, so an empty first render would
 * settle it wrongly and never revisit it.
 *
 * Suspends on the first page only. Later pages stream in as they land, and the connection pickers
 * read the same key through `useAllIntegrations`, so they find that page already resolved and never
 * render a picker-less frame before it.
 */
export const useSuspenseAllIntegrations = () => {
  const firstPage = useSuspenseQuery(getIntegrationsOptions({ page: { number: 0, size: ALL_INTEGRATIONS_PAGE_SIZE } }))
  const results = useGetAllPages({
    getPageQueryOptions: pageOptions => getIntegrationsOptions({ page: pageOptions }),
    pageSize: ALL_INTEGRATIONS_PAGE_SIZE,
  })
  // Merged rather than taken from the walk alone. `calculateAllPages` re-derives the walk from the
  // size the *response* reports, which need not be the size that was asked for — so the walk's own
  // first page can be a different query key from the one suspended on, and still in flight on this
  // render. Merging means the caller always has the suspended page, whichever way that falls.
  const byId = new Map((firstPage.data._embedded?.items ?? []).map(item => [item.id, item]))
  for (const result of results) {
    for (const item of result.data?._embedded?.items ?? []) {
      byId.set(item.id, item)
    }
  }
  return [...byId.values()]
}

/**
 * How many instances this installation has, without reading them.
 *
 * One request for a one-item page: `total` is the size of the whole collection rather than of the
 * page, so the count costs the same whether the org has three instances or three hundred. Counts
 * both kinds, because it labels the tab whose cards are both kinds.
 */
export const useIntegrationCount = () => {
  const { data, isLoading, isError } = useGetIntegrations({ page: { number: 0, size: 1 } })
  return { count: data?.total, isLoading, isError }
}

/** Passing no `path` disables the query, for the common case where the href is not known yet. */
type GetIntegrationFromHalLinkOptionsInput = { path?: string; enabled?: boolean } & Omit<
  UseQueryOptions<Integration | null>,
  'queryKey' | 'queryFn' | 'enabled'
>

const getIntegrationFromHalLinkOptions = ({
  path,
  enabled = true,
  ...rest
}: GetIntegrationFromHalLinkOptionsInput = {}) =>
  queryOptions({
    queryKey: ['integrations', 'by-href', path],
    queryFn: () => {
      // `enabled` gates the request but does not narrow the closure, so the invariant is what makes
      // reading `path` as a string here honest.
      invariant(path, 'getIntegrationFromHalLinkOptions requires a path once the query is enabled')
      return fetchWrapperQuery({
        path,
        schema: z.union([z.null(), ModelledIntegrationSchema]).describe('ModelledIntegration'),
        successStatusCodes: [200, 404],
        deserializeFn: async (response: Response) =>
          response.status === 404 ? null : ((await response.json()) as unknown),
      })
    },
    enabled: enabled && !!path,
    ...rest,
  })

/**
 * One instance, fetched from the HAL link that names it.
 *
 * For a caller holding a `_links.integration.href` from somewhere else — a repository's mapping, say
 * — rather than an id. Following the link is what HAL is for, and it replaces scanning every page of
 * the collection for the item whose `self` href matches. A tool this client does not model parses to
 * `null`, matching how the collection drops such an item rather than failing.
 *
 * A 404 resolves to `null` too: a link can outlive what it names, and a mapping can be linked to a
 * system default the installation never configured. Neither is fixed by retrying.
 */
export const useGetIntegrationFromHalLink = (input?: GetIntegrationFromHalLinkOptionsInput) =>
  useQuery(getIntegrationFromHalLinkOptions(input))

const ALLOWED_METHODS_STALE_TIME_MS = 300_000

const getIntegrationsAllowedMethodsOptions = () =>
  queryOptions({
    // Deliberately outside the ['integrations', …] key space: what an installation permits does not
    // change when its instances do, so the write hooks' invalidation must not re-run this probe.
    queryKey: ['integrations-allowed-methods'],
    queryFn: () =>
      fetchWrapperQuery({
        path: '/api/v1/integrations',
        options: { method: 'OPTIONS' },
        schema: AllowedMethodsSchema,
        // 405 is the documented answer for a read-only installation, and it is the response that
        // carries `Allow`. Treating it as a failure would throw before the header could be read.
        successStatusCodes: [200, 204, 405],
        deserializeFn: async response => response.headers.get('Allow'),
      }),
    // An unanswered probe is an expected negative, not a failed data fetch: the caller reads it as
    // "management is on", so there is nothing to gain from retries or a Sentry event.
    retry: false,
    throwOnError: false,
    // Not `Infinity`: the answer is persisted to localStorage, so an installation that turns
    // management on would stay read-only across reloads until the cache happened to be evicted.
    staleTime: ALLOWED_METHODS_STALE_TIME_MS,
  })

export const useGetIntegrationsAllowedMethods = () => useQuery(getIntegrationsAllowedMethodsOptions())

/** A platform that predates the managed identities endpoint, which has none to offer. */
const isAzureManagedIdentitiesUnsupported = (error: unknown) => error instanceof ApiError && error.status === 404

type AzureManagedIdentitiesOptionsInput = Omit<UseQueryOptions<AzureManagedIdentitiesResponse>, 'queryKey' | 'queryFn'>

const getAzureManagedIdentitiesOptions = (options: AzureManagedIdentitiesOptionsInput = {}) =>
  queryOptions({
    queryKey: ['azure-managed-identities'],
    queryFn: () =>
      fetchWrapperQuery({ path: '/api/v1/azure-managed-identities', schema: AzureManagedIdentitiesResponseSchema }),
    // Only a 404 is read as no identities. Any other failure is retried and reported like any
    // query's, and still leaves no data, so the Setup drawer does not offer managed identity.
    ...inlineAnswerQueryPolicy(isAzureManagedIdentitiesUnsupported),
    ...options,
  })

/** The managed identities the operator added for Azure DevOps connections. */
export const useGetAzureManagedIdentities = (options?: AzureManagedIdentitiesOptionsInput) =>
  useQuery(getAzureManagedIdentitiesOptions(options))

/** Appends `params` to a path or link href, which may already have a query string of its own. */
const withQueryParams = (href: string, params: URLSearchParams) => {
  const query = params.toString()
  return query ? `${href}${href.includes('?') ? '&' : '?'}${query}` : href
}

const withRepositoryEmbed = (path: string, embed?: RepositoryEmbedParameter[]) =>
  withQueryParams(path, new URLSearchParams(embed?.map(value => ['embed', value])))

export const useGetRepositoryFromHalLink = ({ path, enabled = true }: { path?: string; enabled?: boolean }) =>
  useQuery({
    queryKey: ['repository', path],
    queryFn: () => fetchWrapperQuery({ path: path!, schema: RepositorySchema }),
    enabled: enabled && !!path,
  })

/**
 * Refetches every repository read, including the settings drawer's embedded read. Used when a
 * repository's project membership may have changed under an open form.
 */
const invalidateRepositories = (client: QueryClient) => client.invalidateQueries({ queryKey: ['repository'] })

const getRepositoryQueryKey = (repositoryId?: string, embed?: RepositoryEmbedParameter[]) => [
  'repository',
  repositoryId,
  embed ?? [],
]

const getRepositoryOptions = (repositoryId: string | undefined, embed?: RepositoryEmbedParameter[]) =>
  queryOptions({
    queryKey: getRepositoryQueryKey(repositoryId, embed),
    queryFn: () =>
      fetchWrapperQuery({
        path: withRepositoryEmbed(
          buildPath('/api/v1/repositories/:repositoryId', { repositoryId: repositoryId ?? '' }),
          embed
        ),
        schema: RepositorySchema,
      }),
    enabled: !!repositoryId,
  })

export const useGetRepository = ({
  repositoryId,
  embed,
}: {
  repositoryId?: string
  embed?: RepositoryEmbedParameter[]
}) => useQuery(getRepositoryOptions(repositoryId, embed))

export const useSuspenseGetRepository = ({
  repositoryId,
  embed,
}: {
  repositoryId: string
  embed?: RepositoryEmbedParameter[]
}) => useSuspenseQuery(getRepositoryOptions(repositoryId, embed))

/** The `project` filter value that selects repositories in no project. */
export const UNGROUPED_PROJECT_FILTER = 'ungrouped'

type RepositoriesPageParams = {
  pageIndex: number
  pageSize: number
  searchTerm?: string
  /** Project ids, or {@link UNGROUPED_PROJECT_FILTER}, sent as repeated `project` params. Empty means no filter. */
  projectIds?: string[]
  /**
   * Narrows the list to the repositories that reference one integration instance, sent as
   * `integration-id`. The platform answers 422 `unknown-integration` for an id it does not know, and
   * 400 `AdminConsoleIntegrationProblem` for an instance configured in the admin console, because
   * repositories that use one record no integration.
   */
  integrationId?: NonNullable<paths['/api/v1/repositories']['get']['parameters']['query']>['integration-id']
  /**
   * Embeds each repository's membership and the project it names, for a list that shows which project a
   * repository is in. Off by default: a list whose rows are all in one project, or that shows no project
   * at all, pays for the join and the embedded projects for nothing.
   */
  embedProjectMembership?: boolean
}

const getRepositoriesQueryKey = (params?: RepositoriesPageParams) =>
  params
    ? [
        'repositories',
        params.pageIndex,
        params.pageSize,
        params.searchTerm,
        params.projectIds ?? [],
        params.embedProjectMembership ?? false,
        params.integrationId,
      ]
    : ['repositories']

/**
 * One page of repositories.
 *
 * Exposed as options, not only as a hook, so a caller that needs the whole list can fan out over page
 * indices with `useQueries` and still share this cache — see `use-org-threat-posture.ts`.
 */
export const getRepositoriesQueryOptions = ({
  pageIndex,
  pageSize,
  searchTerm = '',
  projectIds = [],
  embedProjectMembership = false,
  integrationId,
}: RepositoriesPageParams) =>
  queryOptions({
    queryKey: getRepositoriesQueryKey({
      pageIndex,
      pageSize,
      searchTerm,
      projectIds,
      embedProjectMembership,
      integrationId,
    }),
    queryFn: async () => {
      const queryParams = createPageParams(pageIndex, pageSize)

      if (searchTerm) {
        queryParams.append('name', searchTerm)
      }

      appendQueryParam(queryParams, 'project', projectIds)

      if (integrationId) {
        queryParams.append('integration-id', integrationId)
      }

      if (embedProjectMembership) {
        // The membership and the project it names, so a list that shows which project a repository is in
        // — the repositories table's project chip, the add dialog's "already in <project>" hint — has it
        // inline rather than per row.
        queryParams.append('embed', 'items.project-membership.project')
      }

      return fetchWrapperQuery({
        path: `/api/v1/repositories?${queryParams}`,
        schema: RepositoryPaginatedResponseSchema,
      })
    },
  })

export const useGetRepositories = (params: RepositoriesPageParams) => {
  return useQuery({
    placeholderData: keepPreviousData,
    ...getRepositoriesQueryOptions(params),
  })
}

/**
 * How many repositories the organization has, read off the `total` of a one-item page. Shares the
 * `['repositories']` prefix so a membership change invalidates it with the lists.
 */
export const useGetRepositoryCount = ({ enabled = true }: { enabled?: boolean } = {}) =>
  useQuery({
    ...getRepositoriesQueryOptions({ pageIndex: 0, pageSize: 1 }),
    select: data => data.total,
    enabled,
  })

export const getScanQueryKey = ({ id, embed }: { id?: string; embed?: ScanEmbedParameter[] }) => [
  'scan',
  id,
  embed ?? [],
]

export const getScanOptions = ({
  id,
  embed,
  enabled = true,
  ...rest
}: Partial<Omit<UseQueryOptions<ScanResponse, ApiError>, 'queryKey' | 'queryFn'>> &
  Parameters<typeof getScanQueryKey>[0]) =>
  queryOptions({
    queryKey: getScanQueryKey({ id, embed }),
    queryFn: () => {
      const queryParams = new URLSearchParams()
      appendQueryParam(queryParams, 'embed', embed ?? [])
      const path = buildPath('/api/v1/scans/:id', { id: id ?? '' })
      return fetchWrapperQuery({
        path: queryParams.size ? `${path}?${queryParams}` : path,
        schema: ScanResponseSchema,
      })
    },
    enabled: enabled && !!id,
    ...rest,
  })

export const useGetScan = (options: Parameters<typeof getScanOptions>[0]) => useQuery(getScanOptions(options))

const SCANS_QUERY_KEY_ROOT = 'scans'

/**
 * The prefix every scans query key starts with, whatever its page and filters.
 *
 * Invalidate with this rather than with a key built for particular filters: React Query matches a
 * key partially, so a key carrying a page object only ever matches a query paginated exactly that
 * way. @see getScanFindingsQueryKeyPrefix
 */
export const getScansQueryKeyPrefix = () => [SCANS_QUERY_KEY_ROOT] as const

/** Refresh every scans list, whatever page or filters it was fetched with. */
export const invalidateGetScans = (client: QueryClient) =>
  client.invalidateQueries({ queryKey: getScansQueryKeyPrefix() })

export const getScansQueryKey = ({
  page,
  filters,
}: {
  page: PageOptions
  filters: {
    repositoryIds?: string[]
    hasAnalysis?: boolean
    tools?: string[]
    branch?: string
    analysisState?: ScanAnalysisState
  }
}) => (filters ? [SCANS_QUERY_KEY_ROOT, page, filters] : [SCANS_QUERY_KEY_ROOT, page])

export const getScansOptions = ({
  page = { number: 0, size: 10 },
  repositoryIds,
  hasAnalysis,
  tools,
  branch,
  analysisState,
  enabled = true,
  refetchInterval,
}: {
  page?: PageOptions
  repositoryIds?: string[]
  hasAnalysis?: boolean
  tools?: Tool[]
  /** Exact branch name. Omitted for every branch. */
  branch?: string
  analysisState?: ScanAnalysisState
  enabled?: boolean
  refetchInterval?: number | false
}) =>
  queryOptions({
    queryKey: getScansQueryKey({
      page,
      filters: { repositoryIds, hasAnalysis, tools, branch, analysisState },
    }),
    queryFn: () => {
      const queryParams = createPageParams(page.number, page.size)

      if (repositoryIds && repositoryIds.length > 0) {
        repositoryIds.forEach(id => queryParams.append('repository-ids', id))
      }

      if (hasAnalysis !== undefined) {
        queryParams.append('has-analysis', String(hasAnalysis))
      }

      if (tools && tools.length > 0) {
        tools.forEach(tool => queryParams.append('tools', tool))
      }

      if (branch) {
        queryParams.append('branch', branch)
      }

      if (analysisState) {
        queryParams.append('analysis-state', analysisState)
      }

      return fetchWrapperQuery({
        path: `/api/v1/scans?${queryParams}`,
        schema: ScanAggregateResponseSchema,
      })
    },
    enabled,
    refetchInterval,
  })

export const useGetScans = (options: Parameters<typeof getScansOptions>[0]) => useQuery(getScansOptions(options))

export const useGetScansWithRepositories = (options: Parameters<typeof getScansOptions>[0]) =>
  useQueryWithLinks(getScansOptions(options), [
    {
      key: '_embedded.items._links.repository.href',
      schema: RepositorySchema,
    },
  ])

// #region Repository branches

const REPOSITORY_BRANCHES_QUERY_KEY_ROOT = 'repository-branches'

/**
 * The key every branches query for one repository's `branches` link and one tool starts with. The one
 * place the key's layout is written down: both options helpers spread it, and cache lookups and
 * placeholder checks match against it instead of reading the key by position.
 */
export const getRepositoryBranchesKeyPrefix = ({
  branchesHref,
  tool,
}: {
  branchesHref: string | undefined
  tool: Tool | undefined
}) => [REPOSITORY_BRANCHES_QUERY_KEY_ROOT, branchesHref, tool] as const

const fetchRepositoryBranches = ({
  branchesHref,
  tool,
  search,
  names,
  embed,
  page,
}: {
  branchesHref: string
  tool?: Tool
  search?: string
  names?: readonly string[]
  embed?: readonly BranchEmbedParameter[]
  page: PageOptions
}) => {
  const queryParams = createPageParams(page.number, page.size)
  if (tool) {
    queryParams.append('tool', tool)
  }
  if (search) {
    queryParams.append('search', search)
  }
  for (const name of names ?? []) {
    queryParams.append('name', name)
  }
  for (const value of embed ?? []) {
    queryParams.append('embed', value)
  }

  return fetchWrapperQuery({ path: withQueryParams(branchesHref, queryParams), schema: BranchPaginatedResponseSchema })
}

type GetRepositoryBranchesOptionsInput = {
  /** The repository's `branches` link; the query waits until it is known. */
  branchesHref: string | undefined
  /** Only branches with scans from this tool count. Omitted for every tool. */
  tool?: Tool
  /**
   * Exact branch names to look up. Must not be empty once the query is enabled: the endpoint reads
   * no `name` parameter as "every branch", the opposite of what an empty filter means.
   */
  names?: readonly string[]
  /** Relations to inline in each branch, e.g. its most recently imported scan. */
  embed?: readonly BranchEmbedParameter[]
  page?: PageOptions
  enabled?: boolean
} & Omit<UseQueryOptions<BranchPaginatedResponse>, 'queryKey' | 'queryFn' | 'enabled'>

const getRepositoryBranchesOptions = ({
  branchesHref,
  tool,
  names,
  embed,
  page = { number: 0, size: 20 },
  enabled = true,
  ...rest
}: GetRepositoryBranchesOptionsInput) => {
  // Sorted so the same set of names shares one cache entry whatever order the caller lists them in.
  const sortedNames = names && [...names].sort()
  return queryOptions({
    queryKey: [...getRepositoryBranchesKeyPrefix({ branchesHref, tool }), { names: sortedNames, embed, page }],
    queryFn: () => {
      // `enabled` gates the request but does not narrow the closure.
      invariant(branchesHref, 'getRepositoryBranchesOptions requires the branches link once the query is enabled')
      invariant(!sortedNames || sortedNames.length > 0, 'An empty `names` filter would list every branch')
      return fetchRepositoryBranches({ branchesHref, tool, names: sortedNames, embed, page })
    },
    enabled: enabled && !!branchesHref,
    ...rest,
  })
}

/** A repository's branches with scans (from `tool`, when given), newest import first. */
export const useGetRepositoryBranches = (input: GetRepositoryBranchesOptionsInput) =>
  useQuery(getRepositoryBranchesOptions(input))

/**
 * Looks up `names` among the branches `tool` has scanned, outside of render — for an event handler
 * that has to know the answer before it acts. Shares the cache entry `useGetRepositoryBranches` uses for
 * the same names, but with the global `staleTime` of 0 that entry is never fresh, so every call
 * refetches.
 */
export const fetchRepositoryBranchesByName = (
  client: QueryClient,
  { branchesHref, tool, names }: { branchesHref: string; tool?: Tool; names: readonly string[] }
) => client.query(getRepositoryBranchesOptions({ branchesHref, tool, names, page: { number: 0, size: names.length } }))

type GetInfiniteRepositoryBranchesOptionsInput = {
  /** The repository's `branches` link; the query waits until it is known. */
  branchesHref: string | undefined
  tool: Tool
  search?: string
  /** Relations to inline in each branch, e.g. its most recently imported scan. */
  embed?: readonly BranchEmbedParameter[]
  pageSize?: number
  enabled?: boolean
} & Omit<
  UseInfiniteQueryOptions<
    BranchPaginatedResponse,
    DefaultError,
    InfiniteData<BranchPaginatedResponse>,
    QueryKey,
    number
  >,
  'queryKey' | 'queryFn' | 'enabled' | 'initialPageParam' | 'getNextPageParam'
>

const getInfiniteRepositoryBranchesOptions = ({
  branchesHref,
  tool,
  search,
  embed,
  pageSize = 30,
  enabled = true,
  ...rest
}: GetInfiniteRepositoryBranchesOptionsInput) =>
  infiniteQueryOptions({
    queryKey: [...getRepositoryBranchesKeyPrefix({ branchesHref, tool }), 'infinite', { search, embed, pageSize }],
    queryFn: ({ pageParam }) => {
      invariant(branchesHref, 'getInfiniteRepositoryBranchesOptions requires the branches link once enabled')
      return fetchRepositoryBranches({ branchesHref, tool, search, embed, page: { number: pageParam, size: pageSize } })
    },
    initialPageParam: 0,
    getNextPageParam: lastPage =>
      (lastPage.page.number + 1) * lastPage.page.size < lastPage.total ? lastPage.page.number + 1 : undefined,
    enabled: enabled && !!branchesHref,
    ...rest,
  })

/** Every page of a repository's branches with scans from `tool`, fetched one page at a time on demand. */
export const useGetInfiniteRepositoryBranches = (input: GetInfiniteRepositoryBranchesOptionsInput) =>
  useInfiniteQuery(getInfiniteRepositoryBranchesOptions(input))

// #endregion

export const useGetScanAnalysis = ({
  analysisId,
  enabled = true,
  ...rest
}: {
  analysisId?: string
} & Omit<UseQueryOptions<AnalysisResponse>, 'queryKey' | 'queryFn'>) =>
  useQuery({
    queryKey: ['scan-analysis', analysisId],
    queryFn: () =>
      fetchWrapperQuery({
        path: buildPath('/api/v1/analyses/:analysisId', { analysisId: analysisId ?? '' }),
        schema: AnalysisResponseSchema,
      }),
    enabled: enabled && !!analysisId,
    ...rest,
  })

export const useGetAnalysisFromHalLink = ({ path, enabled = true }: { path?: string; enabled?: boolean }) =>
  useQuery({
    queryKey: ['analysis', path],
    queryFn: () => fetchWrapperQuery({ path: path!, schema: AnalysisResponseSchema }),
    enabled: enabled && !!path,
  })

export const useGetTriageResultArticle = ({
  resultId,
  enabled = true,
  ...rest
}: {
  resultId?: string
} & Omit<UseQueryOptions<string>, 'queryKey' | 'queryFn'>) =>
  useQuery({
    queryKey: ['triageResultArticle', resultId],
    queryFn: () =>
      fetchWrapperQuery({
        path: buildPath('/api/v1/triage-results/:resultId/article', { resultId: resultId ?? '' }),
        options: {
          headers: {
            'X-Requested-With': 'JavaScript',
            Accept: 'text/markdown',
          },
        },
        schema: z.string(),
        successStatusCodes: [200, 204],
        deserializeFn: (response: Response) => response.text(),
      }),
    enabled: enabled && !!resultId,
    ...rest,
  })

export const useGetScaResultArticle = ({ resultId, enabled = true }: { resultId?: string; enabled?: boolean }) =>
  useQuery({
    queryKey: ['scaResultArticle', resultId],
    queryFn: () =>
      fetchWrapperQuery({
        path: buildPath('/api/v1/sca-results/:resultId/article', { resultId: resultId ?? '' }),
        options: {
          headers: {
            'X-Requested-With': 'JavaScript',
            Accept: 'text/markdown',
          },
        },
        schema: z.string(),
        successStatusCodes: [200, 204],
        deserializeFn: (response: Response) => response.text(),
      }),
    enabled: enabled && !!resultId,
  })

export const useGetScaResultReport = ({
  resultId,
  enabled = true,
  ...rest
}: {
  resultId?: string
} & Omit<UseQueryOptions<string>, 'queryKey' | 'queryFn'>) =>
  useQuery({
    queryKey: ['scaResultReport', resultId],
    queryFn: () =>
      fetchWrapperQuery({
        path: buildPath('/api/v1/sca-results/:resultId/report', { resultId: resultId ?? '' }),
        options: {
          headers: {
            'X-Requested-With': 'JavaScript',
            Accept: 'text/markdown',
          },
        },
        schema: z.string(),
        successStatusCodes: [200, 204],
        deserializeFn: (response: Response) => response.text(),
      }),
    enabled: enabled && !!resultId,
    retry: 1,
    ...rest,
  })

export const useGetTriageResultReport = ({
  resultId,
  enabled = true,
  ...rest
}: {
  resultId?: string
} & Omit<UseQueryOptions<string>, 'queryKey' | 'queryFn'>) =>
  useQuery({
    queryKey: ['triageResultReport', resultId],
    queryFn: () =>
      fetchWrapperQuery({
        path: buildPath('/api/v1/triage-results/:resultId/report', { resultId: resultId ?? '' }),
        options: {
          headers: {
            'X-Requested-With': 'JavaScript',
            Accept: 'text/markdown',
          },
        },
        schema: z.string(),
        successStatusCodes: [200, 204],
        deserializeFn: (response: Response) => response.text(),
      }),
    enabled: enabled && !!resultId,
    ...rest,
  })

export const useGetTriageResultVerification = ({
  resultId,
  enabled = true,
  ...rest
}: {
  resultId?: string
} & Omit<UseQueryOptions<ResultsVerificationResponse | null>, 'queryKey' | 'queryFn'>) =>
  useQuery({
    queryKey: ['triageResultVerification', resultId],
    queryFn: () =>
      fetchWrapperQuery({
        path: buildPath('/api/v1/triage-results/:resultId/verification', { resultId: resultId ?? '' }),
        schema: z.union([ResultsVerificationResponseSchema, z.null()]),
        successStatusCodes: [200, 404],
        deserializeFn: async (response: Response) => {
          if (response.status === 404) {
            return null
          }

          return (await response.json()) as unknown
        },
      }),
    enabled: enabled && !!resultId,
    ...rest,
  })

/**
 * Helper function to append query parameters to URLSearchParams
 * Handles both single values and arrays of values
 */
const appendQueryParam = (params: URLSearchParams, key: string, value: string | string[]) => {
  if (Array.isArray(value)) {
    for (const item of value) {
      params.append(key, item)
    }
  } else {
    params.append(key, value)
  }
}

/**
 * The findings filters, by the names the client uses for them.
 *
 * One list, because the same projection of the filter state feeds two consumers: the findings query
 * and the CSV export. When each spelled its own list, a filter added or renamed in one and missed
 * in the other type-checked and silently stopped filtering that consumer's results.
 */
export type ScanFindingsFilters = {
  analyzed?: boolean
  fixStatuses?: string[]
  triageStatuses?: string[]
  triageSuggestedStatuses?: string[]
  patchStatuses?: string[]
  scaStatus?: ScaStatusResponse
  fixConfidences?: string[]
  scaClassifications?: string[]
  severityUpdates?: string[]
  includeSeverityLabels?: string[]
  includeSuggestedSeverityLabels?: string[]
  minimumSeverityScore?: number
  maximumSeverityScore?: number
  minimumSuggestedSeverityScore?: number
  maximumSuggestedSeverityScore?: number
  sort?: string
  order?: string
}

/** The filters as the request layer takes them: paging spelled out, free text as the API names it. */
export type ScanFindingsQueryParams = ScanFindingsFilters & {
  pageIndex?: number
  pageSize?: number
  q?: string
}

/**
 * Whether any list filter admits nothing. `undefined` leaves a dimension unconstrained, but an empty
 * list means no value passes — which is what intersecting a user's filter with a Data scope that
 * excludes all of it produces. The request cannot say that: `buildScanFindingsQueryParams` omits
 * empty lists, and the API then reads the dimension as unconstrained and returns every finding. So
 * callers short-circuit on this instead of sending the request.
 */
export const hasEmptyFilterSelection = (filters: ScanFindingsFilters): boolean =>
  [
    filters.fixStatuses,
    filters.triageStatuses,
    filters.triageSuggestedStatuses,
    filters.patchStatuses,
    filters.fixConfidences,
    filters.scaClassifications,
    filters.severityUpdates,
    filters.includeSeverityLabels,
    filters.includeSuggestedSeverityLabels,
  ].some(values => values?.length === 0)

/** The response a findings query would return if the API matched nothing. @see hasEmptyFilterSelection */
export const buildEmptyFindingAggregateResponse = ({
  pageIndex,
  pageSize,
}: {
  pageIndex: number
  pageSize: number
}): FindingAggregateResponse => ({
  _embedded: { items: [] },
  _links: { first: { href: '#' }, self: { href: '#' } },
  page: { number: pageIndex, size: pageSize },
  total: 0,
  completed_analysis: 0,
  in_progress_analysis: 0,
  composite: { no_fix_necessary: 0, ready_to_fix: 0 },
  triage: {
    outcome: {
      blocked: 0,
      excluded: 0,
      false_positive: 0,
      inconclusive: 0,
      likely_true_positive: 0,
      not_triaged: 0,
      true_positive: 0,
      wont_fix: 0,
    },
    status: { completed: 0, failed: 0, in_progress: 0, no_recommendations: 0 },
  },
  fix: {
    confidence: { high: 0, low: 0, medium: 0, no_rating: 0 },
    outcome: { blocked: 0, excluded: 0, has_fix: 0 },
    status: { completed: 0, failed: 0, in_progress: 0, no_recommendations: 0 },
  },
  sca: {
    classification: { exploitable: 0, inconclusive: 0, not_exploitable: 0 },
    status: { blocked: 0, completed: 0, excluded: 0, failed: 0, in_progress: 0 },
  },
})

export const buildScanFindingsQueryParams = ({
  pageIndex,
  pageSize,
  analyzed,
  fixStatuses,
  triageStatuses,
  triageSuggestedStatuses,
  patchStatuses,
  scaStatus,
  fixConfidences,
  scaClassifications,
  severityUpdates,
  includeSeverityLabels,
  includeSuggestedSeverityLabels,
  minimumSeverityScore,
  maximumSeverityScore,
  minimumSuggestedSeverityScore,
  maximumSuggestedSeverityScore,
  q,
  sort,
  order,
  embed,
}: ScanFindingsQueryParams & { embed?: FindingEmbedParameter[] }): URLSearchParams => {
  const params = createPageParams(pageIndex, pageSize)

  if (analyzed !== undefined) {
    appendQueryParam(params, SCAN_FINDINGS_PARAMS.analyzed, String(analyzed))
  }

  if (fixStatuses?.length) {
    appendQueryParam(params, SCAN_FINDINGS_PARAMS.fixStatus, fixStatuses)
  }

  if (triageStatuses?.length) {
    appendQueryParam(params, SCAN_FINDINGS_PARAMS.triageStatus, triageStatuses)
  }

  if (triageSuggestedStatuses?.length) {
    appendQueryParam(params, SCAN_FINDINGS_PARAMS.triageSuggestedStatus, triageSuggestedStatuses)
  }

  if (patchStatuses?.length) {
    appendQueryParam(params, SCAN_FINDINGS_PARAMS.patchStatus, patchStatuses)
  }

  if (scaStatus) {
    appendQueryParam(params, SCAN_FINDINGS_PARAMS.scaStatus, scaStatus)
  }

  if (fixConfidences?.length) {
    appendQueryParam(params, SCAN_FINDINGS_PARAMS.fixConfidences, fixConfidences)
  }

  if (scaClassifications?.length) {
    appendQueryParam(params, SCAN_FINDINGS_PARAMS.scaClassifications, scaClassifications)
  }

  if (severityUpdates?.length) {
    appendQueryParam(params, SCAN_FINDINGS_PARAMS.severityUpdates, severityUpdates)
  }

  if (includeSeverityLabels?.length) {
    appendQueryParam(params, SCAN_FINDINGS_PARAMS.includeSeverityLabel, includeSeverityLabels)
  }

  if (includeSuggestedSeverityLabels?.length) {
    appendQueryParam(params, SCAN_FINDINGS_PARAMS.includeSuggestedSeverityLabel, includeSuggestedSeverityLabels)
  }

  if (minimumSeverityScore !== undefined) {
    appendQueryParam(params, SCAN_FINDINGS_PARAMS.minimumSeverityScore, `${minimumSeverityScore}`)
  }

  if (maximumSeverityScore !== undefined) {
    appendQueryParam(params, SCAN_FINDINGS_PARAMS.maximumSeverityScore, `${maximumSeverityScore}`)
  }

  if (minimumSuggestedSeverityScore !== undefined) {
    appendQueryParam(params, SCAN_FINDINGS_PARAMS.minimumSuggestedSeverityScore, `${minimumSuggestedSeverityScore}`)
  }

  if (maximumSuggestedSeverityScore !== undefined) {
    appendQueryParam(params, SCAN_FINDINGS_PARAMS.maximumSuggestedSeverityScore, `${maximumSuggestedSeverityScore}`)
  }

  if (q) {
    appendQueryParam(params, SCAN_FINDINGS_PARAMS.q, q)
  }

  if (sort) {
    appendQueryParam(params, SCAN_FINDINGS_PARAMS.sort, sort.toLowerCase())
  }

  if (order) {
    appendQueryParam(params, SCAN_FINDINGS_PARAMS.order, order.toLowerCase())
  }

  if (embed?.length) {
    appendQueryParam(params, SCAN_FINDINGS_PARAMS.embed, embed)
  }

  return params
}

export const useGetFixResultChangesets = ({
  resultId,
  pageIndex = 0,
  pageSize = 10,
  enabled = true,
  ...rest
}: {
  resultId?: string
  pageIndex?: number
  pageSize?: number
} & Omit<UseQueryOptions<ChangesetPaginatedResponse>, 'queryKey' | 'queryFn'>) =>
  useQuery({
    queryKey: ['changesetsForFixResult', resultId, pageIndex, pageSize],
    queryFn: () =>
      fetchWrapperQuery({
        path: `${buildPath('/api/v1/fix-results/:resultId/changesets', { resultId: resultId ?? '' })}?page-number=${pageIndex}&page-size=${pageSize}`,
        schema: ChangesetPaginatedResponseSchema,
      }),
    enabled: enabled && !!resultId,
    ...rest,
  })

export const useGetFixResultPatches = ({
  resultId,
  pageIndex = 0,
  pageSize = 10,
  enabled = true,
  ...rest
}: {
  resultId?: string
  pageIndex?: number
  pageSize?: number
} & Omit<UseQueryOptions<PatchPaginatedResponse>, 'queryKey' | 'queryFn'>) =>
  useQuery({
    queryKey: ['patchesForFixResult', resultId, pageIndex, pageSize],
    queryFn: () =>
      fetchWrapperQuery({
        path: `${buildPath('/api/v1/fix-results/:resultId/patches', { resultId: resultId ?? '' })}?page-number=${pageIndex}&page-size=${pageSize}`,
        schema: PatchPaginatedResponseSchema,
      }),
    enabled: enabled && !!resultId,
    ...rest,
  })

/**
 * A picker asks for the first page of what matches `search` and never builds a later one: the
 * platform pages by an opaque cursor that only its own `_links.next` carries.
 */
const createPickerSearchParams = (search?: string) => {
  const queryParams = new URLSearchParams({ 'page-size': PICKER_PAGE_SIZE.toString() })
  if (search) {
    queryParams.append('search', search)
  }
  return queryParams
}

type GetAzureOrganizationsOptionsInput = {
  integrationId?: string
  enabled?: boolean
}

const getAzureOrganizationsOptions = ({ integrationId, enabled = true }: GetAzureOrganizationsOptionsInput = {}) =>
  queryOptions({
    queryKey: ['azure-organizations', integrationId],
    queryFn: () => {
      // `enabled` gates the request but does not narrow the closure, so the invariant is what
      // makes the id a string.
      invariant(integrationId, 'getAzureOrganizationsOptions requires an integrationId once the query is enabled')

      return fetchWrapperQuery({
        path: `${buildPath('/api/v1/integrations/:integrationId/organizations', { integrationId })}?${createPickerSearchParams()}`,
        schema: AzureOrganizationPaginatedResponseSchema,
      })
    },
    enabled: enabled && !!integrationId,
  })

export const useGetAzureOrganizations = (input?: GetAzureOrganizationsOptionsInput) =>
  useQuery(getAzureOrganizationsOptions(input))

/**
 * What a picker's search hook adds to its lookup: the previous answer stands in while the next
 * term's loads, and rejected credentials are the answer rather than a fault.
 */
const PICKER_SEARCH_OPTIONS = {
  placeholderData: keepPreviousData,
  ...inlineAnswerQueryPolicy(isRejectedCredentialsError),
}

const getAzureProjectsOptions = ({
  integrationId,
  organizationName,
  search,
  enabled = true,
  ...rest
}: {
  integrationId?: string
  organizationName?: string
  /** A case-insensitive fragment of the project name. */
  search?: string
  enabled?: boolean
} & Omit<UseQueryOptions<AzureProjectPaginatedResponse>, 'queryKey' | 'queryFn'>) =>
  queryOptions({
    queryKey: ['azure-projects', integrationId, organizationName, search],
    queryFn: () => {
      // `enabled` gates the request but does not narrow the closure, so the invariants are what
      // make the names strings.
      invariant(integrationId, 'getAzureProjectsOptions requires an integrationId once the query is enabled')
      invariant(organizationName, 'getAzureProjectsOptions requires an organizationName once the query is enabled')

      return fetchWrapperQuery({
        path: `${buildPath('/api/v1/integrations/:integrationId/:organizationName/projects', { integrationId, organizationName })}?${createPickerSearchParams(search)}`,
        schema: AzureProjectPaginatedResponseSchema,
      })
    },
    enabled: enabled && !!integrationId && !!organizationName,
    ...rest,
  })

/** The first page of an Azure DevOps organization's projects whose names match `search`. */
export const useAzureProjectSearch = (input: {
  integrationId: string | undefined
  organizationName: string | undefined
  search?: string
}) => useQuery(getAzureProjectsOptions({ ...input, ...PICKER_SEARCH_OPTIONS }))

const getAzureRepositoriesOptions = ({
  integrationId,
  organizationName,
  projectId,
  search,
  enabled = true,
  ...rest
}: {
  integrationId?: string
  organizationName?: string
  projectId?: string
  /** A case-insensitive fragment of the repository name. */
  search?: string
  enabled?: boolean
} & Omit<UseQueryOptions<AzureRepositoryPaginatedResponse>, 'queryKey' | 'queryFn'>) =>
  queryOptions({
    queryKey: ['azure-repositories', integrationId, organizationName, projectId, search],
    queryFn: () => {
      // `enabled` gates the request but does not narrow the closure, so the invariants are what
      // make the names strings.
      invariant(integrationId, 'getAzureRepositoriesOptions requires an integrationId once the query is enabled')
      invariant(organizationName, 'getAzureRepositoriesOptions requires an organizationName once the query is enabled')
      invariant(projectId, 'getAzureRepositoriesOptions requires a projectId once the query is enabled')

      return fetchWrapperQuery({
        path: `${buildPath('/api/v1/integrations/:integrationId/:organizationName/projects/:projectId/repositories', { integrationId, organizationName, projectId })}?${createPickerSearchParams(search)}`,
        schema: AzureRepositoryPaginatedResponseSchema,
      })
    },
    enabled: enabled && !!integrationId && !!organizationName && !!projectId,
    ...rest,
  })

/** The first page of an Azure DevOps project's repositories whose names match `search`. */
export const useAzureRepositorySearch = (input: {
  integrationId: string | undefined
  organizationName: string | undefined
  projectId: string | undefined
  search?: string
}) => useQuery(getAzureRepositoriesOptions({ ...input, ...PICKER_SEARCH_OPTIONS }))

const getBitbucketWorkspacesOptions = ({
  integrationId,
  search,
  enabled = true,
  ...rest
}: {
  integrationId?: string
  /** A case-insensitive fragment of the workspace slug; Bitbucket gives the platform no name to match. */
  search?: string
  enabled?: boolean
} & Omit<UseQueryOptions<BitbucketWorkspacePaginatedResponse>, 'queryKey' | 'queryFn'>) =>
  queryOptions({
    queryKey: ['bitbucket-workspaces', integrationId, search],
    queryFn: () => {
      // `enabled` gates the request but does not narrow the closure, so the invariant is what
      // makes the id a string.
      invariant(integrationId, 'getBitbucketWorkspacesOptions requires an integrationId once the query is enabled')

      return fetchWrapperQuery({
        path: `${buildPath('/api/v1/integrations/:integrationId/workspaces', { integrationId })}?${createPickerSearchParams(search)}`,
        schema: BitbucketWorkspacePaginatedResponseSchema,
      })
    },
    enabled: enabled && !!integrationId,
    ...rest,
  })

/** The first page of a Bitbucket account's workspaces whose slugs match `search`. */
export const useBitbucketWorkspaceSearch = (input: { integrationId: string | undefined; search?: string }) =>
  useQuery(getBitbucketWorkspacesOptions({ ...input, ...PICKER_SEARCH_OPTIONS }))

const getBitbucketRepositoriesOptions = ({
  integrationId,
  workspaceSlug,
  search,
  enabled = true,
  ...rest
}: {
  integrationId?: string
  workspaceSlug?: string
  /** A case-insensitive fragment of the repository name. */
  search?: string
  enabled?: boolean
} & Omit<UseQueryOptions<BitbucketRepositoryPaginatedResponse>, 'queryKey' | 'queryFn'>) =>
  queryOptions({
    queryKey: ['bitbucket-repositories', integrationId, workspaceSlug, search],
    queryFn: () => {
      // `enabled` gates the request but does not narrow the closure, so the invariants are what
      // make the names strings.
      invariant(integrationId, 'getBitbucketRepositoriesOptions requires an integrationId once the query is enabled')
      invariant(workspaceSlug, 'getBitbucketRepositoriesOptions requires a workspaceSlug once the query is enabled')

      return fetchWrapperQuery({
        path: `${buildPath('/api/v1/integrations/:integrationId/workspaces/:workspaceSlug/repositories', { integrationId, workspaceSlug })}?${createPickerSearchParams(search)}`,
        schema: BitbucketRepositoryPaginatedResponseSchema,
      })
    },
    enabled: enabled && !!integrationId && !!workspaceSlug,
    ...rest,
  })

/** The first page of a Bitbucket workspace's repositories whose names match `search`. */
export const useBitbucketRepositorySearch = (input: {
  integrationId: string | undefined
  workspaceSlug: string | undefined
  search?: string
}) => useQuery(getBitbucketRepositoriesOptions({ ...input, ...PICKER_SEARCH_OPTIONS }))

export const getAnalysesQueryKey = (options?: GetAnalysesOptions) => {
  if (!options) return ['analyses']
  const {
    page,
    filters: { repositoryIds, states, tools, scanIds, updatedOnOrBefore, updatedOnOrAfter },
  } = options
  return [
    'analyses',
    page,
    {
      repositoryIds,
      states,
      tools,
      scanIds,
      updatedOnOrBefore,
      updatedOnOrAfter,
    },
  ]
}

type GetAnalysesOptions = {
  page: PageOptions
  filters: {
    repositoryIds?: string[]
    states?: AnalysisStateResponse[]
    tools?: Tool[]
    scanIds?: string[]
    updatedOnOrBefore?: string
    updatedOnOrAfter?: string
  }
  enabled?: boolean
}

const getAnalysesOptions = ({
  page,
  filters: { repositoryIds, states, tools, scanIds, updatedOnOrBefore, updatedOnOrAfter },
  enabled,
}: GetAnalysesOptions) =>
  queryOptions({
    queryKey: getAnalysesQueryKey({
      page,
      filters: { repositoryIds, states, tools, scanIds, updatedOnOrBefore, updatedOnOrAfter },
    }),
    enabled,
    queryFn: () => {
      const queryParams = createPageParams(page.number, page.size)

      if (updatedOnOrBefore) {
        queryParams.append('updated-on-or-before', updatedOnOrBefore)
      }

      if (updatedOnOrAfter) {
        queryParams.append('updated-on-or-after', updatedOnOrAfter)
      }

      for (const state of states ?? []) {
        queryParams.append('states', state)
      }

      for (const repositoryId of repositoryIds ?? []) {
        queryParams.append('repository-ids', repositoryId)
      }

      for (const tool of tools ?? []) {
        queryParams.append('tools', tool)
      }

      if (scanIds) {
        appendQueryParam(queryParams, 'scan-ids', scanIds)
      }

      return fetchWrapperQuery({
        path: `/api/v1/analyses?${queryParams}`,
        schema: AnalysisPaginatedResponseSchema,
      })
    },
  })

export const useGetAnalyses = (options: GetAnalysesOptions) => useQuery(getAnalysesOptions(options))

export const useGetAllAnalysesPages = (options: Omit<GetAnalysesOptions, 'page'>) =>
  useGetAllPages({
    getPageQueryOptions: pageOptions => getAnalysesOptions({ ...options, page: pageOptions }),
    // assuming 1000 analyses per period as upper bound, 100 per page is fine (10 parallel queries)
    pageSize: 100,
  })

export const useGetAnalysesWithRepositories = (options: Parameters<typeof getAnalysesOptions>[0]) =>
  useQueryWithLinks(getAnalysesOptions(options), [
    {
      key: '_embedded.items._embedded.scan._links.repository.href',
      schema: RepositorySchema,
    },
  ])

export const useGetLatestAnalysisForRepository = ({
  repositoryId,
  states,
  tools,
}: {
  repositoryId: string
  states?: AnalysisStateResponse[]
  tools?: Tool[]
}) => {
  const { data, isLoading } = useGetAnalyses({
    page: { number: 0, size: 1 },
    filters: { repositoryIds: [repositoryId], states, tools },
  })
  return useMemo(() => ({ data: data?._embedded?.items?.[0], isLoading: isLoading }), [data, isLoading])
}

const getGetWorkflowsQueryKey = (params?: Parameters<typeof useGetWorkflows>[0]) =>
  ['workflows', params?.repositoryId, params?.pageIndex, params?.pageSize] as const

export const useGetWorkflows = ({
  repositoryId,
  pageIndex = 0,
  pageSize = 100,
}: {
  repositoryId: string
  pageIndex?: number
  pageSize?: number
}) => {
  return useQuery({
    queryKey: getGetWorkflowsQueryKey({ repositoryId, pageIndex, pageSize }),
    queryFn: () => {
      const queryParams = createPageParams(pageIndex, pageSize)
      return fetchWrapperQuery({
        path: `${buildPath('/api/v1/repositories/:repositoryId/workflows/', { repositoryId })}?${queryParams}`,
        schema: WorkflowPaginatedResponseSchema,
      })
    },
  })
}

export const useGetScaleFromHalLink = ({ path, enabled = true }: { path?: string; enabled?: boolean }) =>
  useQuery({
    queryKey: ['scale', path],
    queryFn: () =>
      fetchWrapperQuery({
        path: path!,
        schema: ScaleSchema,
      }),
    enabled: enabled && !!path,
    // A scan's scale is decorative metadata (confidence/severity ramp). If the backend hasn't
    // registered it — e.g. a `pixee_ai` scan whose `pixee-ai-confidence` scale 404s — the findings
    // view must still render, so a missing scale degrades to `undefined` rather than throwing to the
    // root error boundary and blanking the page.
    throwOnError: false,
  })

type GetScalesOptionsInput = {
  page?: PageOptions
  tool?: Tool
} & Omit<UseQueryOptions<ScalePaginatedResponse, ApiError>, 'queryKey' | 'queryFn'>

const getScalesOptions = ({
  page = { number: 0, size: 100 },
  tool,
  enabled = true,
  ...rest
}: GetScalesOptionsInput = {}) =>
  queryOptions({
    queryKey: ['scales', { page, tool }],
    queryFn: () => {
      const params = createPageParams(page.number, page.size)
      if (tool) {
        params.set('tool', tool)
      }
      return fetchWrapperQuery({
        path: `/api/v1/scales?${params}`,
        schema: ScalePaginatedResponseSchema,
      })
    },
    enabled,
    ...rest,
  })

export const useGetScales = (input?: GetScalesOptionsInput) => useQuery(getScalesOptions(input))

export const useGetScaleForTool = (tool: Tool | undefined) =>
  useQuery({
    ...getScalesOptions({ tool, enabled: !!tool, throwOnError: false }),
    select: page => page._embedded.items[0],
  })

const SCAN_FINDINGS_QUERY_KEY_ROOT = 'scan-findings'
const SCAN_FINDING_QUERY_KEY_ROOT = 'scan-finding'
const SCAN_FINDING_RESULTS_QUERY_KEY_ROOT = 'scan-finding-results'

/**
 * The prefix every scan-findings query key starts with, whatever its pagination and filters.
 *
 * Invalidate with this rather than with a key built for particular filters: React Query matches a
 * key partially, so the prefix reaches every page and every filter combination of a scan's
 * findings, and nothing else. `scanId` is required because a prefix ending in `undefined` matches
 * only queries that themselves have no scan — silently nothing, which is what this exists to avoid.
 */
export const getScanFindingsQueryKeyPrefix = (scanId: string) => [SCAN_FINDINGS_QUERY_KEY_ROOT, scanId] as const

/** The prefix every scan-finding query key starts with, whatever it embeds. @see getScanFindingsQueryKeyPrefix */
export const getScanFindingQueryKeyPrefix = (scanId: string) => [SCAN_FINDING_QUERY_KEY_ROOT, scanId] as const

/** The prefix every finding-results query key starts with. @see getScanFindingsQueryKeyPrefix */
export const getScanFindingResultsQueryKeyPrefix = (scanId: string) =>
  [SCAN_FINDING_RESULTS_QUERY_KEY_ROOT, scanId] as const

export type GetScanFindingsQueryOptionsInput = ScanFindingsFilters & {
  scanId?: string
  pagination?: { pageIndex: number; pageSize: number }
  enabled?: boolean
  search?: string
  embed?: FindingEmbedParameter[]
} & Omit<UseQueryOptions<FindingAggregateResponse>, 'queryKey' | 'queryFn'>

const getScanFindingsQueryOptions = ({
  scanId,
  pagination,
  enabled = true,
  analyzed,
  fixStatuses,
  triageStatuses,
  triageSuggestedStatuses,
  patchStatuses,
  includeSeverityLabels,
  includeSuggestedSeverityLabels,
  minimumSeverityScore,
  maximumSeverityScore,
  minimumSuggestedSeverityScore,
  maximumSuggestedSeverityScore,
  search,
  sort,
  order,
  scaStatus,
  fixConfidences,
  scaClassifications,
  severityUpdates,
  embed,
  ...rest
}: GetScanFindingsQueryOptionsInput = {}) => {
  // One key shape: every scan-findings query carries pagination and every filter, so
  // `getScanFindingsQueryKeyPrefix` is the only thing callers should invalidate with. Spelled out
  // rather than spread from that helper so `@tanstack/query/exhaustive-deps` can see `scanId` and
  // the filters here.
  const queryKey = [
    SCAN_FINDINGS_QUERY_KEY_ROOT,
    scanId,
    pagination,
    analyzed,
    fixStatuses,
    triageStatuses,
    triageSuggestedStatuses,
    patchStatuses,
    includeSeverityLabels,
    includeSuggestedSeverityLabels,
    minimumSeverityScore,
    maximumSeverityScore,
    minimumSuggestedSeverityScore,
    maximumSuggestedSeverityScore,
    search,
    sort,
    order,
    scaStatus,
    fixConfidences,
    scaClassifications,
    severityUpdates,
    embed,
  ] as const

  return queryOptions({
    queryKey,
    queryFn: () => {
      const effectivePagination = pagination ?? { pageIndex: 0, pageSize: 10 }
      const filters = {
        analyzed,
        fixStatuses,
        triageStatuses,
        triageSuggestedStatuses,
        patchStatuses,
        includeSeverityLabels,
        includeSuggestedSeverityLabels,
        scaStatus,
        fixConfidences,
        scaClassifications,
        severityUpdates,
      }
      if (hasEmptyFilterSelection(filters)) {
        return Promise.resolve(buildEmptyFindingAggregateResponse(effectivePagination))
      }

      const params = buildScanFindingsQueryParams({
        pageIndex: effectivePagination.pageIndex,
        pageSize: effectivePagination.pageSize,
        analyzed,
        fixStatuses,
        triageStatuses,
        triageSuggestedStatuses,
        patchStatuses,
        includeSeverityLabels,
        includeSuggestedSeverityLabels,
        minimumSeverityScore,
        maximumSeverityScore,
        minimumSuggestedSeverityScore,
        maximumSuggestedSeverityScore,
        q: search,
        sort,
        order,
        scaStatus,
        fixConfidences,
        scaClassifications,
        severityUpdates,
        embed,
      })

      return fetchWrapperQuery({
        path: `${buildPath('/api/v1/scans/:scanId/findings', { scanId: scanId ?? '' })}?${params.toString()}`,
        schema: FindingAggregateResponseSchema,
      })
    },
    enabled: enabled && !!scanId,
    ...rest,
  })
}

export const useGetScanFindings = (options: GetScanFindingsQueryOptionsInput) =>
  useQuery(getScanFindingsQueryOptions(options))

/** Every finding of a scan, unfiltered, fetched a page at a time. */
export const useGetAllScanFindingPages = ({ scanId, enabled }: { scanId?: string; enabled?: boolean }) =>
  useGetAllPages({
    getPageQueryOptions: page =>
      getScanFindingsQueryOptions({ scanId, enabled, pagination: { pageIndex: page.number, pageSize: page.size } }),
    pageSize: 100,
  })

type GetScanFindingOptionsInput = {
  scanId?: string
  findingId?: string
  embed?: 'results' | 'representative-results'
} & Omit<UseQueryOptions<FindingResponseWithoutEmbed>, 'queryKey' | 'queryFn'>

const getScanFindingOptions = ({ scanId, findingId, embed, enabled = true, ...rest }: GetScanFindingOptionsInput) =>
  queryOptions({
    queryKey: [SCAN_FINDING_QUERY_KEY_ROOT, scanId, findingId, embed] as const,
    queryFn: () => {
      const queryParams = new URLSearchParams()
      if (embed) queryParams.append('embed', embed)
      const queryString = queryParams.toString()
      const path = `${buildPath('/api/v1/scans/:scanId/findings/:findingId', { scanId: scanId ?? '', findingId: findingId ?? '' })}${queryString ? `?${queryString}` : ''}`
      return fetchWrapperQuery({ path, schema: FindingResponseWithoutEmbedSchema })
    },
    enabled: enabled && !!scanId && !!findingId,
    ...rest,
  })

export const useGetScanFinding = (options: GetScanFindingOptionsInput) => useQuery(getScanFindingOptions(options))

export const useGetEachScanFinding = ({
  findingIds,
  ...rest
}: Omit<GetScanFindingOptionsInput, 'findingId'> & {
  findingIds: NonNullable<GetScanFindingOptionsInput['findingId']>[]
}) =>
  useQueries({
    queries: findingIds.map(findingId => getScanFindingOptions({ findingId, ...rest })),
  })

type GetScanFindingResultsQueryOptionsInput = {
  scanId?: string
  findingId?: string
  representative?: boolean
} & Omit<UseQueryOptions<FindingResultPaginatedResponse>, 'queryKey' | 'queryFn'>

const getScanFindingResultsQueryOptions = ({
  scanId,
  findingId,
  representative,
  enabled = true,
  ...rest
}: GetScanFindingResultsQueryOptionsInput) =>
  queryOptions({
    queryKey: [SCAN_FINDING_RESULTS_QUERY_KEY_ROOT, scanId, findingId, representative] as const,
    queryFn: () => {
      const params = new URLSearchParams()

      if (representative !== undefined) {
        appendQueryParam(params, 'representative', String(representative))
      }

      return fetchWrapperQuery({
        path: `${buildPath('/api/v1/scans/:scanId/findings/:findingId/results', { scanId: scanId ?? '', findingId: findingId ?? '' })}${params.toString() ? `?${params.toString()}` : ''}`,
        schema: FindingResultPaginatedResponseSchema,
      })
    },
    enabled: enabled && !!scanId && !!findingId,
    ...rest,
  })

export const useGetScanFindingsResults = ({
  scanId,
  findingIds,
  ...rest
}: Omit<GetScanFindingResultsQueryOptionsInput, 'findingId'> & {
  findingIds: NonNullable<GetScanFindingResultsQueryOptionsInput['findingId']>[]
}) => {
  return useQueries({
    queries: findingIds.map(findingId => getScanFindingResultsQueryOptions({ scanId, findingId, ...rest })),
  })
}

export const useGetScanFindingResults = ({ scanId, findingId, ...rest }: GetScanFindingResultsQueryOptionsInput) => {
  const queryClient = useQueryClient()
  const query = useQuery(getScanFindingResultsQueryOptions({ scanId, findingId, ...rest }))

  const prevOutcomesRef = useRef<Map<string, boolean>>(new Map())
  useEffect(() => {
    const currentItems = query.data?._embedded?.items
    if (!currentItems || !scanId) {
      return
    }

    const currentOutcomes = new Map(currentItems.map(item => [item.id, Boolean(item.outcome)]))

    const hasNewCompletion = [...currentOutcomes].some(
      ([id, hasOutcome]) => hasOutcome && prevOutcomesRef.current.get(id) === false
    )

    if (hasNewCompletion) {
      queryClient.invalidateQueries({ queryKey: getScanFindingsQueryKeyPrefix(scanId) })
    }

    prevOutcomesRef.current = currentOutcomes
  }, [query.data, queryClient, scanId])

  return query
}

const getGitLabProjectsOptions = ({
  integrationId,
  search,
  enabled = true,
  ...rest
}: {
  integrationId?: string
  /** The term GitLab's project search reads; the picker sends the `name_with_namespace` it shows. */
  search?: string
  enabled?: boolean
} & Omit<UseQueryOptions<GitLabProjectPaginatedResponse>, 'queryKey' | 'queryFn'>) =>
  queryOptions({
    queryKey: ['gitlab-projects', integrationId, search],
    queryFn: () => {
      // `enabled` gates the request but does not narrow the closure, so the invariant is what
      // makes the id a string.
      invariant(integrationId, 'getGitLabProjectsOptions requires an integrationId once the query is enabled')

      return fetchWrapperQuery({
        path: `${buildPath('/api/v1/integrations/:integrationId/projects', { integrationId })}?${createPickerSearchParams(search)}`,
        schema: GitLabProjectPaginatedResponseSchema,
      })
    },
    enabled: enabled && !!integrationId,
    ...rest,
  })

/** The first page of a GitLab connection's projects whose names or paths match `search`. */
export const useGitLabProjectSearch = (input: { integrationId: string | undefined; search?: string }) =>
  useQuery(getGitLabProjectsOptions({ ...input, ...PICKER_SEARCH_OPTIONS }))

const getSonarProjectsOptions = ({
  integrationId,
  search,
  enabled = true,
  ...rest
}: {
  integrationId?: string
  search?: string
  enabled?: boolean
} & Omit<UseQueryOptions<SonarProjectPaginatedResponse>, 'queryKey' | 'queryFn'> = {}) =>
  queryOptions({
    queryKey: ['sonar-projects', { integrationId, search }],
    queryFn: () => {
      invariant(integrationId, 'getSonarProjectsOptions requires an integrationId once the query is enabled')

      return fetchWrapperQuery({
        path: `${buildPath('/api/v1/integrations/:integrationId/sonar-projects', { integrationId })}?${createPickerSearchParams(search)}`,
        schema: SonarProjectPaginatedResponseSchema,
      })
    },
    enabled: enabled && integrationId !== undefined,
    ...rest,
  })

/**
 * A rejected token (424) and a SonarQube Cloud instance with no organization (409) are documented
 * answers the picker shows inline: neither recovers on a retry, and neither is a fault to report.
 */
const isRefusedSonarProjectSearch = (error: unknown) =>
  isRejectedCredentialsError(error) || (error instanceof ApiError && error.status === 409)

/** The first page of a Sonar instance's projects for the project picker, narrowed by `search`. */
export const useSonarProjectSearch = (input: { integrationId: string | undefined; search?: string }) =>
  useQuery(
    getSonarProjectsOptions({
      ...input,
      placeholderData: keepPreviousData,
      ...inlineAnswerQueryPolicy(isRefusedSonarProjectSearch),
    })
  )

type RepositoryIntegrationsOptionsInput = {
  repositoryId: string
  page?: PageOptions
}

const getRepositoryIntegrationsOptions = ({
  repositoryId,
  page = { number: 0, size: 10 },
}: RepositoryIntegrationsOptionsInput) =>
  queryOptions({
    queryKey: getRepositoryIntegrationsQueryKey({ repositoryId, page }),
    queryFn: () => {
      const queryParams = createPageParams(page.number, page.size)

      return fetchWrapperQuery({
        path: `${buildPath('/api/v1/repositories/:repositoryId/integrations', { repositoryId })}?${queryParams}`,
        schema: RepositoryIntegrationPaginatedResponseSchema,
      })
    },
  })

const getRepositoryIntegrationsQueryKey = ({ repositoryId, page }: RepositoryIntegrationsOptionsInput) =>
  page ? ['repository-integrations', repositoryId, page] : ['repository-integrations', repositoryId]

export const useGetRepositoryIntegrations = (input: RepositoryIntegrationsOptionsInput) =>
  useQuery(getRepositoryIntegrationsOptions(input))

export const useSuspenseGetRepositoryIntegrations = (input: RepositoryIntegrationsOptionsInput) =>
  useSuspenseQuery(getRepositoryIntegrationsOptions(input))

export const REPOSITORY_INTEGRATIONS_PAGE_SIZE = 100

export const useGetAllRepositoryIntegrationsPages = (input: Omit<RepositoryIntegrationsOptionsInput, 'page'>) =>
  useGetAllPages({
    getPageQueryOptions: pageOptions => getRepositoryIntegrationsOptions({ ...input, page: pageOptions }),
    pageSize: REPOSITORY_INTEGRATIONS_PAGE_SIZE,
  })

const REFRESH_REQUEST_LIST_LIMIT = 50
const REFRESH_REQUESTS_POLL_INTERVAL_MS = 15_000
const REFRESH_REQUEST_TERMINAL_STATES = new Set<RefreshRequestStateResponse>([
  'no-revision',
  'completed',
  'failed',
  'superseded',
])
const isNonTerminalRefreshRequestState = (state: RefreshRequestStateResponse) =>
  !REFRESH_REQUEST_TERMINAL_STATES.has(state)

const getRepositoryThreatModelRefreshRequestsQueryKey = (repositoryId: string) =>
  ['repository-threat-model-refresh-requests', repositoryId] as const

export const useGetRepositoryThreatModelRefreshRequests = ({ repositoryId }: { repositoryId: string }) =>
  useQuery({
    queryKey: getRepositoryThreatModelRefreshRequestsQueryKey(repositoryId),
    queryFn: () =>
      fetchWrapperQuery({
        path: `${buildPath('/api/v1/repositories/:repositoryId/threat-model/refresh-requests', {
          repositoryId,
        })}?limit=${REFRESH_REQUEST_LIST_LIMIT}`,
        schema: z.union([RefreshRequestsResponseSchema, z.null()]),
        successStatusCodes: [200, 404],
        // A 404 means the feature flag is off; surface as `null` so the consumer's `data?.items ?? []`
        // renders the empty state and no fabricated envelope is needed.
        deserializeFn: async response => {
          if (response.status === 404) {
            return null
          }
          return (await response.json()) as unknown
        },
      }),
    enabled: !!repositoryId,
    refetchInterval: query =>
      query.state.data?.items.some(item => isNonTerminalRefreshRequestState(item.state))
        ? REFRESH_REQUESTS_POLL_INTERVAL_MS
        : false,
  })

export const invalidateGetRepositoryThreatModelRefreshRequests = (
  client: QueryClient,
  { repositoryId }: { repositoryId: string }
) => client.invalidateQueries({ queryKey: getRepositoryThreatModelRefreshRequestsQueryKey(repositoryId) })

export const useGetRepositoryThreatModelRefreshRequest = ({
  repositoryId,
  requestId,
}: {
  repositoryId: string
  requestId: string
}) =>
  useQuery({
    queryKey: ['repository-threat-model-refresh-request', repositoryId, requestId] as const,
    queryFn: () =>
      fetchWrapperQuery({
        path: buildPath('/api/v1/repositories/:repositoryId/threat-model/refresh-requests/:requestId', {
          repositoryId,
          requestId,
        }),
        schema: z.union([RefreshRequestResponseSchema, z.null()]),
        successStatusCodes: [200, 404],
        deserializeFn: async response => {
          if (response.status === 404) {
            return null
          }
          return (await response.json()) as unknown
        },
      }),
    // The single-item GET performs a live dereference against analysis-service for REGENERATING
    // rows; keep polling while the row is non-terminal so the drawer sees fresh document status.
    refetchInterval: query => {
      const state = query.state.data?.state
      return state && isNonTerminalRefreshRequestState(state) ? REFRESH_REQUESTS_POLL_INTERVAL_MS : false
    },
  })

// #endregion queries

// #region: mutations

export type ExportScanFindingsCsvParams = ScanFindingsQueryParams & { scanId: string; filename?: string }

type FetchScanFindingsCsvParams = Omit<ExportScanFindingsCsvParams, 'filename'>

/**
 * Fetches scan findings as CSV content from the API.
 */
async function fetchScanFindingsCsv({ scanId, ...filterParams }: FetchScanFindingsCsvParams): Promise<string> {
  // Nothing can match, and the request would read the empty list as "everything". An empty file is
  // the export of an empty view. @see hasEmptyFilterSelection
  if (hasEmptyFilterSelection(filterParams)) {
    return ''
  }

  const params = buildScanFindingsQueryParams(filterParams)
  const queryString = params.toString()
  const url = `${buildPath('/api/v1/scans/:scanId/findings', { scanId })}${queryString ? `?${queryString}` : ''}`

  let response: Response
  try {
    response = await fetch(url, {
      headers: {
        Accept: 'text/csv',
      },
    })
  } catch (error) {
    throw new Error(`Network error while exporting CSV: ${error instanceof Error ? error.message : 'Unknown error'}`, {
      cause: error,
    })
  }

  if (!response.ok) {
    throw new Error(`Failed to export CSV: ${response.status} ${response.statusText}`)
  }

  return response.text()
}

/**
 * Triggers a browser download for CSV content.
 *
 * Why Blob + DOM manipulation?
 * - Native anchor downloads (`<a href="..." download>`) don't support custom headers
 * - Blob API lets us create a downloadable file from string content
 * - Programmatic anchor click triggers the browser's native download behavior
 */
function triggerCsvDownload(content: string, filename: string): void {
  let downloadUrl: string | undefined
  let anchor: HTMLAnchorElement | undefined

  try {
    const blob = new Blob([content], { type: 'text/csv' })
    downloadUrl = URL.createObjectURL(blob)

    anchor = document.createElement('a')
    anchor.href = downloadUrl
    anchor.download = filename

    document.body.appendChild(anchor)
    anchor.click()
  } finally {
    try {
      if (anchor && document.body.contains(anchor)) {
        document.body.removeChild(anchor)
      }
    } catch {
      // Ignore errors when removing anchor
    }
    try {
      if (downloadUrl) {
        URL.revokeObjectURL(downloadUrl)
      }
    } catch {
      // Ignore errors when revoking URL
    }
  }
}

/**
 * Mutation hook for exporting scan findings as CSV.
 * Composes fetchScanFindingsCsv (data fetching) and triggerCsvDownload (browser side effect).
 */
export const useExportScanFindingsCsv = <TContext = unknown>({
  onMutate,
  onSuccess,
  onError,
  onSettled,
}: {
  onMutate?: OnMutate<ExportScanFindingsCsvParams, TContext>
  onSuccess?: OnSuccess<void, ExportScanFindingsCsvParams, TContext>
  onError?: OnError<Error, ExportScanFindingsCsvParams, TContext>
  onSettled?: OnSettled<void, ExportScanFindingsCsvParams, TContext>
} = {}) => {
  return useMutation({
    mutationFn: async ({ scanId, filename = 'findings.csv', ...filterParams }: ExportScanFindingsCsvParams) => {
      const csvContent = await fetchScanFindingsCsv({ scanId, ...filterParams })
      triggerCsvDownload(csvContent, filename)
    },
    onMutate,
    onSuccess,
    onError,
    onSettled,
  })
}

export const createScanFormData = (body: CreateScanRequestFormData) => {
  if (!body || !body.metadata) {
    throw new Error('Metadata is required')
  }

  const formData = new FormData()

  formData.append(
    'metadata',
    new Blob(
      [
        JSON.stringify({
          ...body.metadata,
          sha: body.metadata.sha === '' ? undefined : body.metadata.sha,
          branch: body.metadata.branch === '' ? undefined : body.metadata.branch,
        }),
      ],
      {
        type: 'application/json',
      }
    )
  )

  if (body.files) {
    Object.entries(body.files).forEach(([_, file]) => {
      formData.append('files', file)
    })
  }

  return formData
}

export const usePostUploadScan = ({
  onSuccess,
  onError,
  retry,
}: {
  onSuccess?: OnSuccess<ScanResponse, { repositoryId: string; body: CreateScanRequestFormData }>
  onError?: OnError<unknown, { repositoryId: string; body: CreateScanRequestFormData }>
  retry?: number
} = {}) => {
  const _onSuccess = useCallback<Exclude<typeof onSuccess, undefined>>(
    (data, variables, onMutateResult, context) => {
      const scanId = data.id

      context.client.invalidateQueries({ queryKey: getScanQueryKey({ id: scanId }) })
      context.client.invalidateQueries({ queryKey: getScanFindingsQueryKeyPrefix(scanId) })

      onSuccess?.(data, variables, onMutateResult, context)
    },
    [onSuccess]
  )

  return useMutation({
    mutationFn: async ({ repositoryId, body }) =>
      fetchWrapperMutation({
        path: buildPath('/api/v1/repositories/:repositoryId/scans', { repositoryId }),
        options: {
          method: 'POST',
          body: createScanFormData(body),
          // Override the wrapper default content type by setting the header
          // Allow the browser to handle the content type
          headers: {
            'X-Requested-With': 'JavaScript',
          },
        },
        schema: ScanResponseSchema,
        successStatusCodes: [201],
        deserializeFn: (response: Response) => response.json(),
      }),
    onSuccess: _onSuccess,
    onError,
    retry,
  })
}

const createSbomFormData = (body: CreateSbomRequestFormData) => {
  const formData = new FormData()
  formData.append(
    'metadata',
    new Blob([JSON.stringify(body.metadata)], {
      type: 'application/json',
    })
  )
  if (body.file) {
    formData.append('file', body.file)
  }
  return formData
}

export const usePostUploadSbom = (
  options: Omit<
    UseMutationOptions<SbomResponse, DefaultError, { repositoryId: string; body: CreateSbomRequestFormData }>,
    'mutationFn' | 'mutationKey'
  > = {}
) =>
  useMutation({
    mutationFn: async ({ repositoryId, body }) =>
      fetchWrapperMutation({
        path: buildPath('/api/v1/repositories/:repositoryId/sboms', { repositoryId }),
        options: {
          method: 'POST',
          body: createSbomFormData(body),
          // Override the wrapper default content type by setting the header
          // Allow the browser to handle the content type
          headers: {
            'X-Requested-With': 'JavaScript',
          },
        },
        schema: SbomResponseSchema,
        successStatusCodes: [201],
        deserializeFn: (response: Response) => response.json(),
      }),
    ...options,
  })

export const usePostAnalyzeScan = (
  options: Omit<
    UseMutationOptions<AnalysisResponse, DefaultError, { scanId: string; findingIds?: string[] }>,
    'mutationFn' | 'mutationKey'
  > = {}
) => {
  const { onSuccess, ...restOptions } = options

  const _onSuccess = useCallback<Exclude<typeof onSuccess, undefined>>(
    (data, variables, onMutateResult, context) => {
      const scanId = data._embedded?.scan?.id
      if (scanId) {
        context.client.invalidateQueries({ queryKey: getScanQueryKey({ id: scanId }) })
        context.client.invalidateQueries({ queryKey: getScanFindingsQueryKeyPrefix(scanId) })
      }

      invalidateGetScans(context.client)

      context.client.invalidateQueries({
        queryKey: getAnalysesQueryKey(),
        refetchType: 'active',
      })

      onSuccess?.(data, variables, onMutateResult, context)
    },
    [onSuccess]
  )

  return useMutation({
    mutationFn: async ({ scanId, findingIds }: { scanId: string; findingIds?: string[] }) =>
      fetchWrapperMutation({
        path: buildPath('/api/v1/scans/:scanId/analyses', { scanId }),
        options: {
          method: 'POST',
          headers: {
            'X-Requested-With': 'JavaScript',
            ...(findingIds && { 'Content-Type': 'application/json' }),
          },
          ...(findingIds && { body: JSON.stringify({ finding_ids: findingIds }) }),
        },
        schema: AnalysisResponseSchema,
        successStatusCodes: [201],
        deserializeFn: (response: Response) => response.json(),
      }),
    onSuccess: _onSuccess,
    ...restOptions,
  })
}

export const useDeleteAnalysis = ({
  onSuccess,
  onError,
  retry,
}: {
  onSuccess?: OnSuccess<unknown, { analysisId: string }>
  onError?: OnError<unknown, { analysisId: string }>
  retry?: number
} = {}) =>
  useMutation({
    mutationFn: async ({ analysisId }) =>
      fetchWrapperMutation({
        path: buildPath('/api/v1/analyses/:analysisId', { analysisId }),
        options: { method: 'DELETE' },
        successStatusCodes: [204, 404],
        schema: z.unknown(),
      }),
    onSuccess,
    onError,
    retry,
  })

export const usePostAnalysisCancellation = (
  options: Omit<
    UseMutationOptions<AnalysisCancellationResponse, DefaultError, { scanId: string }>,
    'mutationFn' | 'mutationKey'
  > = {}
) => {
  const { onSuccess, ...restOptions } = options

  const _onSuccess = useCallback<Exclude<typeof onSuccess, undefined>>(
    (data, variables, onMutateResult, context) => {
      context.client.invalidateQueries({ queryKey: getScanQueryKey({ id: variables.scanId }) })
      context.client.invalidateQueries({ queryKey: getScanFindingsQueryKeyPrefix(variables.scanId) })
      context.client.invalidateQueries({
        queryKey: getAnalysesQueryKey(),
        refetchType: 'active',
      })

      onSuccess?.(data, variables, onMutateResult, context)
    },
    [onSuccess]
  )

  return useMutation({
    mutationFn: async ({ scanId }: { scanId: string }) =>
      fetchWrapperMutation({
        path: buildPath('/api/v1/scans/:scanId/analysis-cancellations', { scanId }),
        options: {
          method: 'POST',
          headers: {
            'X-Requested-With': 'JavaScript',
          },
        },
        schema: AnalysisCancellationResponseSchema,
        successStatusCodes: [201],
        deserializeFn: (response: Response) => response.json(),
      }),
    onSuccess: _onSuccess,
    ...restOptions,
  })
}

export const usePostAddRepository = ({
  onSuccess,
  onError,
}: { onSuccess?: OnSuccess<unknown, AddRepositoryRequest>; onError?: OnError<unknown, AddRepositoryRequest> } = {}) => {
  const _onSuccess = useCallback<Exclude<typeof onSuccess, undefined>>(
    (data, variables, onMutateResult, context) => {
      context.client.invalidateQueries({ queryKey: getRepositoriesQueryKey() })
      onSuccess?.(data, variables, onMutateResult, context)
    },
    [onSuccess]
  )

  return useMutation({
    mutationFn: async params =>
      fetchWrapperMutation({
        path: '/api/v1/repositories',
        options: {
          method: 'POST',
          body: JSON.stringify(AddRepositoryRequestSchema.parse(params)),
        },
        successStatusCodes: [201],
        schema: z.unknown(),
      }),
    onSuccess: _onSuccess,
    onError,
  })
}

type PostScanPatchVariables = {
  createScanPatchRequest: PatchesCreatePatchRequest
}

type GetPostScanPatchMutationOptionsArgs = {
  scanId?: string
} & Omit<UseMutationOptions<unknown, DefaultError, PostScanPatchVariables>, 'mutationKey' | 'mutationFn'>

const getPostScanPatchMutationOptions = ({ scanId, ...rest }: GetPostScanPatchMutationOptionsArgs) =>
  mutationOptions({
    mutationKey: ['post-scan-patch', scanId] as const,
    mutationFn: async ({ createScanPatchRequest }: PostScanPatchVariables) =>
      fetchWrapperMutation({
        path: `/api/v1/patches`,
        options: {
          method: 'POST',
          body: JSON.stringify(PatchesCreatePatchRequestSchema.parse(createScanPatchRequest)),
        },
        successStatusCodes: [201],
        schema: z.unknown(),
      }),
    ...rest,
  })

export const usePostScanPatch = ({ scanId, onSuccess, ...rest }: GetPostScanPatchMutationOptionsArgs) => {
  const _onSuccess = useCallback<NonNullable<typeof onSuccess>>(
    (data, variables, onMutateResult, context) => {
      if (scanId) {
        context.client.invalidateQueries({ queryKey: getScanFindingsQueryKeyPrefix(scanId) })
        context.client.invalidateQueries({ queryKey: getScanFindingQueryKeyPrefix(scanId) })
        context.client.invalidateQueries({ queryKey: getScanFindingResultsQueryKeyPrefix(scanId) })
      }
      onSuccess?.(data, variables, onMutateResult, context)
    },
    [onSuccess, scanId]
  )

  return useMutation(getPostScanPatchMutationOptions({ scanId, onSuccess: _onSuccess, ...rest }))
}

export const useIsPostScanPatchMutating = (scanId?: string) =>
  useIsMutating({ mutationKey: getPostScanPatchMutationOptions({ scanId }).mutationKey }) > 0

export const usePostCreateWorkflow = ({
  onSuccess,
  onError,
}: {
  onSuccess?: OnSuccess<unknown, { repositoryId: string; workflow: CreateWorkflowRequest }>
  onError?: OnError<unknown, { repositoryId: string; workflow: CreateWorkflowRequest }>
} = {}) => {
  const _onSuccess = useCallback<Exclude<typeof onSuccess, undefined>>(
    (data, variables, onMutateResult, context) => {
      context.client.invalidateQueries({ queryKey: getGetWorkflowsQueryKey({ repositoryId: variables.repositoryId }) })
      onSuccess?.(data, variables, onMutateResult, context)
    },
    [onSuccess]
  )

  return useMutation({
    mutationFn: async ({ repositoryId, workflow }) =>
      fetchWrapperMutation({
        path: buildPath('/api/v1/repositories/:repositoryId/workflows', { repositoryId }),
        options: {
          method: 'POST',
          body: JSON.stringify(CreateWorkflowRequestSchema.parse(workflow)),
        },
        successStatusCodes: [201],
        schema: z.unknown(),
      }),
    onSuccess: _onSuccess,
    onError,
  })
}

const getPutUpdateWorkflowMutationKey = () => ['updateWorkflow'] as const

export const useIsPutUpdateWorkflowMutating = () =>
  useIsMutating({ mutationKey: getPutUpdateWorkflowMutationKey() }) > 0

export const usePutUpdateWorkflow = ({
  onSuccess,
  onError,
  ...rest
}: Omit<
  UseMutationOptions<unknown, DefaultError, { workflowId: string; update: UpdateWorkflowRequest }>,
  'mutationKey' | 'mutationFn'
> = {}) => {
  const _onSuccess = useCallback<NonNullable<typeof onSuccess>>(
    (data, variables, onMutateResult, context) => {
      context.client.invalidateQueries({ queryKey: getGetWorkflowsQueryKey() })
      onSuccess?.(data, variables, onMutateResult, context)
    },
    [onSuccess]
  )

  return useMutation({
    mutationKey: getPutUpdateWorkflowMutationKey(),
    mutationFn: async ({ workflowId, update }) =>
      fetchWrapperMutation({
        path: buildPath('/api/v1/workflows/:workflowId', { workflowId }),
        options: {
          method: 'PUT',
          body: JSON.stringify(UpdateWorkflowRequestSchema.parse(update)),
        },
        successStatusCodes: [200],
        // The response isn't consumed — success is handled via query invalidation on onSuccess.
        // Matches usePostCreateWorkflow. A strict schema here would reject backend responses with
        // shape drift and surface a failure toast despite the 200.
        schema: z.unknown(),
      }),
    onSuccess: _onSuccess,
    onError,
    ...rest,
  })
}

export const useDeleteWorkflow = ({
  onSuccess,
  onError,
}: {
  onSuccess?: OnSuccess<unknown, { workflowId: string }>
  onError?: OnError<unknown, { workflowId: string }>
} = {}) => {
  const _onSuccess = useCallback<Exclude<typeof onSuccess, undefined>>(
    (data, variables, onMutateResult, context) => {
      context.client.invalidateQueries({ queryKey: getGetWorkflowsQueryKey() })
      onSuccess?.(data, variables, onMutateResult, context)
    },
    [onSuccess]
  )

  return useMutation({
    mutationFn: async ({ workflowId }) =>
      fetchWrapperMutation({
        path: buildPath('/api/v1/workflows/:workflowId', { workflowId }),
        options: {
          method: 'DELETE',
          headers: {
            'X-Requested-With': 'JavaScript',
          },
        },
        successStatusCodes: [204],
        schema: z.unknown(),
      }),
    onSuccess: _onSuccess,
    onError,
  })
}

export const useDeleteRepository = ({
  onSuccess,
  onError,
}: {
  onSuccess?: UseMutationOptions<unknown, DefaultError, { repositoryId: string }>['onSuccess']
  onError?: UseMutationOptions<unknown, DefaultError, { repositoryId: string }>['onError']
}) => {
  const _onSuccess = useCallback(
    ((data, variables, onMutateResult, context) => {
      context.client.invalidateQueries({ queryKey: getRepositoriesQueryKey() })
      context.client.invalidateQueries({ queryKey: getRepositoryQueryKey(variables.repositoryId) })
      // Deleting a grouped repository takes it out of its project, so project counts and names change too.
      context.client.invalidateQueries({ queryKey: [PROJECTS_QUERY_KEY] })
      onSuccess?.(data, variables, onMutateResult, context)
    }) satisfies Exclude<typeof onSuccess, undefined>,
    [onSuccess]
  )

  return useMutation({
    mutationFn: async ({ repositoryId }: { repositoryId: string }) =>
      fetchWrapperMutation({
        path: buildPath('/api/v1/repositories/:repositoryId', { repositoryId }),
        options: {
          method: 'DELETE',
          headers: {
            'X-Requested-With': 'JavaScript',
          },
        },
        successStatusCodes: [204],
        schema: z.unknown(),
      }),
    onSuccess: _onSuccess,
    onError,
    retry: 0,
  })
}

export const useDeleteScan = ({
  onSuccess,
  onError,
  ...rest
}: Omit<UseMutationOptions<void, DefaultError, { scanId: string }>, 'mutationKey' | 'mutationFn'>) => {
  const _onSuccess = useCallback(
    ((data, variables, onMutateResult, context) => {
      context.client.setQueriesData<ScanAggregateResponse>({ queryKey: ['scans'] }, oldData => {
        if (!oldData?._embedded?.items) {
          return oldData
        }
        return {
          ...oldData,
          total: oldData.total - 1,
          _embedded: {
            items: oldData._embedded.items.filter(scan => scan.id !== variables.scanId),
          },
        }
      })
      context.client.invalidateQueries({ queryKey: ['scans'] })
      onSuccess?.(data, variables, onMutateResult, context)
    }) satisfies NonNullable<typeof onSuccess>,
    [onSuccess]
  )

  return useMutation({
    mutationFn: async ({ scanId }) => {
      await fetchWrapperMutation({
        path: buildPath('/api/v1/scans/:scanId', { scanId }),
        options: {
          method: 'DELETE',
          headers: {
            'X-Requested-With': 'JavaScript',
          },
        },
        successStatusCodes: [204],
        schema: z.unknown(),
      })
    },
    onSuccess: _onSuccess,
    onError,
    retry: 0,
    ...rest,
  })
}

export const usePostRepositoryIntegration = ({
  onSuccess,
  onError,
}: Pick<
  UseMutationOptions<unknown, DefaultError, { repositoryId: string; body: CreateRepositoryIntegrationRequest }>,
  'onSuccess' | 'onError'
> = {}) => {
  const _onSuccess = useCallback(
    ((data, variables, onMutateResult, context) => {
      // The repositories pages too, not just this repository's integrations: a list filtered by
      // `integration-id` still shows a repository that was just reassigned away from that instance.
      context.client.invalidateQueries({ queryKey: getRepositoriesQueryKey() })
      context.client.invalidateQueries({
        queryKey: getRepositoryIntegrationsQueryKey({ repositoryId: variables.repositoryId }),
      })
      onSuccess?.(data, variables, onMutateResult, context)
    }) satisfies Exclude<typeof onSuccess, undefined>,
    [onSuccess]
  )

  return useMutation({
    mutationFn: async ({ repositoryId, body }: { repositoryId: string; body: CreateRepositoryIntegrationRequest }) =>
      fetchWrapperMutation({
        path: buildPath('/api/v1/repositories/:repositoryId/integrations', { repositoryId }),
        options: {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'JavaScript',
          },
          body: JSON.stringify(CreateRepositoryIntegrationRequestSchema.parse(body)),
        },
        successStatusCodes: [201],
        schema: z.unknown(),
      }),
    onSuccess: _onSuccess,
    onError,
  })
}

/**
 * A create/update body for a tool whose Create/Update variant has not shipped in the spec yet. The
 * Available catalog gates those tools out of setup via `getIsIntegrationTypeSupportedByApi`, the same
 * predicate that decides below whether a body is parsed — so a variant shipping upstream opens the
 * catalog and turns on schema parsing together, and neither can be enabled without the other.
 *
 * The extra fields are `string | undefined` rather than `unknown`: every one is a credential or a
 * URI collected from a text input. `unknown` made this arm match any object at all, which erased the
 * typed arm of the union it sits in and turned a compile error into a runtime `parse` throw.
 */
export type AheadOfSpecIntegrationBody = {
  type: Exclude<IntegrationType, WritableIntegrationType>
  name: string
  // `null` erases a stored optional value, which is what the update requests these bodies stand in
  // for document — `UpdateSonarIntegrationRequest.organization` says "Send null to clear it".
  // `undefined` omits the key, and a PUT keeps what is stored for an omitted one.
} & Record<string, string | null | undefined>

export type PostIntegrationVariables = { body: CreateIntegrationRequest | AheadOfSpecIntegrationBody }

type GetPostIntegrationMutationOptionsArgs = {
  // Distinguishes one caller's create from another's, since no integration id exists until the
  // create succeeds. A Setup drawer passes a `useId()`, so a create still running from a drawer that
  // has since closed does not count as in flight for the next one.
  callerId: string
} & Omit<UseMutationOptions<unknown, DefaultError, PostIntegrationVariables>, 'mutationKey' | 'mutationFn'>

const getPostIntegrationMutationOptions = ({ callerId, ...rest }: GetPostIntegrationMutationOptionsArgs) =>
  mutationOptions({
    mutationKey: ['integrations', 'create', callerId] as const,
    mutationFn: async ({ body }: PostIntegrationVariables) =>
      fetchWrapperMutation({
        path: '/api/v1/integrations',
        options: {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'JavaScript',
          },
          body: JSON.stringify(
            getIsIntegrationTypeSupportedByApi(body.type) ? CreateIntegrationRequestSchema.parse(body) : body
          ),
        },
        successStatusCodes: [201],
        schema: z.unknown(),
        // The created instance is read back so a webhook tool's Setup drawer can hand the user the
        // URL the platform generated for it — the id it contains does not exist until this response.
        // Left as `unknown` rather than parsed against a create-response union the spec does not
        // declare; guarded because a 201 with an empty body is still a successful create.
        deserializeFn: async (response: Response) => {
          try {
            return await (response.json() as Promise<unknown>)
          } catch {
            return undefined
          }
        },
      }),
    ...rest,
  })

const getPostIntegrationMutationKey = ({ callerId }: { callerId: string }) =>
  getPostIntegrationMutationOptions({ callerId }).mutationKey

export const usePostIntegration = ({ callerId, onSuccess, ...rest }: GetPostIntegrationMutationOptionsArgs) => {
  const _onSuccess = useCallback(
    ((data, variables, onMutateResult, context) => {
      context.client.invalidateQueries({ queryKey: ['integrations'] })
      onSuccess?.(data, variables, onMutateResult, context)
    }) satisfies Exclude<typeof onSuccess, undefined>,
    [onSuccess]
  )

  return useMutation(getPostIntegrationMutationOptions({ callerId, onSuccess: _onSuccess, ...rest }))
}

/**
 * Returns a check, not a flag: it reads the mutation cache when called, so a component that only
 * needs the answer at one moment (a keypress) does not re-render each time the create starts or
 * settles, as it would with `useIsMutating`.
 */
export const useGetIsPostIntegrationMutating = ({ callerId }: { callerId: string }) => {
  const queryClient = useQueryClient()
  return () => queryClient.isMutating({ mutationKey: getPostIntegrationMutationKey({ callerId }) }) > 0
}

export type PutIntegrationVariables = {
  body: UpdateIntegrationRequest | AheadOfSpecIntegrationBody
}

type GetPutIntegrationMutationOptionsArgs = {
  integrationId: string
} & Omit<UseMutationOptions<unknown, DefaultError, PutIntegrationVariables>, 'mutationKey' | 'mutationFn'>

const getPutIntegrationMutationOptions = ({ integrationId, ...rest }: GetPutIntegrationMutationOptionsArgs) =>
  mutationOptions({
    // Scoped by id so a save of one integration does not count as in flight for another.
    mutationKey: ['integrations', 'save', integrationId] as const,
    mutationFn: async ({ body }: PutIntegrationVariables) =>
      fetchWrapperMutation({
        path: buildPath('/api/v1/integrations/:integrationId', { integrationId }),
        options: {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'JavaScript',
          },
          body: JSON.stringify(
            getIsIntegrationTypeSupportedByApi(body.type) ? UpdateIntegrationRequestSchema.parse(body) : body
          ),
        },
        successStatusCodes: [200],
        schema: z.unknown(),
      }),
    ...rest,
  })

const getPutIntegrationMutationKey = ({ integrationId }: { integrationId: string }) =>
  getPutIntegrationMutationOptions({ integrationId }).mutationKey

export const usePutIntegration = ({ integrationId, onSuccess, ...rest }: GetPutIntegrationMutationOptionsArgs) => {
  const _onSuccess = useCallback(
    ((data, variables, onMutateResult, context) => {
      context.client.invalidateQueries({ queryKey: ['integrations'] })
      onSuccess?.(data, variables, onMutateResult, context)
    }) satisfies Exclude<typeof onSuccess, undefined>,
    [onSuccess]
  )

  return useMutation(getPutIntegrationMutationOptions({ integrationId, onSuccess: _onSuccess, ...rest }))
}

/**
 * Returns a check, not a flag: it reads the mutation cache when called, so a component that only
 * needs the answer at one moment (a keypress) does not re-render each time the save starts or
 * settles, as it would with `useIsMutating`.
 */
export const useGetIsPutIntegrationMutating = ({ integrationId }: { integrationId: string }) => {
  const queryClient = useQueryClient()
  return () => queryClient.isMutating({ mutationKey: getPutIntegrationMutationKey({ integrationId }) }) > 0
}

export type DeleteIntegrationVariables = { integrationId: string }

export const useDeleteIntegration = ({
  onSuccess,
  ...options
}: Omit<UseMutationOptions<unknown, DefaultError, DeleteIntegrationVariables>, 'mutationFn'> = {}) => {
  const _onSuccess = useCallback(
    ((data, variables, onMutateResult, context) => {
      context.client.invalidateQueries({ queryKey: ['integrations'] })
      onSuccess?.(data, variables, onMutateResult, context)
    }) satisfies Exclude<typeof onSuccess, undefined>,
    [onSuccess]
  )

  return useMutation({
    mutationFn: async ({ integrationId }: DeleteIntegrationVariables) =>
      fetchWrapperMutation({
        path: buildPath('/api/v1/integrations/:integrationId', { integrationId }),
        options: {
          method: 'DELETE',
          headers: {
            'X-Requested-With': 'JavaScript',
          },
        },
        successStatusCodes: [204],
        schema: z.unknown(),
      }),
    onSuccess: _onSuccess,
    ...options,
  })
}

export const useDeleteRepositoryIntegration = ({
  onSuccess,
  onError,
}: Pick<
  UseMutationOptions<unknown, DefaultError, { repositoryId: string; integrationId: string }>,
  'onSuccess' | 'onError'
> = {}) => {
  const _onSuccess = useCallback(
    ((data, variables, onMutateResult, context) => {
      // The repositories pages too, not just this repository's integrations: a list filtered by
      // `integration-id` still shows a repository that was just reassigned away from that instance.
      context.client.invalidateQueries({ queryKey: getRepositoriesQueryKey() })
      context.client.invalidateQueries({
        queryKey: getRepositoryIntegrationsQueryKey({ repositoryId: variables.repositoryId }),
      })
      onSuccess?.(data, variables, onMutateResult, context)
    }) satisfies Exclude<typeof onSuccess, undefined>,
    [onSuccess]
  )

  return useMutation({
    mutationFn: async ({ repositoryId, integrationId }: { repositoryId: string; integrationId: string }) =>
      fetchWrapperMutation({
        path: buildPath('/api/v1/repositories/:repositoryId/integrations/:integrationId', {
          repositoryId,
          integrationId,
        }),
        options: {
          method: 'DELETE',
          headers: {
            'X-Requested-With': 'JavaScript',
          },
        },
        successStatusCodes: [204],
        schema: z.unknown(),
      }),
    onSuccess: _onSuccess,
    onError,
  })
}

type PutRepositoryIntegrationVariables = {
  repositoryId: string
  integrationId: string
  body: UpdateRepositoryIntegrationRequest
}

const applyRepositoryIntegrationUpdate = (
  integration: RepositoryIntegration,
  update: UpdateRepositoryIntegrationRequest
): RepositoryIntegration =>
  match(update)
    .with({ type: 'polaris' }, ({ create_scans_on_ci_run }) =>
      integration.type === 'polaris'
        ? { ...integration, create_scans_on_ci_run: create_scans_on_ci_run ?? integration.create_scans_on_ci_run }
        : integration
    )
    .exhaustive()

export const usePutRepositoryIntegration = ({
  onSuccess,
  onError,
}: Pick<
  UseMutationOptions<unknown, DefaultError, PutRepositoryIntegrationVariables>,
  'onSuccess' | 'onError'
> = {}) => {
  const _onSuccess = useCallback(
    ((data, variables, onMutateResult, context) => {
      // The 200 declares no body, so the cached list takes the saved fields from the request, and a
      // control reading it does not flip back while the refetch is in flight.
      context.client.setQueriesData<RepositoryIntegrationPaginatedResponse>(
        { queryKey: getRepositoryIntegrationsQueryKey({ repositoryId: variables.repositoryId }) },
        oldData =>
          oldData && {
            ...oldData,
            _embedded: {
              ...oldData._embedded,
              items: oldData._embedded.items.map(integration =>
                integration.id === variables.integrationId
                  ? applyRepositoryIntegrationUpdate(integration, variables.body)
                  : integration
              ),
            },
          }
      )
      context.client.invalidateQueries({
        queryKey: getRepositoryIntegrationsQueryKey({ repositoryId: variables.repositoryId }),
      })
      onSuccess?.(data, variables, onMutateResult, context)
    }) satisfies NonNullable<typeof onSuccess>,
    [onSuccess]
  )

  return useMutation({
    mutationFn: async ({ repositoryId, integrationId, body }: PutRepositoryIntegrationVariables) =>
      fetchWrapperMutation({
        path: buildPath('/api/v1/repositories/:repositoryId/integrations/:integrationId', {
          repositoryId,
          integrationId,
        }),
        options: {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'JavaScript',
          },
          body: JSON.stringify(UpdateRepositoryIntegrationRequestSchema.parse(body)),
        },
        successStatusCodes: [200],
        schema: z.unknown(),
      }),
    onSuccess: _onSuccess,
    onError,
  })
}

// #endregion mutations

// #region: useQueryWithLinks utility

export type LinkConfig<T> = {
  key: string // e.g. '_links.repository.href' or '_embedded.items._links.repository.href'
  schema: z.ZodType<T>
}

/**
 * Utility for querying an API and its HAL links
 *
 * @param baseQuery - The base query options
 * @param linkConfigs - The link configurations
 */
export function useQueryWithLinks<
  TData extends { _embedded: { items: unknown[] } },
  T1,
  TError = Error,
  TQueryKey extends QueryKey = QueryKey,
>(
  baseQuery: UseQueryOptions<TData, TError, TData, TQueryKey>,
  linkConfigs: [LinkConfig<T1>]
): { baseResult: UseQueryResult<TData>; linkedResults: [UseQueryResult<T1>[]] }

export function useQueryWithLinks<
  TData extends { _embedded: { items: unknown[] } },
  T1,
  T2,
  TError = Error,
  TQueryKey extends QueryKey = QueryKey,
>(
  baseQuery: UseQueryOptions<TData, TError, TData, TQueryKey>,
  linkConfigs: [LinkConfig<T1>, LinkConfig<T2>]
): { baseResult: UseQueryResult<TData>; linkedResults: [UseQueryResult<T1>[], UseQueryResult<T2>[]] }

export function useQueryWithLinks<
  TData extends { _embedded: { items: unknown[] } },
  T1,
  T2,
  T3,
  TError = Error,
  TQueryKey extends QueryKey = QueryKey,
>(
  baseQuery: UseQueryOptions<TData, TError, TData, TQueryKey>,
  linkConfigs: [LinkConfig<T1>, LinkConfig<T2>, LinkConfig<T3>]
): {
  baseResult: UseQueryResult<TData>
  linkedResults: [UseQueryResult<T1>[], UseQueryResult<T2>[], UseQueryResult<T3>[]]
}

export function useQueryWithLinks<
  TData extends { _embedded: { items: unknown[] } },
  TError = Error,
  TQueryKey extends QueryKey = QueryKey,
>(baseQuery: UseQueryOptions<TData, TError, TData, TQueryKey>, linkConfigs: LinkConfig<unknown>[]) {
  const baseResult = useQuery(baseQuery)

  const queryGroups = useMemo(
    () =>
      baseResult.data && linkConfigs.length > 0
        ? createLinksQueryConfigs(baseQuery, baseResult, linkConfigs)
        : linkConfigs.map(() => [] as UseQueryOptions[]),
    [baseResult.data, baseQuery.queryKey, linkConfigs]
  )

  const flatQueries = useMemo(() => queryGroups.flat(), [queryGroups])
  const flatResults = useQueries({ queries: flatQueries })

  let offset = 0
  const linkedResults = queryGroups.map(group => {
    const chunk = flatResults.slice(offset, offset + group.length)
    offset += group.length
    return chunk
  })

  return { baseResult, linkedResults }
}

const createLinksQueryConfigs = <
  TData extends { _embedded: { items: unknown[] } },
  TError = Error,
  TQueryKey extends QueryKey = QueryKey,
>(
  baseQuery: UseQueryOptions<TData, TError, TData, TQueryKey>,
  baseResult: UseQueryResult<TData, TError>,
  linkConfigs: LinkConfig<unknown>[]
) =>
  linkConfigs.map(config =>
    match(config.key)
      .when(
        key => key.includes('_embedded.items'),
        () => {
          const items = baseResult.data?._embedded?.items ?? []
          return items.map(item =>
            createItemLinkQueryConfig(
              baseQuery.queryKey,
              getStringAtPath(item, config.key.replace(/^_embedded\.items\./, '')),
              config
            )
          )
        }
      )
      .otherwise(() => [
        createLinkQueryConfig(baseQuery.queryKey, getStringAtPath(baseResult.data, config.key), config),
      ])
  )

const createItemLinkQueryConfig = <T>(
  baseQueryKey: readonly unknown[],
  path: string,
  config: LinkConfig<T>
  // eslint-disable-next-line @tanstack/query/exhaustive-deps -- queryKeyHashFn runs JSON.stringify on every key, which throws on a recursive schema such as RepositorySchema; each caller pairs a link key with one schema, so the key and path identify the request.
): UseQueryOptions<T> => ({
  queryKey: [...baseQueryKey, `link:${config.key}`, `item:${path}`],
  queryFn: () => fetchWrapperQuery({ path, schema: config.schema }),
  enabled: !!path,
})

const createLinkQueryConfig = <T>(
  baseQueryKey: readonly unknown[],
  path: string,
  config: LinkConfig<T>
  // eslint-disable-next-line @tanstack/query/exhaustive-deps -- queryKeyHashFn runs JSON.stringify on every key, which throws on a recursive schema such as RepositorySchema; each caller pairs a link key with one schema, so the key and path identify the request.
): UseQueryOptions<T> => ({
  queryKey: [...baseQueryKey, `link:${config.key}`, path],
  queryFn: () => fetchWrapperQuery({ path, schema: config.schema }),
  enabled: !!path,
})

const getStringAtPath = (object: unknown, path: string) => {
  const result = _.get(object, path) as unknown
  invariantType(result, 'string', 'result')

  return result
}

// #endregion

// #region: useGetAllPages utility

type PageOptions = {
  number: number
  size: number
}

type GetAllPagesOptions<TData extends PaginatedResponse, TError = Error, TQueryKey extends QueryKey = QueryKey> = {
  getPageQueryOptions: (pageOptions: PageOptions) => UseQueryOptions<TData, TError, TData, TQueryKey>
  pageSize?: number
}

/**
 * **Utility for fetching all pages**
 *
 * @param options.pageToQueryOptions - A function that returns a query options for a given page
 * @returns A hook that fetches all pages of a paginated resource
 *
 * **Parallel queries considerations**
 *
 * Currently all pages are fetched in parallel.
 *
 * In the case of say 1000 analyses with 100 per page, this will fetch 10 queries in parallel which is fine for our database load constraints (few users per enterprise server).
 *
 * In the future may need to limit the number of parallel queries, see this issue for implementation reference: https://github.com/TanStack/query/issues/1000.
 */
export const useGetAllPages = <TData extends PaginatedResponse, TError = Error, TQueryKey extends QueryKey = QueryKey>({
  getPageQueryOptions,
  pageSize = 100,
}: GetAllPagesOptions<TData, TError, TQueryKey>): UseQueryResult<TData, TError>[] => {
  const queryResult = useQuery(getPageQueryOptions({ number: 0, size: pageSize }))
  const queries = calculateAllPages(queryResult.data).map(getPageQueryOptions)
  return useQueries({ queries })
}

export const calculateAllPages = (data?: PaginatedResponse): PageOptions[] => {
  if (!data) return []
  const total = data.total
  const size = data.page.size
  const pageCount = Math.ceil(total / size)
  return Array.from({ length: pageCount }, (_, i) => ({ number: i, size }))
}

// #endregion

// #region: preferences

export type OrganizationPreferencesWithEtag = {
  data: OrganizationPreferencesResponse | null
  etag: string | null
}

type GetOrganizationPreferencesOptionsInput = {
  organizationId?: string
}

const getOrganizationPreferencesOptions = ({
  organizationId = 'default-organization',
}: GetOrganizationPreferencesOptionsInput = {}) =>
  queryOptions({
    queryKey: ['organization-preferences', organizationId],
    queryFn: async (): Promise<OrganizationPreferencesWithEtag> => {
      const path = buildPath('/api/v1/organizations/:organizationId/preferences', { organizationId })
      const response = await fetch(path, {
        headers: {
          'X-Requested-With': 'JavaScript',
          Accept: 'application/hal+json, application/json',
        },
      })

      const etag = response.headers.get('ETag')

      if (response.status === 204) {
        return { data: null, etag: null }
      }

      if (response.status !== 200) {
        throw new ApiError(response.status, await response.text(), path)
      }

      const data = await logZodError<OrganizationPreferencesResponse>(
        path,
        OrganizationPreferencesResponseSchema,
        async () => OrganizationPreferencesResponseSchema.parse(await response.json())
      )

      return { data, etag: etag || null }
    },
  })

export const useSuspenseGetOrganizationPreferences = (input?: GetOrganizationPreferencesOptionsInput) =>
  useSuspenseQuery(getOrganizationPreferencesOptions(input))

export const usePutOrganizationPreferences = ({
  organizationId = 'default-organization',
  onSuccess,
  onError,
  ...options
}: {
  organizationId?: string
} & Omit<UseMutationOptions<void, ApiError, { contentMarkdown: string; etag: string | null }>, 'mutationFn'> = {}) => {
  const _onSuccess = useCallback<Exclude<typeof onSuccess, undefined>>(
    (data, variables, onMutateResult, context) => {
      context.client.setQueryData(
        ['organization-preferences', organizationId],
        (old: OrganizationPreferencesWithEtag | undefined) =>
          old ? { ...old, data: old.data ? { ...old.data, content: variables.contentMarkdown } : old.data } : old
      )
      context.client.invalidateQueries({ queryKey: ['organization-preferences'] })
      onSuccess?.(data, variables, onMutateResult, context)
    },
    [organizationId, onSuccess]
  )

  return useMutation({
    mutationFn: async ({ contentMarkdown, etag }: { contentMarkdown: string; etag: string | null }) => {
      const path = buildPath('/api/v1/organizations/:organizationId/preferences', { organizationId })
      const headers: Record<string, string> = {
        'X-Requested-With': 'JavaScript',
        Accept: 'application/hal+json, application/json',
        'Content-Type': 'application/json',
      }

      if (etag) {
        headers['If-Match'] = etag
      } else {
        headers['If-None-Match'] = '*'
      }

      const response = await fetch(path, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ content: contentMarkdown }),
      })

      if (![200, 204].includes(response.status)) {
        throw new ApiError(response.status, await response.text(), path)
      }
    },
    onSuccess: _onSuccess,
    onError,
    ...options,
  })
}

export const useGetAnalysisPreferences = ({
  analysisId,
  enabled = true,
  ...rest
}: {
  analysisId?: string
  enabled?: boolean
} & Omit<UseQueryOptions<string | null>, 'queryKey' | 'queryFn'> = {}) => {
  return useQuery({
    queryKey: ['analysis-preferences', analysisId],
    // This endpoint returns text/markdown (not JSON), so fetchWrapperQuery cannot be used —
    // response.text() is required instead of response.json().
    queryFn: async () => {
      const path = buildPath('/api/v1/analyses/:analysisId/preferences', { analysisId: analysisId ?? '' })
      const response = await fetch(path, {
        headers: { 'X-Requested-With': 'JavaScript', Accept: 'text/markdown; variant=GFM' },
      })
      if (response.status === 204) return null
      if (response.status === 404) return null
      if (response.status !== 200) throw new ApiError(response.status, await response.text(), path)
      return await response.text()
    },
    enabled: !!analysisId && enabled,
    ...rest,
  })
}

// #endregion

// #region: projects (ISS-8955)

const PROJECTS_QUERY_KEY = 'projects'

const projectPath = (projectId: string) => buildPath('/api/v1/projects/:projectId', { projectId })

type ProjectsListParams = {
  /**
   * Narrows the list to projects whose name contains this, compared case-insensitively. Blank lists
   * every project. The API filters and counts alike, so the total agrees with the page.
   */
  name?: string
  /**
   * Embeds the first page of each project's repositories, for a list that shows member names. Off by
   * default: at hundreds of projects it fetches up to 25 repositories apiece, which a caller that
   * wants only names and counts pays for and never reads.
   */
  embedRepositories?: boolean
}

const getProjectsPageOptions =
  ({ name = '', embedRepositories = false }: ProjectsListParams) =>
  ({ number, size }: PageOptions) =>
    queryOptions({
      queryKey: [PROJECTS_QUERY_KEY, 'page', { number, size, name, embedRepositories }],
      queryFn: () => {
        const queryParams = createPageParams(number, size)
        if (name) {
          queryParams.append('name', name)
        }
        if (embedRepositories) {
          queryParams.append('embed', 'items.repositories')
        }
        return fetchWrapperQuery({
          path: `/api/v1/projects?${queryParams}`,
          schema: ProjectPaginatedResponseSchema,
        })
      },
      // The name filter is the API's, so every keystroke is a new key. Holding the last answer keeps the
      // searched list on screen instead of emptying it between requests.
      placeholderData: keepPreviousData,
    })

/**
 * The organization's projects, ordered by name as the API lists them, optionally narrowed by name.
 *
 * `isLoading` follows the first page, since `useGetAllPages` answers `[]` both before that page settles
 * and when there are genuinely no projects.
 */
export const useGetProjects = ({ name, embedRepositories }: ProjectsListParams = {}) => {
  const queryClient = useQueryClient()
  const getPageQueryOptions = getProjectsPageOptions({ name, embedRepositories })
  const firstPage = useQuery(getPageQueryOptions({ number: 0, size: 100 }))
  const pages = useGetAllPages({ getPageQueryOptions })
  // Flattened on every render: `useQueries` hands back a new array each time, so there is nothing stable
  // to memoise on, and a dependency list spread from the pages would change length between renders.
  const projects = pages.flatMap(page => page.data?._embedded.items ?? [])
  const isPending = firstPage.isPending || pages.some(page => page.isPending)
  const isError = firstPage.isError || pages.some(page => page.isError)
  const error = firstPage.error ?? pages.find(page => page.error)?.error
  return {
    projects,
    collection: firstPage.data,
    isPending,
    isError,
    error,
    total: firstPage.data?.total,
    refetch: () => refetchFailedActiveQueries(queryClient, [PROJECTS_QUERY_KEY, 'page']),
  }
}

/**
 * The first page of the organization's projects, optionally narrowed by name — for a list that offers
 * projects to pick from and lets the API's name search reach the rest, rather than reading every page.
 */
export const useGetProjectsPage = ({ name, size }: { name?: string; size: number }) =>
  useQuery(getProjectsPageOptions({ name })({ number: 0, size }))

const getProjectOptions = ({ projectId, enabled = true }: { projectId: string; enabled?: boolean }) =>
  queryOptions({
    queryKey: [PROJECTS_QUERY_KEY, projectId],
    queryFn: () => fetchWrapperQuery({ path: projectPath(projectId), schema: ProjectSchema }),
    enabled,
  })

export const useGetProject = (input: { projectId: string; enabled?: boolean }) => useQuery(getProjectOptions(input))

/**
 * A project read by following a HAL link as the API gave it, rather than by taking an id apart and
 * rebuilding the URL — the link is the API's to shape. @see useGetRepositoryFromHalLink
 */
export const useGetProjectFromHalLink = ({ path, enabled = true }: { path?: string; enabled?: boolean }) =>
  useQuery({
    queryKey: [PROJECTS_QUERY_KEY, 'link', path],
    queryFn: () => fetchWrapperQuery({ path: path!, schema: ProjectSchema }),
    enabled: enabled && !!path,
  })

export const getProjectMembershipsOptions = (path: string) =>
  queryOptions({
    queryKey: [PROJECTS_QUERY_KEY, 'memberships', path],
    queryFn: () => fetchWrapperQuery({ path, schema: ProjectMembershipPaginatedResponseSchema }),
  })

export const useGetProjectMembershipsFromHalLink = ({ path, enabled = true }: { path?: string; enabled?: boolean }) =>
  useQuery({ ...getProjectMembershipsOptions(path ?? ''), enabled: enabled && !!path })

/**
 * Whether a cache entry is a repositories page filtered to exactly this project — the member list a
 * project's page reads. Matched on the key's shape because the filter is one element of a positional
 * key, and there is no narrower prefix to match on.
 */
const isRepositoriesQueryForProject = (queryKey: readonly unknown[], projectId: string) => {
  if (queryKey[0] !== 'repositories') {
    return false
  }
  const projectIds = queryKey[4]
  return Array.isArray(projectIds) && projectIds.includes(projectId)
}

/** What the cache-wide `MutationCache` does for a thrown 403, for the failures a mutation resolves with. */
const refreshRoleOnForbidden = (client: QueryClient, errors: unknown[]) => {
  if (errors.some(isForbiddenError)) {
    client.invalidateQueries({ queryKey: getCurrentUserQueryKey() })
  }
}

const invalidateProjectMembership = (client: QueryClient) => {
  client.invalidateQueries({ queryKey: [PROJECTS_QUERY_KEY] })
  client.invalidateQueries({ queryKey: ['repositories'] })
  // The single-repository reads carry the membership too, so a move that misses them leaves the
  // repository drawer naming the project the repository has just left.
  invalidateRepositories(client)
}

const useRefetchProjectsOnForbidden = <TData, TVariables>(
  onError: UseMutationOptions<TData, ApiError, TVariables>['onError']
) =>
  useCallback<NonNullable<typeof onError>>(
    (error, variables, onMutateResult, context) => {
      if (isForbiddenError(error)) {
        invalidateProjectMembership(context.client)
      }
      onError?.(error, variables, onMutateResult, context)
    },
    [onError]
  )

const postProject = (template: AvailableTemplate, body: CreateProjectRequest) =>
  fetchWrapperMutation({
    path: template.target,
    options: { method: template.method, body: JSON.stringify(CreateProjectRequestSchema.parse(body)) },
    successStatusCodes: [201],
    schema: ProjectSchema,
    deserializeFn: async response => (await response.json()) as unknown,
  })

export type CreateProjectWithRepositoriesVariables = {
  template: AvailableTemplate
  body: CreateProjectRequest
  repositoryIds: string[]
}

/**
 * A created project, and what became of its first memberships: the failure they met, or
 * `isAssignmentWithheld` when the new project's memberships collection offered no `create`.
 */
export type CreatedProject = { project: Project; assignmentError?: Error; isAssignmentWithheld?: boolean }

/**
 * Creates a project and puts its first repositories in it.
 *
 * One mutation rather than two chained ones, so there is a single pending state and a single error
 * path: started from the first's `onSuccess`, neither hook was pending in the gap between them, and a
 * failed assignment raised a toast from one and a success toast from the other.
 *
 * A failed assignment is not a failed create — the project exists, and the spec says the reader lands
 * on it with an error toast — so it resolves, carrying the refusal for the caller to report.
 */
export const useCreateProjectWithRepositories = ({
  onSuccess,
  onError,
  ...options
}: Omit<UseMutationOptions<CreatedProject, ApiError, CreateProjectWithRepositoriesVariables>, 'mutationFn'> = {}) => {
  const queryClient = useQueryClient()
  const _onSuccess = useCallback<Exclude<typeof onSuccess, undefined>>(
    (data, variables, onMutateResult, context) => {
      invalidateProjectMembership(context.client)
      onSuccess?.(data, variables, onMutateResult, context)
    },
    [onSuccess]
  )
  const _onError = useRefetchProjectsOnForbidden(onError)

  return useMutation({
    mutationFn: async ({ template, body, repositoryIds }: CreateProjectWithRepositoriesVariables) => {
      const project = await postProject(template, body)
      if (repositoryIds.length === 0) {
        return { project }
      }
      try {
        const memberships = await queryClient.query(getProjectMembershipsOptions(project._links.memberships.href))
        const create = getTemplate(memberships, 'create')
        if (create.state !== 'available') {
          return { project, isAssignmentWithheld: true }
        }
        await addProjectMemberships(create, repositoryIds)
        return { project }
      } catch (error) {
        // Resolved rather than thrown, so the cache-wide 403 handling never sees it.
        refreshRoleOnForbidden(queryClient, [error])
        return { project, assignmentError: error instanceof Error ? error : new Error(String(error)) }
      }
    },
    onSuccess: _onSuccess,
    onError: _onError,
    ...options,
  })
}

export type PutProjectVariables = { projectId: string; template: AvailableTemplate; body: UpdateProjectRequest }

export const usePutProject = ({
  onSuccess,
  onError,
  ...options
}: Omit<UseMutationOptions<Project, ApiError, PutProjectVariables>, 'mutationFn'> = {}) => {
  const _onSuccess = useCallback<Exclude<typeof onSuccess, undefined>>(
    (data, variables, onMutateResult, context) => {
      context.client.setQueryData(getProjectOptions({ projectId: variables.projectId }).queryKey, data)
      invalidateProjectMembership(context.client)
      onSuccess?.(data, variables, onMutateResult, context)
    },
    [onSuccess]
  )
  const _onError = useRefetchProjectsOnForbidden(onError)

  return useMutation({
    mutationFn: ({ template, body }: PutProjectVariables) =>
      fetchWrapperMutation({
        path: template.target,
        options: { method: template.method, body: JSON.stringify(UpdateProjectRequestSchema.parse(body)) },
        successStatusCodes: [200],
        schema: ProjectSchema,
        deserializeFn: async response => (await response.json()) as unknown,
      }),
    onSuccess: _onSuccess,
    onError: _onError,
    ...options,
  })
}

export type DeleteProjectVariables = { projectId: string; membershipsHref: string; template: AvailableTemplate }

export const useDeleteProject = ({
  onSuccess,
  onError,
  ...options
}: Omit<UseMutationOptions<unknown, ApiError, DeleteProjectVariables>, 'mutationFn'> = {}) => {
  // The caller leaves the project's page first, but a route change does not unmount synchronously, so
  // the detail and member queries are still observed when the invalidation runs. Drop them instead of
  // invalidating them: refetching a deleted project only buys 404s and their retries.
  const _onSuccess = useCallback<Exclude<typeof onSuccess, undefined>>(
    (data, variables, onMutateResult, context) => {
      onSuccess?.(data, variables, onMutateResult, context)
      context.client.removeQueries({ queryKey: getProjectOptions({ projectId: variables.projectId }).queryKey })
      context.client.removeQueries({ queryKey: getProjectMembershipsOptions(variables.membershipsHref).queryKey })
      context.client.removeQueries({
        predicate: query => isRepositoriesQueryForProject(query.queryKey, variables.projectId),
      })
      invalidateProjectMembership(context.client)
    },
    [onSuccess]
  )
  const _onError = useRefetchProjectsOnForbidden(onError)

  return useMutation({
    mutationFn: ({ template }: DeleteProjectVariables) =>
      fetchWrapperMutation({
        path: template.target,
        options: { method: template.method },
        successStatusCodes: [204],
        schema: z.unknown(),
      }),
    onSuccess: _onSuccess,
    onError: _onError,
    ...options,
  })
}

export type AddProjectMembershipsVariables = {
  projectId: string
  template: AvailableTemplate
  repositoryIds: string[]
}

/** Adds repositories to a project. A repository already in another project is moved to this one. */
export const useAddProjectMemberships = ({
  onSuccess,
  ...options
}: Omit<
  UseMutationOptions<ProjectMembershipPaginatedResponse, ApiError, AddProjectMembershipsVariables>,
  'mutationFn'
> = {}) => {
  // On settle, not on success: a project-full 409 means the count the client held was out of date, so
  // the refusal is exactly when the lists need refreshing. The caller does not have to know that.
  const _onSettled = useCallback<Exclude<typeof options.onSettled, undefined>>(
    (data, error, variables, onMutateResult, context) => {
      invalidateProjectMembership(context.client)
      options.onSettled?.(data, error, variables, onMutateResult, context)
    },
    [options.onSettled]
  )

  return useMutation({
    mutationFn: ({ template, repositoryIds }: AddProjectMembershipsVariables) =>
      addProjectMemberships(template, repositoryIds),
    ...options,
    onSuccess,
    onSettled: _onSettled,
  })
}

const addProjectMemberships = (template: AvailableTemplate, repositoryIds: string[]) =>
  fetchWrapperMutation({
    path: template.target,
    options: {
      method: template.method,
      body: JSON.stringify(AddProjectMembershipsRequestSchema.parse({ repository_ids: repositoryIds })),
    },
    successStatusCodes: [200],
    schema: ProjectMembershipPaginatedResponseSchema,
    deserializeFn: async response => (await response.json()) as unknown,
  })

type MembershipRemoval = { repositoryId: string; template: AvailableTemplate }

export type DeleteRepositoryProjectMembershipsVariables = { removals: MembershipRemoval[] }

/** What a bulk removal did: the ids that left their project, and the ones the server refused. */
export type BulkMembershipRemoval = { removed: string[]; failed: string[] }

/**
 * Removes a selection of repositories from their project.
 *
 * Each membership is its own resource, so a selection is N calls. They go together and settle together:
 * one invalidation rather than N cancel-and-refetch rounds against the same two lists, and one result
 * describing what actually happened — a partial failure is neither a success nor a failure, and the
 * caller needs to be able to say so.
 *
 * A removal where nothing at all was removed is a failure, and raises the first refusal, so a caller
 * reports it through `onError`. `onSuccess` therefore means at least one repository left its project.
 */
export const useDeleteRepositoryProjectMemberships = ({
  onSuccess,
  onError,
  ...options
}: Omit<
  UseMutationOptions<BulkMembershipRemoval, ApiError, DeleteRepositoryProjectMembershipsVariables>,
  'mutationFn'
> = {}) => {
  const _onSuccess = useCallback<Exclude<typeof onSuccess, undefined>>(
    (data, variables, onMutateResult, context) => {
      invalidateProjectMembership(context.client)
      onSuccess?.(data, variables, onMutateResult, context)
    },
    [onSuccess]
  )
  const _onError = useRefetchProjectsOnForbidden(onError)

  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ removals }: DeleteRepositoryProjectMembershipsVariables) => {
      const results = await Promise.allSettled(
        removals.map(({ template }) =>
          fetchWrapperMutation({
            path: template.target,
            options: { method: template.method },
            successStatusCodes: [204],
            schema: z.unknown(),
          })
        )
      )
      const repositoryIds = removals.map(removal => removal.repositoryId)
      const removed = repositoryIds.filter((_, index) => results[index]?.status === 'fulfilled')
      const failed = repositoryIds.filter((_, index) => results[index]?.status === 'rejected')
      const reasons = results.flatMap((result): unknown[] => (result.status === 'rejected' ? [result.reason] : []))
      if (removed.length === 0 && reasons.length > 0) {
        throw reasons[0]
      }
      // A partial removal resolves, so the cache-wide 403 handling never sees its refusals.
      refreshRoleOnForbidden(queryClient, reasons)
      return { removed, failed }
    },
    onSuccess: _onSuccess,
    onError: _onError,
    ...options,
  })
}

// #endregion

// #region SSE Hooks

const ACTIVITY_ENDPOINT_BY_TYPE = {
  triage: '/api/v1/triage-results/:resultId/activity',
  fix: '/api/v1/fix-results/:resultId/activity',
  sca: '/api/v1/sca-results/:resultId/activity',
} as const

/**
 * The event names this build knows how to parse. The platform sets the SSE `event:` name from the event's
 * top-level `type`, so an allowlist here drops a `data-*` type we do not recognize before it reaches the
 * parser. Without it, a producer that leads the consumer during a deploy fails the strict union and logs a
 * `console.error` per frame.
 *
 * Derived from the union rather than listed, so the comment above stays true by construction: a new member
 * of `AnalysisStreamEventSchema` is admitted automatically. A hand-kept copy would drop it silently, and a
 * silent drop is indistinguishable from the allowlist working as intended.
 */
const ACTIVITY_EVENT_NAMES = AnalysisStreamEventSchema.options.map(option => option.shape.type.value)

/**
 * Event names already reported by {@link reportDroppedActivityEventName}. A skewed producer re-sends the
 * same unrecognized name on every frame, so dedupe to one line per name per session — enough to diagnose
 * the skew from a support ticket, not enough to bury the console.
 */
const reportedDroppedEventNames = new Set<string>()

const reportDroppedActivityEventName = (eventName: string) => {
  if (reportedDroppedEventNames.has(eventName)) {
    return
  }
  reportedDroppedEventNames.add(eventName)
  console.warn(
    `Dropped SSE event "${eventName}": this build does not recognize it. ` +
      'Expected during a deploy where analysis-service leads user-platform.'
  )
}

export const useGetResultActivitySSE = (
  resultId: string | undefined,
  resultType: 'triage' | 'fix' | 'sca'
): SSEStream<AnalysisStreamEvent> => {
  const url = resultId ? buildPath(ACTIVITY_ENDPOINT_BY_TYPE[resultType], { resultId }) : undefined

  return useSSEStream({
    url,
    schema: AnalysisStreamEventSchema,
    eventNames: ACTIVITY_EVENT_NAMES,
    onDroppedEventName: reportDroppedActivityEventName,
    // Closing here is deliberate. The widgets ADR (product-os `adrs/rich-activity-widgets.md`) makes "a
    // widget's last snapshot must precede its finding's terminal event" a producer obligation rather than
    // widening the stream's lifetime, which would mean holding an open connection per viewed finding with
    // no signal left to close it. The ADR also records that the obligation is enforced nowhere and that a
    // late snapshot hides from whoever causes it, since it does appear on reload.
    isFinished: event => event.type === 'data-progress-finding-complete',
    onEvent: event => {
      if (event.type === 'error') {
        console.warn('SSE error event received:', event.content)
      }
      if (event.type === 'workflow' && event.content.status === 'error') {
        console.warn('SSE workflow error:', event.content.error ?? event.content.description)
      }
    },
  })
}

/**
 * Subscribes to the SSE phase stream for a single support bundle. Passing `undefined` keeps the
 * hook idle (no connection opened); pass the bundle id returned by `usePostSupportBundle`'s 202
 * response to start streaming. The stream closes when the bundle reaches a terminal phase.
 */
export const useSupportBundleStream = (bundleId: string | undefined): SSEStream<SupportBundlePhaseEvent> =>
  useSSEStream({
    url: bundleId ? buildPath('/api/v1/support-bundles/:bundleId', { bundleId }) : undefined,
    schema: SupportBundlePhaseEventSchema,
    isFinished: event => event.phase === 'succeeded' || event.phase === 'failed',
  })

// #endregion

// #region Pixee Beacon (every endpoint is Labs-gated and 404s with Labs off)

/** While any listed run is unfinished the Repositories column refreshes its pills this often. */
export const BEACON_LATEST_RUNS_POLL_MS = 15_000

/** How often a queued or dispatching run is re-read, so its page notices it start or wait for capacity. */
export const BEACON_WAITING_RUN_POLL_MS = 2_000

/** How often an in-progress run is re-read: a backstop to its stream, which carries the progress. */
export const BEACON_RUNNING_RUN_POLL_MS = 15_000

/**
 * The batched latest-runs endpoint answers at most this many repositories per request. The ids travel in
 * the query string, so a larger batch also outgrows the server's request-line limit.
 */
export const BEACON_LATEST_RUNS_BATCH_SIZE = 100

const BEACON_QUERY_KEY_ROOT = 'beacon'

export const getBeaconRunQueryKey = (runId: string | undefined) => [BEACON_QUERY_KEY_ROOT, 'run', runId] as const
const getBeaconRunCoverageQueryKey = (runId: string | undefined) => [BEACON_QUERY_KEY_ROOT, 'coverage', runId] as const
const getLatestBeaconRunsQueryKey = (repositoryIds: readonly string[]) =>
  [BEACON_QUERY_KEY_ROOT, 'latest-runs', [...repositoryIds].sort()] as const
const getRepositoryLatestBeaconRunQueryKey = (repositoryId: string | undefined) =>
  [BEACON_QUERY_KEY_ROOT, 'repository-latest-run', repositoryId] as const

/** Refresh every Beacon run snapshot: pills, run pages and each repository's latest run. */
export const invalidateBeaconRuns = (client: QueryClient) =>
  client.invalidateQueries({ queryKey: [BEACON_QUERY_KEY_ROOT] })

/** The id a run's link names. A run carries its references as links, never as foreign ids. */
const idFromBeaconRunLink = (link: HALLink | undefined, collection: 'repositories' | 'scans') =>
  new RegExp(`^/api/v1/${collection}/([^/?#]+)$`).exec(link?.href ?? '')?.[1]

/** The repository a run scans, read from its `_links.repository`. */
export const getBeaconRunRepositoryId = (run: Pick<BeaconRun, '_links'>) =>
  idFromBeaconRunLink(run._links.repository, 'repositories')

/** The scan a completed run's findings became, read from its `_links.scan`; absent before then. */
export const getBeaconRunScanId = (run: Pick<BeaconRun, '_links'>) => idFromBeaconRunLink(run._links.scan, 'scans')

const UNFINISHED_BEACON_RUN_STATES: ReadonlySet<AiScanRunState> = new Set(['queued', 'dispatching', 'in-progress'])

/** Whether a run has yet to complete, fail or be cancelled. */
export const isBeaconRunUnfinished = (run: Pick<BeaconRun, 'state'> | null | undefined) =>
  !!run && UNFINISHED_BEACON_RUN_STATES.has(run.state)

/** Re-read a waiting run often enough to see it start, a running one as a backstop, a finished one never. */
export const getBeaconRunRefetchInterval = (run: Pick<BeaconRun, 'state'> | null | undefined) =>
  match(run?.state)
    .with('queued', 'dispatching', () => BEACON_WAITING_RUN_POLL_MS)
    .with('in-progress', () => BEACON_RUNNING_RUN_POLL_MS)
    .otherwise(() => false as const)

/**
 * GET that treats the given statuses (no run yet, or not ready yet) as `null` rather than an error, so an
 * absent resource renders as an empty state instead of an error boundary.
 */
const fetchBeaconResource = async <T extends z.ZodType>({
  path,
  schema,
  absentStatuses,
}: {
  path: string
  schema: T
  absentStatuses: readonly number[]
}): Promise<z.infer<T> | null> => {
  const response = await fetch(path, {
    headers: { 'X-Requested-With': 'JavaScript', Accept: 'application/json' },
  })
  if (absentStatuses.includes(response.status)) {
    return null
  }
  if (!response.ok) {
    throw new ApiError(response.status, await response.text(), path)
  }
  return logZodError<z.infer<T>>(path, schema, async () => schema.parse(await response.json()))
}

type PostBeaconScanVariables = { repositoryId: string; effort: BeaconEffort }

/**
 * Starts a Beacon run on a repository. A 202 carries the new run, queued; a 200 carries the run the
 * repository already has unfinished, since a repository runs one at a time. Either way the caller opens
 * the run it gets back.
 */
export const usePostBeaconScan = ({
  onSuccess,
  onError,
}: {
  onSuccess?: OnSuccess<BeaconRun, PostBeaconScanVariables>
  onError?: OnError<BeaconRun, PostBeaconScanVariables>
} = {}) => {
  const _onSuccess = useCallback<Exclude<typeof onSuccess, undefined>>(
    (data, variables, onMutateResult, context) => {
      invalidateBeaconRuns(context.client)
      context.client.setQueryData(getBeaconRunQueryKey(data.id), data)
      onSuccess?.(data, variables, onMutateResult, context)
    },
    [onSuccess]
  )

  return useMutation({
    mutationFn: async ({ repositoryId, effort }: PostBeaconScanVariables) =>
      fetchWrapperMutation({
        path: buildPath('/api/v1/repositories/:repositoryId/ai-scans', { repositoryId }),
        options: { method: 'POST', body: JSON.stringify({ effort } satisfies BeaconScanRequest) },
        schema: BeaconRunSchema,
        successStatusCodes: [200, 202],
        deserializeFn: (response: Response) => response.json(),
      }),
    onSuccess: _onSuccess,
    onError,
  })
}

type CancelBeaconRunVariables = { runId: string }

/**
 * Cancels an unfinished run. The platform answers 200 with the run already cancelled, and again with the
 * same run should it have been cancelled before (a retry, a second tab). A 409 means the run completed or
 * failed first, so the run is re-read before the caller reports it.
 */
export const useCancelBeaconRun = ({
  onSuccess,
  onError,
}: {
  onSuccess?: OnSuccess<BeaconRun, CancelBeaconRunVariables>
  onError?: OnError<BeaconRun, CancelBeaconRunVariables>
} = {}) => {
  const _onSuccess = useCallback<Exclude<typeof onSuccess, undefined>>(
    (data, variables, onMutateResult, context) => {
      invalidateBeaconRuns(context.client)
      context.client.setQueryData(getBeaconRunQueryKey(data.id), data)
      onSuccess?.(data, variables, onMutateResult, context)
    },
    [onSuccess]
  )
  const _onError = useCallback<Exclude<typeof onError, undefined>>(
    (error, variables, onMutateResult, context) => {
      if (error instanceof ApiError && error.status === 409) {
        invalidateBeaconRuns(context.client)
      }
      onError?.(error, variables, onMutateResult, context)
    },
    [onError]
  )

  return useMutation({
    mutationFn: async ({ runId }: CancelBeaconRunVariables) =>
      fetchWrapperMutation({
        path: buildPath('/api/v1/ai-scans/:runId/cancellation', { runId }),
        options: { method: 'POST' },
        schema: BeaconRunSchema,
        successStatusCodes: [200],
        deserializeFn: (response: Response) => response.json(),
      }),
    onSuccess: _onSuccess,
    onError: _onError,
  })
}

/** No such run, or Labs off: an answer the run's page shows, not a failure to retry or report. */
export const isBeaconRunNotFound = (error: unknown) => error instanceof ApiError && error.status === 404

/** One run. A 404 is answered at once rather than retried; any other failure follows the client's policy. */
export const useGetBeaconRun = ({ runId, enabled = true }: { runId: string | undefined; enabled?: boolean }) =>
  useQuery(
    queryOptions({
      ...inlineAnswerQueryPolicy(isBeaconRunNotFound),
      queryKey: getBeaconRunQueryKey(runId),
      queryFn: () =>
        fetchWrapperQuery({
          path: buildPath('/api/v1/ai-scans/:runId', { runId: runId ?? '' }),
          schema: BeaconRunSchema,
        }),
      enabled: enabled && isLabsEnabled() && !!runId,
      refetchInterval: query => getBeaconRunRefetchInterval(query.state.data),
      // A background tab or covered window reads as hidden, and hidden pages skip interval refetches.
      refetchIntervalInBackground: true,
    })
  )

/**
 * A completed run's coverage summary. Enable it only once the run completed: before that the endpoint
 * answers 409, which resolves to `null`. A summary never changes once it exists, but a `null` can be
 * stale within seconds, since the run's `completed` frame can arrive before the platform has completed
 * the run, so a `null` is asked again until the summary arrives.
 */
export const useGetBeaconRunCoverage = ({ runId, enabled = true }: { runId: string | undefined; enabled?: boolean }) =>
  useQuery(
    queryOptions({
      queryKey: getBeaconRunCoverageQueryKey(runId),
      queryFn: (): Promise<BeaconSummary | null> =>
        fetchBeaconResource({
          path: buildPath('/api/v1/ai-scans/:runId/coverage', { runId: runId ?? '' }),
          schema: BeaconSummarySchema,
          absentStatuses: [409],
        }),
      enabled: enabled && isLabsEnabled() && !!runId,
      staleTime: query => (query.state.data ? Infinity : 0),
      refetchInterval: query => (query.state.data === null ? BEACON_WAITING_RUN_POLL_MS : false),
    })
  )

/**
 * The latest run of each listed repository, keyed by repository id, in as few requests as the endpoint's
 * batch limit allows. Polls only while one of them is unfinished, and never with Labs off.
 */
export const useGetLatestBeaconRuns = ({ repositoryIds }: { repositoryIds: readonly string[] }) =>
  useQuery(
    queryOptions({
      queryKey: getLatestBeaconRunsQueryKey(repositoryIds),
      queryFn: async () => {
        const pages = await Promise.all(
          _.chunk([...repositoryIds].sort(), BEACON_LATEST_RUNS_BATCH_SIZE).map(batch =>
            fetchWrapperQuery({
              path: `/api/v1/ai-scans/latest?${new URLSearchParams(batch.map(id => ['repository-id', id]))}`,
              schema: BeaconRunListSchema,
            })
          )
        )
        return pages.flatMap(page => page.items)
      },
      // The cache holds the plain list: the query cache is persisted as JSON, which a Map does not survive.
      select: runs =>
        new Map(
          runs.flatMap(run => {
            const repositoryId = getBeaconRunRepositoryId(run)
            return repositoryId ? [[repositoryId, run] as const] : []
          })
        ),
      enabled: isLabsEnabled() && repositoryIds.length > 0,
      refetchInterval: query => getLatestBeaconRunsRefetchInterval(query.state.data),
    })
  )

/** Poll while any listed repository has an unfinished run; settle once every run has ended. */
export const getLatestBeaconRunsRefetchInterval = (runs: readonly BeaconRun[] | undefined) =>
  (runs ?? []).some(isBeaconRunUnfinished) ? BEACON_LATEST_RUNS_POLL_MS : false

/** One repository's latest run, or `null` before its first. */
export const useGetRepositoryLatestBeaconRun = ({
  repositoryId,
  enabled = true,
}: {
  repositoryId: string | undefined
  enabled?: boolean
}) =>
  useQuery(
    queryOptions({
      queryKey: getRepositoryLatestBeaconRunQueryKey(repositoryId),
      queryFn: () =>
        fetchBeaconResource({
          path: buildPath('/api/v1/repositories/:repositoryId/ai-scans/latest', { repositoryId: repositoryId ?? '' }),
          schema: BeaconRunSchema,
          absentStatuses: [404],
        }),
      enabled: enabled && isLabsEnabled() && !!repositoryId,
      refetchInterval: query => getBeaconRunRefetchInterval(query.state.data),
    })
  )

// The platform keeps the connection alive with comments, so a dropped name means analysis-service emits
// an event this build does not know yet; worth a warning so producer/consumer skew is visible, harmless to
// the run state either way.
const BEACON_DROPPED_EVENT_REPORTER = (eventName: string) => {
  console.warn(`Beacon stream: ignoring unknown event "${eventName}"`)
}

const BEACON_STREAM_GIVEN_UP_ERROR = new Error('The Beacon run stream kept closing before the run ended')

// Each frame's seq is its SSE id.
const highestBeaconSeqOf = ({ events }: SSEStream<BeaconStreamFrame>) =>
  events.reduce((highest, frame) => Math.max(highest, frame.seq), 0)

/**
 * A run's event stream, from the moment the run exists. The platform sends every event it recorded for
 * the run, each with its seq as its SSE id, then each new one as it is recorded, so the frames alone
 * rebuild the run for every viewer and every reload. A connection that closes while the run is unfinished
 * (a proxy timeout, a deploy) is reopened with `Last-Event-ID`, so the platform resumes after the last
 * frame seen; consumers still fold by `seq`, because the SSE hook's own retry after a network error
 * replays from the start. Reconnects that bring no frame past the highest seq seen back off, and after
 * `SSE_STREAM_MAX_IDLE_RECONNECTS` of them in a row the stream gives up and reports an error.
 *
 * The platform closes the stream once it has finished the run, at times with no terminal frame (a run
 * cancelled while queued has none, and one analysis-service lost ends without one), so a close is the
 * end of the stream once the run itself reads as finished. Pass `undefined` to stay idle.
 */
export const useBeaconRunStream = (runId: string | undefined): SSEStream<BeaconStreamFrame> => {
  const queryClient = useQueryClient()
  const { data: run } = useGetBeaconRun({ runId })
  return useReconnectingSSEStream({
    path: runId ? buildPath('/api/v1/ai-scans/:runId/events', { runId }) : undefined,
    schema: BeaconStreamFrameSchema,
    eventNames: BEACON_EVENT_NAMES,
    onDroppedEventName: BEACON_DROPPED_EVENT_REPORTER,
    isFinished: isTerminalBeaconFrame,
    progressOf: highestBeaconSeqOf,
    isRecordFinished: !!run && !isBeaconRunUnfinished(run),
    reread: () => void queryClient.invalidateQueries({ queryKey: getBeaconRunQueryKey(runId) }),
    givenUpError: BEACON_STREAM_GIVEN_UP_ERROR,
  })
}

// #endregion

// #region Feedback Hooks

/**
 * The request body, through the schema that describes it rather than assembled by hand, so a field
 * the form sends that the contract does not carry fails here rather than at the server.
 */
const feedbackSubmissionBody = ({ rating, scope, text, projectId }: Omit<FeedbackSubmissionVariables, 'resultId'>) =>
  CreateFeedbackSubmissionRequestSchema.parse({ rating, scope, text, project_id: projectId })

type FeedbackSubmissionVariables = {
  resultId: string
  rating: FeedbackRating
  text: string
  scope?: FeedbackScope
  /**
   * The project the form displayed, sent at project scope. If the repository moved under the open
   * form the server answers 409 rather than scoping the feedback to a project the author never saw.
   */
  projectId?: string
}

export const usePostTriageFeedbackSubmission = ({
  onSuccess,
  ...options
}: Omit<
  UseMutationOptions<FeedbackSubmissionResponse, DefaultError, FeedbackSubmissionVariables>,
  'mutationFn' | 'mutationKey'
> = {}) => {
  const _onSuccess = useCallback<Exclude<typeof onSuccess, undefined>>(
    (data, variables, onMutateResult, context) => {
      invalidateGetTriageFeedbackSubmissions(context.client, { resultId: variables.resultId })
      onSuccess?.(data, variables, onMutateResult, context)
    },
    [onSuccess]
  )

  return useMutation({
    mutationFn: ({ resultId, rating, text, scope, projectId }) =>
      fetchWrapperMutation({
        path: buildPath('/api/v1/triage-results/:resultId/feedback-submissions', { resultId }),
        options: { method: 'POST', body: JSON.stringify(feedbackSubmissionBody({ rating, scope, text, projectId })) },
        schema: FeedbackSubmissionResponseSchema,
        successStatusCodes: [201],
        deserializeFn: async response => (await response.json()) as unknown,
      }),
    onSuccess: _onSuccess,
    ...options,
  })
}

export const usePostFixFeedbackSubmission = ({
  onSuccess,
  ...options
}: Omit<
  UseMutationOptions<FeedbackSubmissionResponse, DefaultError, FeedbackSubmissionVariables>,
  'mutationFn' | 'mutationKey'
> = {}) => {
  const _onSuccess = useCallback<Exclude<typeof onSuccess, undefined>>(
    (data, variables, onMutateResult, context) => {
      invalidateGetFixFeedbackSubmissions(context.client, { resultId: variables.resultId })
      onSuccess?.(data, variables, onMutateResult, context)
    },
    [onSuccess]
  )

  return useMutation({
    mutationFn: ({ resultId, rating, text, scope, projectId }) =>
      fetchWrapperMutation({
        path: buildPath('/api/v1/fix-results/:resultId/feedback-submissions', { resultId }),
        options: { method: 'POST', body: JSON.stringify(feedbackSubmissionBody({ rating, scope, text, projectId })) },
        schema: FeedbackSubmissionResponseSchema,
        successStatusCodes: [201],
        deserializeFn: async response => (await response.json()) as unknown,
      }),
    onSuccess: _onSuccess,
    ...options,
  })
}

export const usePostScaFeedbackSubmission = ({
  onSuccess,
  ...options
}: Omit<
  UseMutationOptions<FeedbackSubmissionResponse, DefaultError, FeedbackSubmissionVariables>,
  'mutationFn' | 'mutationKey'
> = {}) => {
  const _onSuccess = useCallback<Exclude<typeof onSuccess, undefined>>(
    (data, variables, onMutateResult, context) => {
      invalidateGetScaFeedbackSubmissions(context.client, { resultId: variables.resultId })
      onSuccess?.(data, variables, onMutateResult, context)
    },
    [onSuccess]
  )

  return useMutation({
    mutationFn: ({ resultId, rating, text, scope, projectId }) =>
      fetchWrapperMutation({
        path: buildPath('/api/v1/sca-results/:resultId/feedback-submissions', { resultId }),
        options: { method: 'POST', body: JSON.stringify(feedbackSubmissionBody({ rating, scope, text, projectId })) },
        schema: FeedbackSubmissionResponseSchema,
        successStatusCodes: [201],
        deserializeFn: async response => (await response.json()) as unknown,
      }),
    onSuccess: _onSuccess,
    ...options,
  })
}

const getTriageFeedbackSubmissionsOptions = ({
  resultId,
  enabled = true,
  ...rest
}: Omit<UseQueryOptions<FeedbackSubmissionPaginatedResponse, ApiError>, 'queryKey' | 'queryFn'> & {
  resultId: string
}) =>
  queryOptions({
    queryKey: ['triage-feedback-submissions', resultId],
    queryFn: () =>
      fetchWrapperQuery({
        path: buildPath('/api/v1/triage-results/:resultId/feedback-submissions', { resultId }),
        schema: FeedbackSubmissionPaginatedResponseSchema,
      }),
    enabled: enabled && !!resultId,
    ...rest,
  })

export const useGetTriageFeedbackSubmissions = (options: Parameters<typeof getTriageFeedbackSubmissionsOptions>[0]) =>
  useQuery(getTriageFeedbackSubmissionsOptions(options))

export const invalidateGetTriageFeedbackSubmissions = (
  client: QueryClient,
  input: Parameters<typeof getTriageFeedbackSubmissionsOptions>[0]
) => client.invalidateQueries({ queryKey: getTriageFeedbackSubmissionsOptions(input).queryKey })

const getFixFeedbackSubmissionsOptions = ({
  resultId,
  enabled = true,
  ...rest
}: Omit<UseQueryOptions<FeedbackSubmissionPaginatedResponse, ApiError>, 'queryKey' | 'queryFn'> & {
  resultId: string
}) =>
  queryOptions({
    queryKey: ['fix-feedback-submissions', resultId],
    queryFn: () =>
      fetchWrapperQuery({
        path: buildPath('/api/v1/fix-results/:resultId/feedback-submissions', { resultId }),
        schema: FeedbackSubmissionPaginatedResponseSchema,
      }),
    enabled: enabled && !!resultId,
    ...rest,
  })

export const useGetFixFeedbackSubmissions = (options: Parameters<typeof getFixFeedbackSubmissionsOptions>[0]) =>
  useQuery(getFixFeedbackSubmissionsOptions(options))

export const invalidateGetFixFeedbackSubmissions = (
  client: QueryClient,
  input: Parameters<typeof getFixFeedbackSubmissionsOptions>[0]
) => client.invalidateQueries({ queryKey: getFixFeedbackSubmissionsOptions(input).queryKey })

const getScaFeedbackSubmissionsOptions = ({
  resultId,
  enabled = true,
  ...rest
}: Omit<UseQueryOptions<FeedbackSubmissionPaginatedResponse, ApiError>, 'queryKey' | 'queryFn'> & {
  resultId: string
}) =>
  queryOptions({
    queryKey: ['sca-feedback-submissions', resultId],
    queryFn: () =>
      fetchWrapperQuery({
        path: buildPath('/api/v1/sca-results/:resultId/feedback-submissions', { resultId }),
        schema: FeedbackSubmissionPaginatedResponseSchema,
      }),
    enabled: enabled && !!resultId,
    ...rest,
  })

export const useGetScaFeedbackSubmissions = (options: Parameters<typeof getScaFeedbackSubmissionsOptions>[0]) =>
  useQuery(getScaFeedbackSubmissionsOptions(options))

export const invalidateGetScaFeedbackSubmissions = (
  client: QueryClient,
  input: Parameters<typeof getScaFeedbackSubmissionsOptions>[0]
) => client.invalidateQueries({ queryKey: getScaFeedbackSubmissionsOptions(input).queryKey })

// #endregion

// #region Push

type PostPushVariables = { triageResultIds: string[] }

export const usePostPush = ({
  onSuccess,
  onError,
}: {
  onSuccess?: OnSuccess<CreatePushResponse, PostPushVariables>
  onError?: OnError<CreatePushResponse, PostPushVariables>
} = {}) =>
  useMutation({
    mutationFn: async (variables: PostPushVariables) => {
      const body = { triage_result_ids: variables.triageResultIds }
      return fetchWrapperMutation({
        path: '/api/v1/pushes',
        options: {
          method: 'POST',
          body: JSON.stringify(body),
        },
        schema: CreatePushResponseSchema,
        successStatusCodes: [202],
        deserializeFn: async response => (await response.json()) as unknown,
      })
    },
    onSuccess,
    onError,
  })

type PolarisStatusLookupVariables = { triageResultIds: string[] }

/**
 * How long a displayed Polaris status is treated as current.
 *
 * Only applies to the read-only surfaces below. The push preflight never reads this cache.
 */
const POLARIS_STATUS_STALE_TIME_MS = 30_000

const POLARIS_STATUSES_QUERY_KEY_ROOT = 'polaris-statuses'

/**
 * Drops every cached Polaris status.
 *
 * A completed push is what changes the status these queries report, so its cache must not outlive
 * one. Exported as a helper rather than as a key prefix so that reason lives here, next to the
 * queries it invalidates, rather than in each component that pushes.
 */
export const invalidateGetPolarisStatuses = (client: QueryClient) =>
  client.invalidateQueries({ queryKey: [POLARIS_STATUSES_QUERY_KEY_ROOT] })

const lookupPolarisStatuses = async ({
  triageResultIds,
  includeAttribution = false,
}: {
  triageResultIds: string[]
  includeAttribution?: boolean
}) =>
  fetchWrapperMutation({
    // Only sent when asking for attribution: the endpoint already defaults it to false.
    path: `/api/v1/pushes/polaris-status${includeAttribution ? `?${new URLSearchParams({ include_attribution: 'true' })}` : ''}`,
    options: {
      method: 'POST',
      body: JSON.stringify({ triage_result_ids: triageResultIds } satisfies CreatePushRequest),
    },
    schema: PolarisStatusLookupResponseSchema,
    successStatusCodes: [200],
    deserializeFn: async response => (await response.json()) as unknown,
  })

/**
 * Reads the live Polaris triage status for the given targets, on demand.
 *
 * A mutation rather than a query because the result gates a write: the push confirmation has to
 * reflect Polaris as it is at push time, and a cached answer would let a status that changed since
 * the page loaded pass unchallenged. Use `useGetPolarisStatuses` for the read-only surfaces where a
 * cached answer is what you want.
 */
export const usePostPolarisStatusLookup = (
  options: Omit<
    UseMutationOptions<PolarisStatusLookupResponse, DefaultError, PolarisStatusLookupVariables>,
    'mutationFn'
  > = {}
) => {
  const queryClient = useQueryClient()
  return useMutation({
    // No attribution: this gates a push on the conflict count, and asking for who dismissed each
    // finding costs a Polaris request per conflict — a bulk push would pay it for nothing.
    mutationFn: async (variables: PolarisStatusLookupVariables) =>
      lookupPolarisStatuses({ triageResultIds: variables.triageResultIds }),
    // This uncached read is the more recent of the two, so anything the cached queries are still
    // showing is superseded by it — including a "match" that has since become a conflict, which is
    // the case the tooltip most needs to stop repeating.
    onSuccess: () => invalidateGetPolarisStatuses(queryClient),
    ...options,
  })
}

/**
 * Query options for displaying Polaris status without intending to push.
 *
 * Deduplicated and cached per set of triage result IDs, so re-reaching the same tooltip does not
 * re-request. IDs asked for one at a time each get their own cache entry, which is what the badge does
 * — it cannot see its siblings. That is affordable only because the badge gates the lookup on hover,
 * so a table of findings has one request in flight rather than one per row. A surface that wants
 * statuses for many targets up front should batch them here and pass the results down instead.
 *
 * Requests attribution, unlike the push preflight: this feeds a tooltip that names who dismissed the
 * finding and when, and it asks about one finding at a time, so the per-conflict cost is one request.
 */
const getPolarisStatusesOptions = ({
  triageResultIds,
  enabled = true,
  ...rest
}: Omit<UseQueryOptions<PolarisStatusLookupResponse, ApiError>, 'queryKey' | 'queryFn'> & {
  triageResultIds: string[]
}) => {
  // Sorted for the request as well as for the key: two callers passing the same ids in a different
  // order share one cache entry, so the payload's order has to be the key's order.
  const sortedTriageResultIds = [...triageResultIds].sort()
  return queryOptions({
    queryKey: [POLARIS_STATUSES_QUERY_KEY_ROOT, sortedTriageResultIds],
    queryFn: () => lookupPolarisStatuses({ triageResultIds: sortedTriageResultIds, includeAttribution: true }),
    enabled: enabled && triageResultIds.length > 0,
    staleTime: POLARIS_STATUS_STALE_TIME_MS,
    ...rest,
  })
}

export const useGetPolarisStatuses = (options: Parameters<typeof getPolarisStatusesOptions>[0]) =>
  useQuery(getPolarisStatusesOptions(options))

const getPushesByIdsOptions = ({ ids, enabled = true }: { ids: string[]; enabled?: boolean }) => {
  const isActive = enabled && ids.length > 0
  return {
    queryKey: ['pushesByIds', [...ids].sort()],
    queryFn: async () => {
      return fetchWrapperQuery({
        path: `/api/v1/pushes?${ids.map(id => `ids=${encodeURIComponent(id)}`).join('&')}`,
        schema: GetPushesResponseSchema,
        deserializeFn: async response => (await response.json()) as unknown,
      })
    },
    enabled: isActive,
    refetchInterval: (query: { state: { data: GetPushesResponse | undefined } }) => {
      if (!isActive) return false as const
      const data = query.state.data
      if (!data) return 1000
      const allTerminal = data.pushes.every(push => push.status === 'SUCCESS' || push.status === 'FAILED')
      return allTerminal ? (false as const) : 1000
    },
  }
}

export const useGetPushesByIds = (input: { ids: string[]; enabled?: boolean }) => useQuery(getPushesByIdsOptions(input))

// #region Generation resources (threat model, exploit chains)

/**
 * Builds the shared hook set for a repository-scoped, on-demand "generation" resource — one that is
 * created by POST, polled/streamed while `processing`, and read back as metadata. Threat Model and
 * Exploit Chains are the same resource shape end to end; this factory is the single source of truth
 * for their polling cadence, query keys, 404→null handling, and SSE wiring so the two can never
 * silently drift.
 *
 * The `/content` sub-resource is deliberately *not* built here: its representation differs per
 * resource (threat-model and finding-overlays return structured JSON, each with its own schema), so
 * every consumer owns a bespoke content hook next to its region below.
 */
const createGenerationResourceHooks = <
  TResponse extends { status: string },
  TStreamEvent extends { status: string },
>(config: {
  /** Query-key prefix, e.g. `repository-threat-model`. */
  queryKeyBase: string
  /** Path segment under `/repositories/:repositoryId/`, e.g. `threat-model`. */
  pathSegment: string
  responseSchema: z.ZodType<TResponse>
  streamEventSchema: z.ZodType<TStreamEvent>
  /** Phase enum options; the SSE event names are these phases plus the terminal statuses. */
  phaseOptions: readonly string[]
  /**
   * Status enum options. Every status that isn't in flight (see `isInFlightGenerationStatus`) is
   * terminal: its same-named SSE event ends the stream, and the poll stops on it.
   */
  statusOptions: readonly TStreamEvent['status'][]
  /** Additional named progress events the stream consumes beyond phase transitions. */
  progressEventNames?: readonly string[]
  /**
   * Metadata poll interval. Generation takes minutes and SSE is the primary live channel, so the
   * poll is a slow fallback/reconnect mechanism rather than the main progress driver.
   */
  pollIntervalMs?: number
}) => {
  const pollIntervalMs = config.pollIntervalMs ?? 10_000
  const terminalStatuses = config.statusOptions.filter(status => !isInFlightGenerationStatus(status))
  // eslint-disable-next-line pixee/no-unescaped-path-params -- pathSegment is a static internal route segment (not user input); buildPath still encodes :repositoryId.
  const basePath = `/api/v1/repositories/:repositoryId/${config.pathSegment}` as const
  // The stream's SSE event names are the phases for progress events plus the terminal statuses;
  // anything else (e.g. periodic `keepalive` events) is dropped silently.
  const streamEventNames = [...config.phaseOptions, ...(config.progressEventNames ?? []), ...terminalStatuses]

  const getRefetchInterval = (data: TResponse | null | undefined): number | false =>
    data && isInFlightGenerationStatus(data.status) ? pollIntervalMs : false

  const getOptions = ({
    repositoryId,
    enabled = true,
    ...rest
  }: {
    repositoryId: string
    enabled?: boolean
  } & Omit<UseQueryOptions<TResponse | null, ApiError>, 'queryKey' | 'queryFn'>) =>
    // eslint-disable-next-line @tanstack/query/exhaustive-deps -- queryKeyBase uniquely identifies the resource's path + schema (stable per factory instance)
    queryOptions({
      queryKey: [config.queryKeyBase, repositoryId],
      queryFn: () =>
        fetchWrapperQuery({
          path: buildPath(basePath, { repositoryId }),
          schema: z.union([config.responseSchema, z.null()]),
          successStatusCodes: [200, 404],
          // A 404 means the resource has never been generated; surface it as `null` rather than an
          // error. While `processing`, the query polls until the status reaches a terminal state.
          deserializeFn: async (response: Response) => {
            if (response.status === 404) {
              return null
            }

            return (await response.json()) as unknown
          },
        }),
      refetchInterval: query => getRefetchInterval(query.state.data),
      enabled: enabled && !!repositoryId,
      ...rest,
    })

  const useGet = (input: Parameters<typeof getOptions>[0]) => useQuery(getOptions(input))

  const invalidateGet = (client: QueryClient, input: Parameters<typeof getOptions>[0]) =>
    client.invalidateQueries({ queryKey: getOptions(input).queryKey })

  type PostVariables = { repositoryId: string }

  const usePost = ({
    onSuccess,
    onError,
  }: {
    onSuccess?: OnSuccess<TResponse, PostVariables>
    onError?: OnError<TResponse, PostVariables>
  } = {}) =>
    useMutation({
      mutationFn: (variables: PostVariables) =>
        fetchWrapperMutation({
          path: buildPath(basePath, { repositoryId: variables.repositoryId }),
          options: { method: 'POST' },
          schema: config.responseSchema,
          successStatusCodes: [202],
          deserializeFn: async response => (await response.json()) as unknown,
        }),
      onSuccess,
      onError,
    })

  /**
   * Streams progress events for an in-flight generation. The stream replays the persisted event log
   * from the start (idempotently — a stream opened against an already-terminal run replays history
   * plus the terminal event and closes) and live-tails until an event for one of the resource's
   * terminal statuses. Pass `undefined` while no generation is in flight to keep the stream idle.
   * GET polling remains the fallback/reconnect source of truth; a 404 simply marks it unavailable.
   */
  const useStream = (repositoryId: string | undefined): SSEStream<TStreamEvent> =>
    useSSEStream({
      url: repositoryId ? buildPath(`${basePath}/events`, { repositoryId }) : undefined,
      schema: config.streamEventSchema,
      isFinished: event => !isInFlightGenerationStatus(event.status),
      eventNames: streamEventNames,
    })

  return {
    pollIntervalMs,
    getRefetchInterval,
    getOptions,
    useGet,
    invalidateGet,
    usePost,
    useStream,
  }
}

// #endregion

// #region Threat Model

const threatModelHooks = createGenerationResourceHooks<ThreatModelResponse, ThreatModelStreamEvent>({
  queryKeyBase: 'repository-threat-model',
  pathSegment: 'threat-model',
  responseSchema: ThreatModelResponseSchema,
  streamEventSchema: ThreatModelStreamEventSchema,
  phaseOptions: ThreatModelPhaseSchema.options,
  statusOptions: ThreatModelStatusSchema.options,
})

export const THREAT_MODEL_POLL_INTERVAL_MS = threatModelHooks.pollIntervalMs
export const getThreatModelRefetchInterval = threatModelHooks.getRefetchInterval
/**
 * Extracted for `useQueries`, the same reason as `getRepositoryThreatModelContentOptions` below:
 * the org-level posture view needs each model's `generatedAt` and there is no aggregate endpoint.
 *
 * Callers fanning this out across an estate **must** override `refetchInterval` to `false`. The options
 * poll every 10s while a generation is in flight, which is right for one repository's page and would
 * leave an org page polling once per repository for as long as it is open.
 */
export const getRepositoryThreatModelQueryOptions = threatModelHooks.getOptions
export const useGetRepositoryThreatModel = threatModelHooks.useGet
export const invalidateGetRepositoryThreatModel = threatModelHooks.invalidateGet
export const usePostRepositoryThreatModel = threatModelHooks.usePost
export const useThreatModelStream = threatModelHooks.useStream

// The `/content` endpoint negotiates on `Accept`: `application/json` returns the structured
// `model_json` document (what we render), while the legacy default is `text/markdown`. We send the
// explicit JSON header so the backend never falls back to markdown.
const getRepositoryThreatModelContentQueryKey = (repositoryId: string) =>
  ['repository-threat-model-content', repositoryId] as const

type GetRepositoryThreatModelContentOptionsInput = {
  repositoryId: string
  enabled?: boolean
} & Omit<UseQueryOptions<ThreatModelContent, ApiError>, 'queryKey' | 'queryFn'>

/**
 * The key is deliberately just the repository — **`generatedAt` is not part of it.** Keying on the
 * timestamp made the cache self-invalidating for whoever passed it and left everyone who didn't on a
 * separate, never-refreshed entry: the repositories page's risk score kept weighting by the previous
 * document's exposure while that entry stayed warm. Regeneration now invalidates through
 * {@link invalidateGetRepositoryThreatModelContent}, which is where invalidation belongs.
 *
 * Exported so a caller needing its own `combine` can build the queries itself. The org posture hook
 * does: its combine closes over the repository list to name each entry, so it cannot be the
 * module-scope function {@link useGetEachRepositoryThreatModelContent} requires.
 */
export const getRepositoryThreatModelContentOptions = ({
  repositoryId,
  enabled = true,
  ...rest
}: GetRepositoryThreatModelContentOptionsInput) =>
  queryOptions({
    queryKey: getRepositoryThreatModelContentQueryKey(repositoryId),
    queryFn: () =>
      fetchWrapperQuery({
        path: buildPath('/api/v1/repositories/:repositoryId/threat-model/content', { repositoryId }),
        options: { headers: { 'X-Requested-With': 'JavaScript', Accept: 'application/json' } },
        schema: ThreatModelContentSchema,
        successStatusCodes: [200],
      }),
    enabled: enabled && !!repositoryId,
    ...rest,
  })

export const useGetRepositoryThreatModelContent = (input: GetRepositoryThreatModelContentOptionsInput) =>
  useQuery(getRepositoryThreatModelContentOptions(input))

/**
 * Fans the content query across an estate — the org-level posture view and the repositories page's
 * risk column have no aggregate endpoint and have to ask per repository.
 *
 * `combine` is forwarded to `useQueries`, not spread into each query. Pass a **module-scope** function:
 * react-query memoises the combined result against the callback's identity, so an inline arrow gives a
 * new identity every render, which yields a fresh object, which changes any dependency array it feeds.
 */
export const useGetEachRepositoryThreatModelContent = <TCombined = UseQueryResult<ThreatModelContent, ApiError>[]>({
  repositoryIds,
  combine,
  ...rest
}: Omit<GetRepositoryThreatModelContentOptionsInput, 'repositoryId'> & {
  repositoryIds: string[]
  combine?: (results: UseQueryResult<ThreatModelContent, ApiError>[]) => TCombined
}) =>
  useQueries({
    queries: repositoryIds.map(repositoryId => getRepositoryThreatModelContentOptions({ repositoryId, ...rest })),
    combine,
  })

export const invalidateGetRepositoryThreatModelContent = (
  client: QueryClient,
  { repositoryId }: { repositoryId: string }
) => client.invalidateQueries({ queryKey: getRepositoryThreatModelContentQueryKey(repositoryId) })

// #endregion

// #region Exploit Chains

const exploitChainsHooks = createGenerationResourceHooks<ExploitChainsResponse, ExploitChainsStreamEvent>({
  queryKeyBase: 'repository-exploit-chains',
  pathSegment: 'exploit-chains',
  responseSchema: ExploitChainsResponseSchema,
  streamEventSchema: ExploitChainsStreamEventSchema,
  phaseOptions: ExploitChainsPhaseSchema.options,
  statusOptions: ExploitChainsStatusSchema.options,
  progressEventNames: ExploitChainsProgressEventNameSchema.options,
})

export const EXPLOIT_CHAINS_POLL_INTERVAL_MS = exploitChainsHooks.pollIntervalMs
export const getExploitChainsRefetchInterval = exploitChainsHooks.getRefetchInterval
export const useGetRepositoryExploitChains = exploitChainsHooks.useGet
export const invalidateGetRepositoryExploitChains = exploitChainsHooks.invalidateGet
export const usePostRepositoryExploitChains = exploitChainsHooks.usePost
export const useExploitChainsStream = exploitChainsHooks.useStream

// #endregion

// #region Finding Overlays

const findingOverlaysHooks = createGenerationResourceHooks<FindingOverlaysResponse, FindingOverlaysStreamEvent>({
  queryKeyBase: 'repository-finding-overlays',
  pathSegment: 'finding-overlays',
  responseSchema: FindingOverlaysResponseSchema,
  streamEventSchema: FindingOverlaysStreamEventSchema,
  phaseOptions: FindingOverlayPhaseSchema.options,
  statusOptions: FindingOverlayStatusSchema.options,
})

export const FINDING_OVERLAYS_POLL_INTERVAL_MS = findingOverlaysHooks.pollIntervalMs
export const getFindingOverlaysRefetchInterval = findingOverlaysHooks.getRefetchInterval
export const useGetRepositoryFindingOverlays = findingOverlaysHooks.useGet
export const invalidateGetRepositoryFindingOverlays = findingOverlaysHooks.invalidateGet
export const usePostRepositoryFindingOverlays = findingOverlaysHooks.usePost
export const useFindingOverlaysStream = findingOverlaysHooks.useStream

// finding-overlays /content returns structured JSON (narrative + priorities + clusters +
// assumptions) with its own schema, so — like threat-model above — it owns a bespoke content hook
// rather than the shared factory. `generatedAt` participates in the query key so a regeneration
// invalidates the cached content.
export const useGetRepositoryFindingOverlaysContent = ({
  repositoryId,
  generatedAt,
  enabled = true,
  ...rest
}: {
  repositoryId: string
  generatedAt?: string | null
  enabled?: boolean
} & Omit<UseQueryOptions<FindingOverlayResponse, ApiError>, 'queryKey' | 'queryFn'>) =>
  useQuery({
    queryKey: ['repository-finding-overlays-content', repositoryId, generatedAt ?? null],
    queryFn: () =>
      fetchWrapperQuery({
        path: buildPath('/api/v1/repositories/:repositoryId/finding-overlays/content', { repositoryId }),
        // Force JSON: the backend content-negotiates and otherwise falls back to markdown. Mirrors
        // `useGetRepositoryThreatModelContent`.
        options: { headers: { 'X-Requested-With': 'JavaScript', Accept: 'application/json' } },
        schema: FindingOverlayResponseSchema,
        successStatusCodes: [200],
      }),
    enabled: enabled && !!repositoryId,
    ...rest,
  })

// #endregion

// #region Scorecards

/**
 * Scorecards mirror the threat-model / exploit-chains lifecycle (POST to (re)generate, poll GET
 * while `processing`, terminal on `completed`/`failed`) but the backend deliberately exposes no
 * SSE stream and no content sub-resource — the full report ships inline on the GET. So this uses
 * a bespoke poll-only pair of hooks rather than the shared `createGenerationResourceHooks`
 * factory, which is tuned to the stream + content sub-resource shape.
 */
export const SCORECARDS_POLL_INTERVAL_MS = 10_000
const SCORECARDS_BASE_PATH = '/api/v1/repositories/:repositoryId/scorecards' as const

export const getScorecardsRefetchInterval = (data: ScorecardsResponse | null | undefined): number | false =>
  data?.status === 'processing' ? SCORECARDS_POLL_INTERVAL_MS : false

const getRepositoryScorecardsOptions = ({
  repositoryId,
  enabled = true,
  ...rest
}: {
  repositoryId: string
  enabled?: boolean
} & Omit<UseQueryOptions<ScorecardsResponse | null, ApiError>, 'queryKey' | 'queryFn'>) =>
  queryOptions({
    queryKey: ['repository-scorecards', repositoryId],
    queryFn: () =>
      fetchWrapperQuery({
        path: buildPath(SCORECARDS_BASE_PATH, { repositoryId }),
        schema: z.union([ScorecardsResponseSchema, z.null()]),
        successStatusCodes: [200, 404],
        // A 404 means the resource has never been generated; surface it as `null` rather than
        // an error. While `processing`, the query polls until the status reaches terminal.
        deserializeFn: async (response: Response) => {
          if (response.status === 404) {
            return null
          }
          return (await response.json()) as unknown
        },
      }),
    refetchInterval: query => getScorecardsRefetchInterval(query.state.data),
    enabled: enabled && !!repositoryId,
    ...rest,
  })

/**
 * Extracted for `useQueries`, the same reason as `getRepositoryThreatModelQueryOptions`: the org-level
 * posture view needs every repository's scorecard and there is no aggregate endpoint.
 *
 * Callers fanning this out across an estate **must** override `refetchInterval` to `false`. These options
 * poll while a scorecard is `processing`, which is right for one repository's page and would leave an org
 * page polling once per repository for as long as it is open.
 */
export const getRepositoryScorecardsQueryOptions = getRepositoryScorecardsOptions

export const useGetRepositoryScorecards = (input: Parameters<typeof getRepositoryScorecardsOptions>[0]) =>
  useQuery(getRepositoryScorecardsOptions(input))

/**
 * Fans the scorecards query across an estate, for the repositories page's risk column. Each query keeps
 * its own `refetchInterval`, so a repository still generating polls to completion on its own.
 *
 * See {@link useGetEachRepositoryThreatModelContent} on why `combine` must be module-scope.
 */
export const useGetEachRepositoryScorecards = <TCombined = UseQueryResult<ScorecardsResponse | null, ApiError>[]>({
  repositoryIds,
  combine,
  ...rest
}: Omit<Parameters<typeof getRepositoryScorecardsOptions>[0], 'repositoryId'> & {
  repositoryIds: string[]
  combine?: (results: UseQueryResult<ScorecardsResponse | null, ApiError>[]) => TCombined
}) =>
  useQueries({
    queries: repositoryIds.map(repositoryId => getRepositoryScorecardsOptions({ repositoryId, ...rest })),
    combine,
  })

export const invalidateGetRepositoryScorecards = (
  client: QueryClient,
  input: Parameters<typeof getRepositoryScorecardsOptions>[0]
) => client.invalidateQueries({ queryKey: getRepositoryScorecardsOptions(input).queryKey })

type PostRepositoryScorecardsVariables = { repositoryId: string }

export const usePostRepositoryScorecards = ({
  onSuccess,
  onError,
}: {
  onSuccess?: OnSuccess<ScorecardsResponse, PostRepositoryScorecardsVariables>
  onError?: OnError<ScorecardsResponse, PostRepositoryScorecardsVariables>
} = {}) =>
  useMutation({
    mutationFn: (variables: PostRepositoryScorecardsVariables) =>
      fetchWrapperMutation({
        path: buildPath(SCORECARDS_BASE_PATH, { repositoryId: variables.repositoryId }),
        options: { method: 'POST' },
        schema: ScorecardsResponseSchema,
        successStatusCodes: [202],
        deserializeFn: async response => (await response.json()) as unknown,
      }),
    onSuccess,
    onError,
  })

// #endregion

// #region Opengrep Rules

const openGrepRulesHooks = createGenerationResourceHooks<OpenGrepRulesResponse, OpenGrepRulesStreamEvent>({
  queryKeyBase: 'repository-opengrep-rules',
  pathSegment: 'opengrep-rules',
  responseSchema: OpenGrepRulesResponseSchema,
  streamEventSchema: OpenGrepRulesStreamEventSchema,
  phaseOptions: OpenGrepRulesPhaseSchema.options,
  statusOptions: OpenGrepRulesStatusSchema.options,
})

export const OPENGREP_RULES_POLL_INTERVAL_MS = openGrepRulesHooks.pollIntervalMs
export const getOpenGrepRulesRefetchInterval = openGrepRulesHooks.getRefetchInterval
export const useGetRepositoryOpenGrepRules = openGrepRulesHooks.useGet
export const invalidateGetRepositoryOpenGrepRules = openGrepRulesHooks.invalidateGet
export const usePostRepositoryOpenGrepRules = openGrepRulesHooks.usePost
export const useOpenGrepRulesStream = openGrepRulesHooks.useStream

export const useGetRepositoryOpenGrepRulesContent = ({
  repositoryId,
  generatedAt,
  enabled = true,
  ...rest
}: {
  repositoryId: string
  generatedAt?: string | null
  enabled?: boolean
} & Omit<UseQueryOptions<OpenGrepRuleSet, ApiError>, 'queryKey' | 'queryFn'>) =>
  useQuery({
    queryKey: ['repository-opengrep-rules-content', repositoryId, generatedAt ?? null],
    queryFn: () =>
      fetchWrapperQuery({
        path: buildPath('/api/v1/repositories/:repositoryId/opengrep-rules/content', { repositoryId }),
        options: { headers: { 'X-Requested-With': 'JavaScript', Accept: 'application/json' } },
        schema: OpenGrepRuleSetSchema,
        successStatusCodes: [200],
      }),
    enabled: enabled && !!repositoryId,
    ...rest,
  })

export type { OpenGrepRule, OpenGrepRuleSet }

// #endregion

const getCodingGuidelinesOptions = ({
  organizationId = 'default-organization',
  enabled = true,
  ...rest
}: {
  organizationId?: string
  enabled?: boolean
} & Omit<UseQueryOptions<CodingGuidelinesResponse[], ApiError>, 'queryKey' | 'queryFn'> = {}) =>
  queryOptions({
    queryKey: ['coding-guidelines', organizationId],
    queryFn: () =>
      fetchWrapperQuery({
        path: buildPath('/api/v1/organizations/:organizationId/coding-guidelines', { organizationId }),
        schema: CodingGuidelinesResponseSchema.array(),
        successStatusCodes: [200],
      }),
    enabled,
    ...rest,
  })

export const useGetCodingGuidelines = (input?: Parameters<typeof getCodingGuidelinesOptions>[0]) =>
  useQuery(getCodingGuidelinesOptions(input))

const uploadWithProgress = ({
  path,
  formData,
  onProgress,
}: {
  path: string
  formData: FormData
  onProgress: (progressPercent: number) => unknown
}): Promise<CodingGuidelinesResponse> =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.upload.addEventListener('progress', event => {
      if (event.lengthComputable) {
        onProgress(Math.round((event.loaded / event.total) * 100))
      }
    })
    xhr.addEventListener('load', () => {
      if (xhr.status === 201) {
        try {
          resolve(CodingGuidelinesResponseSchema.parse(JSON.parse(xhr.responseText) as unknown))
        } catch {
          reject(new ApiError(xhr.status, xhr.responseText, path))
        }
      } else {
        reject(new ApiError(xhr.status, xhr.responseText, path))
      }
    })
    xhr.addEventListener('error', () => reject(new ApiError(0, 'Network error', path)))
    xhr.open('POST', path)
    xhr.setRequestHeader('X-Requested-With', 'JavaScript')
    xhr.setRequestHeader('Accept', 'application/hal+json, application/json')
    xhr.send(formData)
  })

export const usePostCodingGuideline = ({
  organizationId = 'default-organization',
  onSuccess,
  ...options
}: {
  organizationId?: string
} & Omit<
  UseMutationOptions<
    CodingGuidelinesResponse,
    DefaultError,
    { displayName: string; file: File; onUploadProgress?: (progressPercent: number) => unknown }
  >,
  'mutationFn'
> = {}) => {
  const _onSuccess = useCallback<Exclude<typeof onSuccess, undefined>>(
    (data, variables, onMutateResult, context) => {
      context.client.invalidateQueries({ queryKey: getCodingGuidelinesOptions({ organizationId }).queryKey })
      onSuccess?.(data, variables, onMutateResult, context)
    },
    [organizationId, onSuccess]
  )

  return useMutation({
    mutationFn: async ({
      displayName,
      file,
      onUploadProgress,
    }: {
      displayName: string
      file: File
      onUploadProgress?: (progressPercent: number) => unknown
    }) => {
      const path = buildPath('/api/v1/organizations/:organizationId/coding-guidelines', { organizationId })
      const formData = new FormData()
      formData.append('display_name', displayName)
      formData.append('file', file)
      if (onUploadProgress) {
        return uploadWithProgress({ path, formData, onProgress: onUploadProgress })
      }
      return fetchWrapperMutation({
        path,
        options: { method: 'POST', body: formData, headers: { 'X-Requested-With': 'JavaScript' } },
        schema: CodingGuidelinesResponseSchema,
        successStatusCodes: [201],
        deserializeFn: response => response.json(),
      })
    },
    onSuccess: _onSuccess,
    ...options,
  })
}

// Multipart body for a freeform upload session: one submit carries many files, each appended as a
// `files` part (mirroring createScanFormData's multi-file loop) so the org-scoped endpoint receives
// the whole corpus at once.
const createFreeformUploadFormData = (files: File[]): FormData => {
  const formData = new FormData()
  files.forEach(file => {
    formData.append('files', file)
  })
  return formData
}

/** How often an unfinished Findings Intake run is re-read: a backstop to its stream, which carries the progress. */
export const FREEFORM_RUNNING_SESSION_POLL_MS = 15_000

/** The runs the Findings Intake tab lists at once. */
export const FREEFORM_SESSIONS_PAGE_SIZE = 10

const FREEFORM_QUERY_KEY_ROOT = 'freeform'

/** Refresh every Findings Intake run snapshot: the tab's list and each run page. */
export const invalidateFreeformUploadSessions = (client: QueryClient) =>
  client.invalidateQueries({ queryKey: [FREEFORM_QUERY_KEY_ROOT] })

/** Whether a run has yet to complete or fail. */
export const isFreeformUploadSessionUnfinished = (session: Pick<FreeformUploadSessionResponse, 'status'> | undefined) =>
  session !== undefined &&
  match(session.status)
    .with('parsing', 'associating', () => true)
    .with('completed', 'failed', () => false)
    .exhaustive()

/** The platform fails a run its instance stopped working on once the run has been unchanged this long. */
const FREEFORM_PLATFORM_ORPHAN_AFTER_MS = 40 * 60_000

/** How often the platform looks for such runs. */
const FREEFORM_PLATFORM_ORPHAN_SWEEP_MS = 5 * 60_000

/** Headroom for this browser's clock running ahead of the platform's, which `updated_at` comes from. */
const FREEFORM_CLOCK_SKEW_MARGIN_MS = 5 * 60_000

/**
 * How long an unfinished run may go without the platform recording a change before the page stops waiting
 * on it. A backstop past the platform's own sweep, plus one poll to read its result and a margin for clock
 * skew, so the failure the platform records arrives first.
 */
export const FREEFORM_RUN_REPORTING_DEADLINE_MS =
  FREEFORM_PLATFORM_ORPHAN_AFTER_MS +
  FREEFORM_PLATFORM_ORPHAN_SWEEP_MS +
  FREEFORM_RUNNING_SESSION_POLL_MS +
  FREEFORM_CLOCK_SKEW_MARGIN_MS

/**
 * Whether an unfinished run has gone quiet past its deadline, so it will never report again. A run whose
 * `updated_at` does not parse is never called stalled; it keeps being re-read.
 */
export const isFreeformUploadSessionStalled = (
  session: Pick<FreeformUploadSessionResponse, 'status' | 'updated_at'> | undefined,
  now = nowMs()
) => {
  if (!isFreeformUploadSessionUnfinished(session)) {
    return false
  }
  const updatedMs = session ? isoToMs(session.updated_at) : undefined
  return updatedMs !== undefined && now - updatedMs > FREEFORM_RUN_REPORTING_DEADLINE_MS
}

const isFreeformUploadSessionPolled = (
  session: Pick<FreeformUploadSessionResponse, 'status' | 'updated_at'> | undefined,
  now = nowMs()
) => isFreeformUploadSessionUnfinished(session) && !isFreeformUploadSessionStalled(session, now)

/** Re-read a run while it is still going; a finished or stalled one never changes again. */
export const getFreeformUploadSessionRefetchInterval = (
  session: Pick<FreeformUploadSessionResponse, 'status' | 'updated_at'> | undefined,
  now = nowMs()
) => (isFreeformUploadSessionPolled(session, now) ? FREEFORM_RUNNING_SESSION_POLL_MS : (false as const))

/** Re-read the list while any run on it is still going. */
export const getFreeformUploadSessionsRefetchInterval = (
  sessions: readonly Pick<FreeformUploadSessionResponse, 'status' | 'updated_at'>[],
  now = nowMs()
) =>
  sessions.some(session => isFreeformUploadSessionPolled(session, now))
    ? FREEFORM_RUNNING_SESSION_POLL_MS
    : (false as const)

/** No such run, or Labs off: an answer the run's page shows, not a failure to retry or report. */
export const isFreeformUploadSessionNotFound = (error: unknown) => error instanceof ApiError && error.status === 404

const getFreeformUploadSessionOptions = ({
  organizationId = 'default-organization',
  sessionId,
  ...rest
}: {
  organizationId?: string
  sessionId: string | undefined
} & Omit<UseQueryOptions<FreeformUploadSessionResponse, Error>, 'queryKey' | 'queryFn'>) =>
  queryOptions({
    ...inlineAnswerQueryPolicy(isFreeformUploadSessionNotFound),
    queryKey: [FREEFORM_QUERY_KEY_ROOT, 'session', organizationId, sessionId],
    queryFn: () =>
      fetchWrapperQuery({
        path: buildPath('/api/v1/organizations/:organizationId/freeform-uploads/:sessionId', {
          organizationId,
          sessionId: sessionId ?? '',
        }),
        schema: FreeformUploadSessionResponseSchema,
      }),
    enabled: isLabsEnabled() && !!sessionId,
    refetchInterval: query => getFreeformUploadSessionRefetchInterval(query.state.data),
    // The run's page is often left in a background tab while a long run works; it should have caught up
    // when the viewer comes back, and the stall deadline is measured against what it last read.
    refetchIntervalInBackground: true,
    ...rest,
  })

/** One Findings Intake run. A 404 is answered at once; an unfinished run is re-read until it ends. */
export const useGetFreeformUploadSession = (input: Parameters<typeof getFreeformUploadSessionOptions>[0]) =>
  useQuery(getFreeformUploadSessionOptions(input))

const getFreeformUploadSessionsOptions = ({
  organizationId = 'default-organization',
  pageIndex = 0,
  ...rest
}: {
  organizationId?: string
  pageIndex?: number
} & Omit<UseQueryOptions<FreeformUploadSessionPaginatedResponse, Error>, 'queryKey' | 'queryFn'> = {}) =>
  queryOptions({
    queryKey: [FREEFORM_QUERY_KEY_ROOT, 'sessions', organizationId, pageIndex],
    queryFn: () =>
      fetchWrapperQuery({
        path: `${buildPath('/api/v1/organizations/:organizationId/freeform-uploads', { organizationId })}?${createPageParams(pageIndex, FREEFORM_SESSIONS_PAGE_SIZE)}`,
        schema: FreeformUploadSessionPaginatedResponseSchema,
      }),
    enabled: isLabsEnabled(),
    // Unlike a run's page, the list only polls while visible: it is a glance at recent runs, and it catches
    // up on focus.
    refetchInterval: query => getFreeformUploadSessionsRefetchInterval(query.state.data?._embedded.items ?? []),
    ...rest,
  })

/** One page of the organization's Findings Intake runs, newest first; polls while any of them is unfinished. */
export const useGetFreeformUploadSessions = (input?: Parameters<typeof getFreeformUploadSessionsOptions>[0]) =>
  useQuery(getFreeformUploadSessionsOptions(input))

export const usePostFreeformUpload = ({
  organizationId = 'default-organization',
  onSuccess,
  ...options
}: {
  organizationId?: string
} & Omit<UseMutationOptions<FreeformUploadResponse, DefaultError, { files: File[] }>, 'mutationFn'> = {}) => {
  const _onSuccess = useCallback<Exclude<typeof onSuccess, undefined>>(
    (data, variables, onMutateResult, context) => {
      invalidateFreeformUploadSessions(context.client)
      onSuccess?.(data, variables, onMutateResult, context)
    },
    [onSuccess]
  )

  return useMutation({
    mutationFn: async ({ files }: { files: File[] }) => {
      const path = buildPath('/api/v1/organizations/:organizationId/freeform-uploads', { organizationId })
      return fetchWrapperMutation({
        path,
        options: {
          method: 'POST',
          body: createFreeformUploadFormData(files),
          // Let the browser set the multipart boundary; only the CSRF marker header is forced.
          headers: { 'X-Requested-With': 'JavaScript' },
        },
        schema: FreeformUploadResponseSchema,
        successStatusCodes: [201, 202],
        deserializeFn: response => response.json(),
      })
    },
    onSuccess: _onSuccess,
    ...options,
  })
}

export const usePatchCodingGuideline = ({
  organizationId = 'default-organization',
  onSuccess,
  ...options
}: {
  organizationId?: string
} & Omit<
  UseMutationOptions<CodingGuidelinesResponse, DefaultError, { id: string; body: UpdateCodingGuidelinesRequest }>,
  'mutationFn'
> = {}) => {
  const _onSuccess = useCallback<Exclude<typeof onSuccess, undefined>>(
    (data, variables, onMutateResult, context) => {
      context.client.invalidateQueries({ queryKey: getCodingGuidelinesOptions({ organizationId }).queryKey })
      onSuccess?.(data, variables, onMutateResult, context)
    },
    [organizationId, onSuccess]
  )

  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: UpdateCodingGuidelinesRequest }) =>
      fetchWrapperMutation({
        path: buildPath('/api/v1/organizations/:organizationId/coding-guidelines/:id', { organizationId, id }),
        options: {
          method: 'PATCH',
          body: JSON.stringify(body),
          headers: { 'X-Requested-With': 'JavaScript', 'Content-Type': 'application/json' },
        },
        schema: CodingGuidelinesResponseSchema,
        deserializeFn: response => response.json(),
      }),
    onSuccess: _onSuccess,
    ...options,
  })
}

export const useDeleteCodingGuideline = ({
  organizationId = 'default-organization',
  onSuccess,
  ...options
}: {
  organizationId?: string
} & Omit<UseMutationOptions<unknown, DefaultError, { id: string }>, 'mutationFn'> = {}) => {
  const _onSuccess = useCallback<Exclude<typeof onSuccess, undefined>>(
    (data, variables, onMutateResult, context) => {
      context.client.invalidateQueries({ queryKey: getCodingGuidelinesOptions({ organizationId }).queryKey })
      onSuccess?.(data, variables, onMutateResult, context)
    },
    [organizationId, onSuccess]
  )

  return useMutation({
    mutationFn: async ({ id }: { id: string }) =>
      fetchWrapperMutation({
        path: buildPath('/api/v1/organizations/:organizationId/coding-guidelines/:id', { organizationId, id }),
        options: { method: 'DELETE', headers: { 'X-Requested-With': 'JavaScript' } },
        schema: z.unknown(),
        successStatusCodes: [204],
      }),
    onSuccess: _onSuccess,
    ...options,
  })
}

/**
 * Triggers the in-cluster support-bundle service to generate a bundle and
 * upload it to the Pixee Vendor Portal in one shot.
 *
 * Pixee Enterprise Server only — the endpoint does not exist on SaaS, where
 * calls surface as a generic error toast in the UI.
 *
 * Status codes:
 * - 202: bundle generation started, body `{ id, phase: 'pending' }`
 * - 409: another bundle is already generating, body `{ error, id, phase }`
 */
const getPostSupportBundleOptions = (
  options: Omit<UseMutationOptions<SupportBundleResponse>, 'mutationFn' | 'mutationKey'> = {}
) =>
  mutationOptions({
    mutationKey: ['post-support-bundle'] as const,
    mutationFn: () =>
      fetchWrapperMutation({
        path: '/api/v1/support-bundles',
        options: { method: 'POST' },
        schema: SupportBundleResponseSchema,
        successStatusCodes: [202],
        deserializeFn: async response => (await response.json()) as unknown,
      }),
    ...options,
  })

export const usePostSupportBundle = (
  options: Omit<UseMutationOptions<SupportBundleResponse>, 'mutationFn' | 'mutationKey'> = {}
) => useMutation(getPostSupportBundleOptions(options))

/**
 * Fetches the API root. The presence of `_links.support-bundles` is the canonical signal that the
 * in-cluster support-bundle service is rendered on this install; user-platform features gate UI
 * affordances on that link.
 */
const getApiRootOptions = (options: Omit<UseQueryOptions<ApiRootResponse, ApiError>, 'queryKey' | 'queryFn'> = {}) =>
  queryOptions({
    queryKey: ['api-root'],
    queryFn: () =>
      fetchWrapperQuery({
        path: '/api/v1',
        schema: ApiRootResponseSchema,
      }),
    ...options,
  })

export const useApiRoot = (options: Omit<UseQueryOptions<ApiRootResponse, ApiError>, 'queryKey' | 'queryFn'> = {}) =>
  useQuery(getApiRootOptions(options))

// #endregion
